/// <reference path="../types.d.ts" />
/**
 * Niksiç & Hociç Family Tree Cloud Application
 * Full-featured online replacement for Chronoplex My Family Tree:
 * - Cloudflare D1 live database sync + AES-256-GCM encrypted static fallback
 * - Two-tier access: Viewer ('niksic') & Editor ('niksic-edit' / 'niksic2026')
 * - Full CRUD: Add/Edit/Delete People, Add/Connect Relatives (Parents, Spouses, Children, Siblings),
 *   Edit Marriages, Merge Duplicate People, Bulk Find & Replace
 * - Interactive Hourglass Tree, Concentric Fan Chart, Chronological Timeline,
 *   Directory, BFS Kinship Pathfinder, Statistics, Live GEDCOM 5.5.1 Export & Audit Log Undo
 */

import {
  STORAGE_KEY_MASTER,
  STORAGE_KEY_SESSION_TOKEN,
  STORAGE_KEY_SESSION_ROLE,
  STORAGE_KEY_EDITOR_NAME,
  STORAGE_KEY_LANG,
  STORAGE_KEY_THEME,
  STORAGE_KEY_TREE_ANCESTOR_DEPTH,
  STORAGE_KEY_TREE_DESCENDANT_DEPTH,
  STORAGE_KEY_TREE_SHOW_SIBLINGS,
  STORAGE_KEY_TREE_SHOW_SPOUSES,
  ADMIN_CODES,
  EDITOR_CODES,
  FEATURED_BRANCHES,
  NODE_W,
  SPOUSE_W,
  COLLATERAL_W,
  SPOUSE_GAP,
  SIBLING_GAP,
  SUBTREE_GAP,
  LEVEL_H,
} from "./core/constants";
import { I18N, t } from "./core/i18n";
import { state } from "./core/state";
import {
  normalizeText,
  normalizePhonetic,
  scorePersonSearch,
  searchPersons,
  escapeHtml,
  highlightMatch,
  normalizePasscode,
  base64ToBytes,
  bytesToBase64,
  extractNumericYear,
  formatLifeYears,
  renderAvatarSilhouette,
} from "./core/helpers";
import {
  getParents,
  getSpousesAndChildren,
  getSiblings,
  getSiblingsDetails,
  findShortestKinshipPath,
  translateRelStep,
  getBranchPersonIds,
} from "./engine/graph";
import {
  openPosterExportModal as openPosterExportModalView,
  buildPosterSvgFromConfig as buildPosterSvgFromConfigView,
} from "./views/poster";
import { openShareCardModal as openShareCardModalView } from "./views/shareCard";
import {
  setupArchiveView,
  loadArchiveManifest,
  initArchiveView,
  renderArchiveView,
  openArchiveUploadModal,
  openArchiveDocument,
  closeArchiveViewer,
} from "./views/archive";
import { setupFanChart, renderFanChart } from "./views/fan";
import {
  setupMigrationMap,
  renderMigrationMap,
  fitMap,
  zoomMap,
  initMapPanZoomGestures,
  locateCityOnMap,
  updateMapTimelineUI,
  toggleMapTimelinePlay,
  startStoryTour,
  prevStoryStep,
  nextStoryStep,
  closeStoryTour,
  closeCityInspector,
} from "./views/map";

/* ================= CRYPTO & CLOUD API ================= */
async function loadEncryptedBundle() {
  if (state.encryptedBundle) return state.encryptedBundle;
  const res = await fetch("./family-data.enc.json");
  if (!res.ok) throw new Error("Failed to fetch encrypted bundle");
  state.encryptedBundle = await res.json();
  return state.encryptedBundle;
}

async function decryptWithMasterKeyBytes(masterKeyBytes, bundle) {
  const cryptoKey = await crypto.subtle.importKey("raw", masterKeyBytes, { name: "AES-GCM" }, false, ["decrypt"]);
  const iv = base64ToBytes(bundle.iv);
  const ciphertext = base64ToBytes(bundle.ciphertext);
  const decryptedBuf = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, cryptoKey, ciphertext);
  return JSON.parse(new TextDecoder("utf-8").decode(decryptedBuf));
}

async function unlockWithPasscode(rawPasscode, bundle) {
  const norm = normalizePasscode(rawPasscode);
  if (!norm) throw new Error("Empty passcode");

  const enc = new TextEncoder().encode(norm);
  const baseKey = await crypto.subtle.importKey("raw", enc, "PBKDF2", false, ["deriveKey"]);

  for (const entry of bundle.wrappedKeys) {
    try {
      const salt = base64ToBytes(entry.salt);
      const wrapIv = base64ToBytes(entry.iv);
      const wrappedBytes = base64ToBytes(entry.wrappedKey);

      const kek = await crypto.subtle.deriveKey(
        { name: "PBKDF2", salt, iterations: bundle.iterations, hash: "SHA-256" },
        baseKey,
        { name: "AES-GCM", length: 256 },
        false,
        ["decrypt"]
      );

      const masterKeyBuf = await crypto.subtle.decrypt({ name: "AES-GCM", iv: wrapIv }, kek, wrappedBytes);
      const masterKeyBytes = new Uint8Array(masterKeyBuf);
      const data = await decryptWithMasterKeyBytes(masterKeyBytes, bundle);
      return { data, masterKeyB64: bytesToBase64(masterKeyBytes), normPass: norm };
    } catch (_) {}
  }
  throw new Error("Invalid passcode");
}

function getDeviceSummary() {
  const ua = navigator.userAgent || "";
  let os = "Bilinmeyen Cihaz";
  if (/iPhone/i.test(ua)) os = "Apple iPhone";
  else if (/iPad/i.test(ua)) os = "Apple iPad";
  else if (/Macintosh|Mac OS X/i.test(ua)) os = "Mac";
  else if (/Android/i.test(ua)) os = "Android Cihaz";
  else if (/Windows/i.test(ua)) os = "Windows PC";
  else if (/Linux/i.test(ua)) os = "Linux";

  let browser = "";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/Chrome\//i.test(ua) || /CriOS\//i.test(ua)) browser = "Chrome";
  else if (/Safari\//i.test(ua)) browser = "Safari";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";

  return `${os} ${browser}`.trim();
}

function getApiHeaders() {
  const headers = {
    "Content-Type": "application/json",
  };
  if (state.sessionToken) {
    headers["X-Session-Token"] = state.sessionToken;
  }
  if (state.role === "editor" && state.editorName) {
    headers["X-Editor-Name"] = encodeURIComponent(state.editorName);
  }
  return headers;
}

async function syncFromCloud() {
  try {
    const res = await fetch("/api/tree", { headers: getApiHeaders() });
    if (res.status === 401) {
      // Session expired or revoked
      localStorage.removeItem(STORAGE_KEY_SESSION_TOKEN);
      localStorage.removeItem(STORAGE_KEY_SESSION_ROLE);
      state.sessionToken = "";
      state.role = "viewer";
      updateEditorBadge();
      return false;
    }
    if (!res.ok) return false;
    const cloudData = await res.json();
    if (cloudData && cloudData.individuals) {
      state.auditLog = cloudData.auditLog || [];
      state.lastAuditId = cloudData.latestAuditId || (state.auditLog[0] ? state.auditLog[0].id : 0);
      if (cloudData.role) {
        state.role = cloudData.role;
        localStorage.setItem(STORAGE_KEY_SESSION_ROLE, state.role);
      }
      prepareDataset(cloudData);
      updateEditorBadge();
      refreshActiveViews(false);
      startIncrementalSync();
      return true;
    }
  } catch (_) {
    // Running on local static server without Worker runtime; fallback to in-memory/localStorage edits
  }
  return false;
}

async function apiRequest(path: string, method: string = "GET", body: any = null): Promise<any> {
  try {
    const res = await fetch(`/api/${path}`, {
      method,
      headers: getApiHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.ok) {
      const data = await res.json();
      if (data && data.individuals) {
        state.auditLog = data.auditLog || state.auditLog;
        if (data.latestAuditId) {
          state.lastAuditId = data.latestAuditId;
        } else if (state.auditLog[0]) {
          state.lastAuditId = state.auditLog[0].id;
        }
        prepareDataset(data);
        refreshActiveViews(false);
      }
      return { ok: true, data };
    }
    const errJson = await res.json().catch(() => ({}));
    return { ok: false, error: errJson.error || `HTTP ${res.status}` };
  } catch (err) {
    // Fallback for local static server preview: apply mutation locally in memory
    return applyLocalMutationFallback(path, method, body);
  }
}

function showSyncToast(msg, duration = 4000) {
  let toast = document.getElementById("sync-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "sync-toast";
    toast.style.position = "fixed";
    toast.style.bottom = "24px";
    toast.style.right = "24px";
    toast.style.maxWidth = "380px";
    toast.style.backgroundColor = "rgba(15, 23, 42, 0.94)";
    toast.style.color = "#f8fafc";
    toast.style.padding = "10px 18px";
    toast.style.borderRadius = "10px";
    toast.style.boxShadow = "0 10px 30px rgba(0,0,0,0.35)";
    toast.style.fontSize = "0.86rem";
    toast.style.fontWeight = "500";
    toast.style.zIndex = "99999";
    toast.style.display = "flex";
    toast.style.alignItems = "center";
    toast.style.gap = "8px";
    toast.style.transition = "opacity 0.25s ease, transform 0.25s ease";
    toast.style.pointerEvents = "none";
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.style.opacity = "1";
  toast.style.transform = "translateY(0)";
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => {
    toast.style.opacity = "0";
    toast.style.transform = "translateY(8px)";
  }, duration);
}

async function syncDelta(isManual = false) {
  if (state.isSyncing || !state.data) return false;
  if (!state.lastAuditId) {
    if (state.auditLog && state.auditLog.length > 0) {
      state.lastAuditId = Math.max(...state.auditLog.map((r) => r.id || 0));
    }
  }

  const liveBadgeText = document.getElementById("sync-live-text");
  if (liveBadgeText && isManual) liveBadgeText.textContent = "Eşitleniyor...";

  state.isSyncing = true;
  try {
    const sinceParam = state.lastAuditId || 0;
    const res = await fetch(`/api/sync?since=${encodeURIComponent(sinceParam)}`, {
      headers: getApiHeaders(),
    });

    if (res.status === 401) {
      if (liveBadgeText) liveBadgeText.textContent = "Giriş Gerekli";
      return false;
    }
    if (!res.ok) {
      if (liveBadgeText) liveBadgeText.textContent = "Canlı";
      return false;
    }

    const deltaRes = await res.json();
    if (!deltaRes || !deltaRes.ok) {
      if (liveBadgeText) liveBadgeText.textContent = "Canlı";
      return false;
    }

    if (deltaRes.upToDate) {
      if (isManual) showSyncToast("✓ Veritabanı güncel (Yeni değişiklik yok)");
      if (liveBadgeText) liveBadgeText.textContent = "Canlı";
      return true;
    }

    if (deltaRes.fullReload && deltaRes.individuals) {
      state.auditLog = deltaRes.auditLog || state.auditLog;
      state.lastAuditId = deltaRes.lastAuditId || state.lastAuditId;
      prepareDataset(deltaRes);
      refreshActiveViews(false);
      showSyncToast("⚡ Soy ağacı güncellendi (Yeni veriler yüklendi)");
      if (liveBadgeText) liveBadgeText.textContent = "Canlı";
      return true;
    }

    let changedCount = 0;
    const deltas = deltaRes.deltas || {};

    // 1. Delete removed individuals
    for (const did of deltas.deletedIndividuals || []) {
      if (state.data.individuals[did]) {
        delete state.data.individuals[did];
        changedCount++;
      }
    }

    // 2. Delete removed families
    for (const dfid of deltas.deletedFamilies || []) {
      if (state.data.families[dfid]) {
        delete state.data.families[dfid];
        changedCount++;
      }
    }

    // 3. Update/insert mutated individuals
    for (const [id, indi] of Object.entries(deltas.individuals || {})) {
      state.data.individuals[id] = indi;
      changedCount++;
    }

    // 4. Update/insert mutated families
    for (const [id, fam] of Object.entries(deltas.families || {})) {
      state.data.families[id] = fam;
      changedCount++;
    }

    if (deltaRes.newAuditEntries && deltaRes.newAuditEntries.length > 0) {
      state.auditLog = [...deltaRes.newAuditEntries, ...state.auditLog].slice(0, 100);
    }
    if (deltaRes.lastAuditId) {
      state.lastAuditId = deltaRes.lastAuditId;
    }

    if (changedCount > 0) {
      prepareDataset(state.data);
      const isModalOpen = Boolean(document.querySelector(".modal-backdrop:not(.hidden)"));
      if (!isModalOpen) {
        refreshActiveViews(false);
      }
      const latestSummary = (deltaRes.newAuditEntries && deltaRes.newAuditEntries[0] && deltaRes.newAuditEntries[0].summary) || "";
      showSyncToast(`⚡ Canlı Güncelleme: ${latestSummary || `${changedCount} kayıt güncellendi`}`);
    } else if (isManual) {
      showSyncToast("✓ Veritabanı güncel");
    }
    if (liveBadgeText) liveBadgeText.textContent = "Canlı";
    return true;
  } catch (_) {
    if (liveBadgeText) liveBadgeText.textContent = "Canlı";
  } finally {
    state.isSyncing = false;
  }
  return false;
}

let syncIntervalTimer = null;
function startIncrementalSync() {
  if (syncIntervalTimer) clearInterval(syncIntervalTimer);
  syncIntervalTimer = setInterval(() => {
    if (!document.hidden) {
      syncDelta(false);
    }
  }, 25000);

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      syncDelta(false);
    }
  });
}

/* ================= GRAPH METRICS & DATASET PREPARATION ================= */
function prepareDataset(data) {
  const indis = data.individuals || {};
  const fams = data.families || {};

  // Reset fams/famc and rebuild bidirectionally
  for (const p of Object.values(indis)) {
    p.name = `${p.given || ""} ${p.surname || ""}`.trim() || p.name || "Bilinmiyor";
    p.fams = [];
    p.famc = [];
  }
  for (const fam of Object.values(fams)) {
    if (fam.husb && indis[fam.husb] && !indis[fam.husb].fams.includes(fam.id)) {
      indis[fam.husb].fams.push(fam.id);
    }
    if (fam.wife && indis[fam.wife] && !indis[fam.wife].fams.includes(fam.id)) {
      indis[fam.wife].fams.push(fam.id);
    }
    for (const cid of fam.children || []) {
      if (indis[cid] && !indis[cid].famc.includes(fam.id)) {
        indis[cid].famc.push(fam.id);
      }
    }
  }

  // Compute descendant & ancestor counts
  function countDesc(startId) {
    const vis = new Set();
    const stack = [startId];
    while (stack.length) {
      const cur = stack.pop();
      if (vis.has(cur)) continue;
      vis.add(cur);
      const p = indis[cur];
      if (!p) continue;
      for (const fid of p.fams) {
        const f = fams[fid];
        if (f) {
          for (const cid of f.children || []) {
            if (!vis.has(cid)) stack.push(cid);
          }
        }
      }
    }
    return Math.max(0, vis.size - 1);
  }

  function countAnc(startId) {
    const vis = new Set();
    const stack = [startId];
    while (stack.length) {
      const cur = stack.pop();
      if (vis.has(cur)) continue;
      vis.add(cur);
      const p = indis[cur];
      if (!p) continue;
      for (const fid of p.famc) {
        const f = fams[fid];
        if (f) {
          if (f.husb && !vis.has(f.husb)) stack.push(f.husb);
          if (f.wife && !vis.has(f.wife)) stack.push(f.wife);
        }
      }
    }
    return Math.max(0, vis.size - 1);
  }

  const genLevels = {};
  const queue = [];
  for (const p of Object.values(indis)) {
    p.descendantCount = countDesc(p.id);
    p.ancestorCount = countAnc(p.id);
    if (p.famc.length === 0) {
      genLevels[p.id] = 0;
      queue.push(p.id);
    }
  }

  while (queue.length) {
    const u = queue.shift();
    const uGen = genLevels[u] || 0;
    const p = indis[u];
    if (!p) continue;
    for (const fid of p.fams) {
      const f = fams[fid];
      if (!f) continue;
      for (const sp of [f.husb, f.wife]) {
        if (sp && sp !== u && indis[sp] && indis[sp].famc.length === 0) {
          if ((genLevels[sp] || 0) < uGen) genLevels[sp] = uGen;
        }
      }
      for (const cid of f.children || []) {
        if (indis[cid] && (genLevels[cid] === undefined || genLevels[cid] < uGen + 1)) {
          genLevels[cid] = uGen + 1;
          queue.push(cid);
        }
      }
    }
  }

  if (data.primaryPhotos) {
    state.primaryPhotos = Object.assign({}, state.primaryPhotos || {}, data.primaryPhotos);
  } else if (!state.primaryPhotos) {
    state.primaryPhotos = {};
  }

  const list = Object.values(indis);
  for (const p of list) {
    p.generation = genLevels[p.id] || 0;
    p.avatarUrl = state.primaryPhotos[p.id] || null;
    const searchRaw = `${p.name} ${p.given} ${p.surname} ${p.birthSurname || ""} ${p.nickname || ""} ${p.birthYear} ${p.deathYear} ${p.birthPlace || ""} ${p.occupation || ""}`;
    p._searchNorm = normalizeText(searchRaw);
    p._phoneticNorm = normalizePhonetic(searchRaw);
  }

  computeEstimatedDates(indis, fams);

  data.meta = {
    title: "Niksiç - Hociç Soy Ağacı",
    totalIndividuals: list.length,
    totalFamilies: Object.keys(fams).length,
  };

  state.data = data;
  state.personList = list;
}

function computeEstimatedDates(individuals, families) {
  const knownBirth = {};
  const knownDeath = {};

  for (const p of Object.values(individuals)) {
    const by = extractNumericYear(p.birthYear);
    if (by && by >= 1400 && by <= 2050) knownBirth[p.id] = by;
    const dy = extractNumericYear(p.deathYear);
    if (dy && dy >= 1400 && dy <= 2050) knownDeath[p.id] = dy;
  }

  const estBirth = { ...knownBirth };
  let changed = true;
  let iters = 0;

  // Bidirectional constraint propagation across family tree
  while (changed && iters < 30) {
    changed = false;
    iters++;
    for (const p of Object.values(individuals)) {
      if (estBirth[p.id] !== undefined) continue;
      const candidates = [];

      // 1. From children (parent is born ~29 years earlier)
      for (const fId of p.fams || []) {
        const fam = families[fId];
        if (!fam) continue;
        for (const chId of fam.children || []) {
          if (estBirth[chId] !== undefined) candidates.push(estBirth[chId] - 29);
        }
        // From spouse
        const spId = fam.husb === p.id ? fam.wife : fam.husb;
        if (spId && estBirth[spId] !== undefined) candidates.push(estBirth[spId]);
      }

      // 2. From parents (child is born ~29 years later)
      for (const fId of p.famc || []) {
        const fam = families[fId];
        if (!fam) continue;
        if (fam.husb && estBirth[fam.husb] !== undefined) candidates.push(estBirth[fam.husb] + 29);
        if (fam.wife && estBirth[fam.wife] !== undefined) candidates.push(estBirth[fam.wife] + 29);
        // From siblings
        for (const sibId of fam.children || []) {
          if (sibId !== p.id && estBirth[sibId] !== undefined) candidates.push(estBirth[sibId]);
        }
      }

      if (candidates.length > 0) {
        estBirth[p.id] = Math.round(candidates.reduce((a, b) => a + b, 0) / candidates.length);
        changed = true;
      }
    }
  }

  const currentYear = new Date().getFullYear();
  for (const p of Object.values(individuals)) {
    if (estBirth[p.id] === undefined) {
      const gen = p.generation || 0;
      estBirth[p.id] = 1800 + gen * 28;
    }
  }

  for (const p of Object.values(individuals)) {
    const isExactBirth = knownBirth[p.id] !== undefined;
    const bYear = isExactBirth ? knownBirth[p.id] : estBirth[p.id];
    p.computedBirthYear = bYear;
    p.isBirthEstimated = !isExactBirth;

    const isExactDeath = knownDeath[p.id] !== undefined;
    if (isExactDeath) {
      p.computedDeathYear = knownDeath[p.id];
      p.isDeathEstimated = false;
    } else if (p.deceased) {
      p.computedDeathYear = Math.min(currentYear, bYear + 68);
      p.isDeathEstimated = true;
    } else {
      p.computedDeathYear = null;
      p.isDeathEstimated = false;
    }

    p.lifeSpanYears = p.computedDeathYear
      ? Math.max(0, p.computedDeathYear - p.computedBirthYear)
      : Math.max(0, currentYear - p.computedBirthYear);
  }
}

const HISTORICAL_ERAS = [
  {
    id: "era-1",
    min: 1780,
    max: 1830,
    titleTr: "1790 — 1830 • Kadim Niksiç Kökleri & İlk Ecdat",
    titleEn: "1790 — 1830 • Ancient Niksic Roots & Forefathers",
    descTr: "Karadağ / Niksiç'teki ilk kurucu atalarımızın kök dönemi.",
    descEn: "The era of our founding forefathers in Niksic, Montenegro.",
  },
  {
    id: "era-2",
    min: 1831,
    max: 1878,
    titleTr: "1830 — 1878 • Osmanlı Karadağ Dönemi & Lim Havzası",
    titleEn: "1830 — 1878 • Ottoman Montenegro Era & Lim Valley",
    descTr: "Mehmet Hoca, Cemile, Zülfü, Adem ve Zehra Ceriç kuşağının doğduğu dönem.",
    descEn: "Era of Mehmet Hoca, Cemile, Zulfu, Adem, and Zehra Ceric.",
  },
  {
    id: "era-3",
    min: 1879,
    max: 1923,
    titleTr: "1878 — 1923 • 93 Harbi, Büyük Göç & İntikal",
    titleEn: "1878 — 1923 • Russo-Turkish War, Migration & Transition",
    descTr: "Berlin Antlaşması sonrası Anadolu'ya (Edirne, Karamürsel, Burhaniye) göç eden kuşak.",
    descEn: "The generation that migrated from the Balkans to Anatolia.",
  },
  {
    id: "era-4",
    min: 1924,
    max: 1950,
    titleTr: "1923 — 1950 • Erken Cumhuriyet & Burhaniye / Körfez Yerleşimi",
    titleEn: "1923 — 1950 • Early Republic & Gulf Settlement",
    descTr: "Körfez bölgesinde yeni hayatın kurulduğu ve Soyadı Kanunu dönemi.",
    descEn: "Establishment of life in Burhaniye and adoption of surnames.",
  },
  {
    id: "era-5",
    min: 1951,
    max: 1980,
    titleTr: "1950 — 1980 • Şehirleşme, Büyüme & Yayılma",
    titleEn: "1950 — 1980 • Urbanization & Family Expansion",
    descTr: "Balıkesir, İzmir, İstanbul ve Ankara gibi büyük şehirlere yayılma kuşağı.",
    descEn: "Expansion across Istanbul, Izmir, Balikesir, and Ankara.",
  },
  {
    id: "era-6",
    min: 1981,
    max: 2005,
    titleTr: "1980 — 2005 • Çağdaş Aile Kuşakları",
    titleEn: "1980 — 2005 • Contemporary Generations",
    descTr: "Modern Türkiye ve dünyada kariyer ve aile kuran kuşaklarımız.",
    descEn: "Generations establishing careers and modern family life.",
  },
  {
    id: "era-7",
    min: 2006,
    max: 2035,
    titleTr: "2005 — Günümüz • Yeni Nesiller & Aile Mirası",
    titleEn: "2005 — Present • New Generations & Heritage",
    descTr: "Ailenin en genç üyeleri, geleceğe uzanan soyumuz.",
    descEn: "Youngest family members carrying the heritage into the future.",
  },
];

function getTimelineEra(year) {
  const y = parseInt(year, 10);
  if (!y) return HISTORICAL_ERAS[0];
  for (const era of HISTORICAL_ERAS) {
    if (y >= era.min && y <= era.max) return era;
  }
  return HISTORICAL_ERAS[HISTORICAL_ERAS.length - 1];
}

function getMaternalBranchInfo(pid) {
  if (!state.data || !state.data.individuals || !state.data.families) return null;
  const p = state.data.individuals[pid];
  if (!p) return null;

  const { father, mother, familyId } = getParents(pid);
  if (!familyId) return null;

  // Case 1: Father had multiple marriages/spouses
  if (father && father.fams && father.fams.length > 1) {
    const famIndex = Math.max(0, father.fams.indexOf(familyId));
    const motherName = mother ? (mother.given || mother.name.split(" ")[0]) : (state.lang === "tr" ? "Bilinmeyen Anne" : "Unknown Mother");
    const icons = ["🌸", "🌿", "✨", "💜", "🌼", "🍁"];
    const icon = icons[famIndex % icons.length] || "🌸";
    const branchLabel = `${motherName} ${state.lang === "tr" ? "Kolu" : "Branch"}`;
    return {
      type: "mother",
      label: branchLabel,
      icon,
      colorIndex: famIndex % 4,
      familyId,
      parentName: father.name,
      spouseName: mother ? mother.name : motherName,
      fullTitle: `${branchLabel} (${father.name} & ${mother ? mother.name : motherName})`,
    };
  }

  // Case 2: Mother had multiple marriages/spouses (and father didn't)
  if (mother && mother.fams && mother.fams.length > 1) {
    const famIndex = Math.max(0, mother.fams.indexOf(familyId));
    const fatherName = father ? (father.given || father.name.split(" ")[0]) : (state.lang === "tr" ? "Bilinmeyen Baba" : "Unknown Father");
    const icons = ["🌸", "🌿", "✨", "💜", "🌼", "🍁"];
    const icon = icons[famIndex % icons.length] || "🌸";
    const branchLabel = `${fatherName} ${state.lang === "tr" ? "Kolu" : "Branch"}`;
    return {
      type: "father",
      label: branchLabel,
      icon,
      colorIndex: famIndex % 4,
      familyId,
      parentName: mother.name,
      spouseName: father ? father.name : fatherName,
      fullTitle: `${branchLabel} (${mother.name} & ${father ? father.name : fatherName})`,
    };
  }

  return null;
}

function getCandidateParentFamilies(pid) {
  if (!state.data || !state.data.individuals || !state.data.families) return [];
  const p = state.data.individuals[pid];
  if (!p) return [];

  const currentParents = getParents(pid);
  const famSet = new Set();
  if (currentParents.familyId) famSet.add(currentParents.familyId);
  if (currentParents.father) {
    for (const fid of currentParents.father.fams || []) famSet.add(fid);
  }
  if (currentParents.mother) {
    for (const fid of currentParents.mother.fams || []) famSet.add(fid);
  }

  const icons = ["🌸", "🌿", "✨", "💜", "🌼", "🍁"];

  return Array.from(famSet).map((fid: any, idx) => {
    const fam = state.data.families[fid];
    const husb = fam && fam.husb ? state.data.individuals[fam.husb] : null;
    const wife = fam && fam.wife ? state.data.individuals[fam.wife] : null;
    const isCurrent = fid === currentParents.familyId;
    const husbName = husb ? husb.name : (state.lang === "tr" ? "Bilinmeyen Baba" : "Unknown Father");
    const wifeName = wife ? wife.name : (state.lang === "tr" ? "Bilinmeyen Anne" : "Unknown Mother");
    const childCount = fam && fam.children ? fam.children.length : 0;
    const icon = icons[idx % icons.length] || "🌸";

    return {
      familyId: fid,
      isCurrent,
      husbName,
      wifeName,
      childCount,
      icon,
      label: `${icon} ${husbName} & ${wifeName} (${childCount} ${state.lang === "tr" ? "çocuk" : "children"})${isCurrent ? (state.lang === "tr" ? " — [Şu Anki Seçim]" : " — [Current]") : ""}`,
    };
  });
}

/* ================= HOURGLASS + COLLATERAL FAMILY TREE LAYOUT ENGINE ================= */
function buildHourglassLayout(rootId, maxUp, maxDown) {
  const nodes = [];
  const marriages = [];
  const brackets = [];
  const visitedDown = new Set([rootId]);
  const visitedUp = new Set([rootId]);

  const COUPLE_STEP = NODE_W + SPOUSE_GAP;
  const SIB_STEP = COLLATERAL_W + SIBLING_GAP;

  /* ---- 1. Downward Descendants Subtree (depth >= 0) ---- */
  function buildDownNode(pid, depth) {
    const person = state.data.individuals[pid];
    if (!person) return null;

    const unions = getSpousesAndChildren(pid);
    const allowSpouses = depth === 0 || state.showSpouses;
    const spouses = allowSpouses
      ? unions
          .map((u) => ({ person: u.spouse, status: u.status, familyId: u.familyId }))
          .filter((s) => s.person)
      : [];

    const allChildrenIds = [];
    for (const u of unions) {
      for (const ch of u.children) {
        if (!visitedDown.has(ch.id)) {
          visitedDown.add(ch.id);
          allChildrenIds.push(ch.id);
        }
      }
    }

    const childSubtrees = [];
    let hiddenChildrenCount = 0;

    if (depth < maxDown) {
      for (const cid of allChildrenIds) {
        const sub = buildDownNode(cid, depth + 1);
        if (sub) childSubtrees.push(sub);
      }
    } else {
      hiddenChildrenCount = allChildrenIds.length;
    }

    const S = spouses.length;
    const dropXRel = S > 0 ? NODE_W / 2 + SPOUSE_GAP / 2 : 0;
    const cardLeft = -NODE_W / 2 - dropXRel;
    const cardRight = NODE_W / 2 + S * (SPOUSE_W + SPOUSE_GAP) - dropXRel;

    let childOffsets = [];
    let childrenLeft = 0;
    let childrenRight = 0;

    if (childSubtrees.length > 0) {
      let currentX = 0;
      for (let i = 0; i < childSubtrees.length; i++) {
        const ch = childSubtrees[i];
        if (i === 0) {
          currentX = 0;
        } else {
          const prev = childSubtrees[i - 1];
          currentX += prev.rightOffset + SIBLING_GAP + ch.leftOffset;
        }
        childOffsets.push(currentX);
      }
      const startX = -childSubtrees[0].leftOffset;
      const endX = childOffsets[childOffsets.length - 1] + childSubtrees[childSubtrees.length - 1].rightOffset;
      const centerShift = (startX + endX) / 2;
      childOffsets = childOffsets.map((x) => x - centerShift);

      childrenLeft = childOffsets[0] - childSubtrees[0].leftOffset;
      childrenRight = childOffsets[childOffsets.length - 1] + childSubtrees[childSubtrees.length - 1].rightOffset;
    }

    const leftOffset = childSubtrees.length > 0 ? Math.max(-cardLeft, -childrenLeft) : -cardLeft;
    const rightOffset = childSubtrees.length > 0 ? Math.max(cardRight, childrenRight) : cardRight;

    return {
      id: pid,
      person,
      spouses,
      depth,
      hiddenChildrenCount,
      dropXRel,
      leftOffset,
      rightOffset,
      width: leftOffset + rightOffset,
      children: childSubtrees,
      childOffsets,
      primaryX: 0,
      dropX: 0,
      y: depth * LEVEL_H,
    };
  }

  function assignDownCoords(node, dropX) {
    node.dropX = dropX;
    node.primaryX = dropX - node.dropXRel;
    const S = node.spouses.length;

    nodes.push({
      id: node.id,
      person: node.person,
      x: node.primaryX,
      y: node.y,
      width: NODE_W,
      isFocused: node.depth === 0,
      isDirectAncestor: false,
      isSpouse: false,
      isCollateral: false,
      hiddenChildrenCount: node.hiddenChildrenCount,
      hiddenParentsCount: 0,
    });

    let prevRightEdge = node.primaryX + NODE_W / 2;
    node.spouses.forEach((sp, idx) => {
      const spCenterX = prevRightEdge + SPOUSE_GAP + SPOUSE_W / 2;
      const midX = (prevRightEdge + (spCenterX - SPOUSE_W / 2)) / 2;
      marriages.push({
        x1: prevRightEdge,
        x2: spCenterX - SPOUSE_W / 2,
        midX,
        y: node.y,
        familyId: sp.familyId,
        status: sp.status,
      });

      const spParents = getParents(sp.person.id);
      let spHiddenParents = (spParents.father ? 1 : 0) + (spParents.mother ? 1 : 0);

      const inLawY = (node.depth - 1) * LEVEL_H;
      const hasConflict = nodes.some(
        (n) =>
          Math.abs(n.y - inLawY) < 10 &&
          Math.abs(n.x - spCenterX) < COUPLE_STEP + 40
      );

      // Render in-law parents above spouse if at depth >= 1 and space permits (e.g. Cevahir's parents Süleyman & Zehra Ceriç)
      if (
        state.showSpouses &&
        state.showSiblings &&
        node.depth >= 1 &&
        spParents.father &&
        spParents.mother &&
        !hasConflict &&
        !visitedDown.has(spParents.father.id) &&
        !visitedDown.has(spParents.mother.id) &&
        !visitedUp.has(spParents.father.id) &&
        !visitedUp.has(spParents.mother.id)
      ) {
        const inLawF_X = spCenterX - COUPLE_STEP / 2;
        const inLawM_X = spCenterX + COUPLE_STEP / 2;

        visitedDown.add(spParents.father.id);
        visitedDown.add(spParents.mother.id);

        nodes.push({
          id: spParents.father.id,
          person: spParents.father,
          x: inLawF_X,
          y: inLawY,
          width: NODE_W,
          isFocused: false,
          isDirectAncestor: false,
          isSpouse: false,
          isCollateral: true,
          hiddenChildrenCount: 0,
          hiddenParentsCount: 0,
        });

        nodes.push({
          id: spParents.mother.id,
          person: spParents.mother,
          x: inLawM_X,
          y: inLawY,
          width: NODE_W,
          isFocused: false,
          isDirectAncestor: false,
          isSpouse: true,
          isCollateral: true,
          hiddenChildrenCount: 0,
          hiddenParentsCount: 0,
        });

        marriages.push({
          x1: inLawF_X + NODE_W / 2,
          x2: inLawM_X - NODE_W / 2,
          midX: spCenterX,
          y: inLawY,
          familyId: spParents.familyId,
          status: "current",
        });

        brackets.push({
          parentX: spCenterX,
          parentY: inLawY + 12,
          childY: node.y - 39,
          midY: (inLawY + node.y) / 2,
          isDescendant: true,
          children: [{ x: spCenterX, isDirect: true }],
        });

        spHiddenParents = 0;
      }

      nodes.push({
        id: sp.person.id,
        person: sp.person,
        x: spCenterX,
        y: node.y,
        width: SPOUSE_W,
        isFocused: false,
        isDirectAncestor: false,
        isSpouse: true,
        isCollateral: false,
        hiddenChildrenCount: 0,
        hiddenParentsCount: spHiddenParents,
      });
      prevRightEdge = spCenterX + SPOUSE_W / 2;
    });

    if (node.children.length === 0) return;

    const childTargets = [];
    for (let i = 0; i < node.children.length; i++) {
      const ch = node.children[i];
      const chDropX = dropX + node.childOffsets[i];
      assignDownCoords(ch, chDropX);
      childTargets.push({ x: ch.primaryX, isDirect: true });
    }

    brackets.push({
      parentX: node.dropX,
      parentY: node.y + (S > 0 ? 12 : 39),
      childY: (node.depth + 1) * LEVEL_H - 39,
      midY: node.y + LEVEL_H / 2,
      isDescendant: true,
      children: childTargets,
    });
  }

  /* ---- 2. Upward Ancestor Couples + Collateral Siblings (depth >= 1) ---- */
  function buildUpUnit(targetPersonId, depth) {
    const { father, mother, familyId } = getParents(targetPersonId);
    const fCandidate = father && !visitedUp.has(father.id) ? father : null;
    const mCandidate = mother && !visitedUp.has(mother.id) ? mother : null;
    if (!fCandidate && !mCandidate) return null;

    if (depth > maxUp) {
      return {
        isCutoff: true,
        count: (fCandidate ? 1 : 0) + (mCandidate ? 1 : 0),
      };
    }

    if (fCandidate) visitedUp.add(fCandidate.id);
    if (mCandidate) visitedUp.add(mCandidate.id);

    const famStatus = familyId && state.data.families[familyId] ? state.data.families[familyId].status || "current" : "current";

    const fSibs =
      state.showSiblings && fCandidate
        ? getSiblings(fCandidate.id).filter((s) => !visitedUp.has(s.id))
        : [];
    for (const s of fSibs) visitedUp.add(s.id);

    const mSibs =
      state.showSiblings && mCandidate
        ? getSiblings(mCandidate.id).filter((s) => !visitedUp.has(s.id))
        : [];
    for (const s of mSibs) visitedUp.add(s.id);

    let fRelX = null;
    let mRelX = null;
    let dropRelX = 0;
    const fSibNodes = [];
    const mSibNodes = [];

    if (fCandidate && mCandidate) {
      fRelX = -COUPLE_STEP / 2;
      mRelX = COUPLE_STEP / 2;
      dropRelX = 0;
      fSibs.forEach((s, idx) => {
        fSibNodes.push({ person: s, relX: fRelX - (fSibs.length - idx) * SIB_STEP });
      });
      mSibs.forEach((s, idx) => {
        mSibNodes.push({ person: s, relX: mRelX + (idx + 1) * SIB_STEP });
      });
    } else if (fCandidate) {
      fRelX = 0;
      dropRelX = 0;
      fSibs.forEach((s, idx) => {
        fSibNodes.push({ person: s, relX: fRelX - (fSibs.length - idx) * SIB_STEP });
      });
    } else if (mCandidate) {
      mRelX = 0;
      dropRelX = 0;
      mSibs.forEach((s, idx) => {
        mSibNodes.push({ person: s, relX: mRelX - (mSibs.length - idx) * SIB_STEP });
      });
    }

    const fUp = fCandidate ? buildUpUnit(fCandidate.id, depth + 1) : null;
    const mUp = mCandidate ? buildUpUnit(mCandidate.id, depth + 1) : null;

    const fChildRelXs = [...fSibNodes.map((n) => n.relX), ...(fRelX !== null ? [fRelX] : [])];
    const mChildRelXs = [...(mRelX !== null ? [mRelX] : []), ...mSibNodes.map((n) => n.relX)];

    let fUpOffsetX =
      fChildRelXs.length > 0 ? (fChildRelXs[0] + fChildRelXs[fChildRelXs.length - 1]) / 2 : 0;
    let mUpOffsetX =
      mChildRelXs.length > 0 ? (mChildRelXs[0] + mChildRelXs[mChildRelXs.length - 1]) / 2 : 0;

    if (fUp && !fUp.isCutoff && mUp && !mUp.isCutoff) {
      let maxOverlap = 0;
      for (const dStr of Object.keys(fUp.maxXByDepth)) {
        const d = Number(dStr);
        if (mUp.minXByDepth[d] !== undefined) {
          const leftMax = fUpOffsetX + fUp.maxXByDepth[d];
          const rightMin = mUpOffsetX + mUp.minXByDepth[d];
          const overlap = leftMax + SUBTREE_GAP - rightMin;
          if (overlap > maxOverlap) maxOverlap = overlap;
        }
      }
      if (maxOverlap > 0) {
        fUpOffsetX -= maxOverlap / 2;
        mUpOffsetX += maxOverlap / 2;
      }
    }

    const allRowXs = [...fChildRelXs, ...mChildRelXs];
    const minXByDepth = {
      [depth]: Math.min(...allRowXs) - NODE_W / 2,
    };
    const maxXByDepth = {
      [depth]: Math.max(...allRowXs) + NODE_W / 2,
    };

    if (fUp && !fUp.isCutoff) {
      for (const dStr of Object.keys(fUp.minXByDepth)) {
        const d = Number(dStr);
        const l = fUpOffsetX + fUp.minXByDepth[d];
        const r = fUpOffsetX + fUp.maxXByDepth[d];
        minXByDepth[d] = minXByDepth[d] === undefined ? l : Math.min(minXByDepth[d], l);
        maxXByDepth[d] = maxXByDepth[d] === undefined ? r : Math.max(maxXByDepth[d], r);
      }
    }
    if (mUp && !mUp.isCutoff) {
      for (const dStr of Object.keys(mUp.minXByDepth)) {
        const d = Number(dStr);
        const l = mUpOffsetX + mUp.minXByDepth[d];
        const r = mUpOffsetX + mUp.maxXByDepth[d];
        minXByDepth[d] = minXByDepth[d] === undefined ? l : Math.min(minXByDepth[d], l);
        maxXByDepth[d] = maxXByDepth[d] === undefined ? r : Math.max(maxXByDepth[d], r);
      }
    }

    return {
      isCutoff: false,
      depth,
      fPerson: fCandidate,
      mPerson: mCandidate,
      familyId,
      famStatus,
      fRelX,
      mRelX,
      dropRelX,
      fSibNodes,
      mSibNodes,
      fUp,
      mUp,
      fUpOffsetX,
      mUpOffsetX,
      minXByDepth,
      maxXByDepth,
    };
  }

  function placeUpUnit(unit, unitCenterX, childBracketTargets) {
    if (!unit || unit.isCutoff) return;
    const y = -unit.depth * LEVEL_H;

    for (const s of unit.fSibNodes) {
      nodes.push({
        id: s.person.id,
        person: s.person,
        x: unitCenterX + s.relX,
        y,
        width: COLLATERAL_W,
        isFocused: false,
        isDirectAncestor: false,
        isSpouse: false,
        isCollateral: true,
        hiddenChildrenCount: 0,
        hiddenParentsCount: 0,
      });
    }

    let fAbsX = null;
    let mAbsX = null;

    if (unit.fPerson && unit.fRelX !== null) {
      fAbsX = unitCenterX + unit.fRelX;
      nodes.push({
        id: unit.fPerson.id,
        person: unit.fPerson,
        x: fAbsX,
        y,
        width: NODE_W,
        isFocused: false,
        isDirectAncestor: true,
        isSpouse: false,
        isCollateral: false,
        hiddenChildrenCount: 0,
        hiddenParentsCount: unit.fUp && unit.fUp.isCutoff ? unit.fUp.count : 0,
      });
    }

    if (unit.mPerson && unit.mRelX !== null) {
      mAbsX = unitCenterX + unit.mRelX;
      nodes.push({
        id: unit.mPerson.id,
        person: unit.mPerson,
        x: mAbsX,
        y,
        width: NODE_W,
        isFocused: false,
        isDirectAncestor: true,
        isSpouse: false,
        isCollateral: false,
        hiddenChildrenCount: 0,
        hiddenParentsCount: unit.mUp && unit.mUp.isCutoff ? unit.mUp.count : 0,
      });
    }

    for (const s of unit.mSibNodes) {
      nodes.push({
        id: s.person.id,
        person: s.person,
        x: unitCenterX + s.relX,
        y,
        width: COLLATERAL_W,
        isFocused: false,
        isDirectAncestor: false,
        isSpouse: false,
        isCollateral: true,
        hiddenChildrenCount: 0,
        hiddenParentsCount: 0,
      });
    }

    if (fAbsX !== null && mAbsX !== null) {
      marriages.push({
        x1: fAbsX + NODE_W / 2,
        x2: mAbsX - NODE_W / 2,
        midX: unitCenterX,
        y,
        familyId: unit.familyId,
        status: unit.famStatus,
      });
    }

    if (childBracketTargets && childBracketTargets.length > 0) {
      const parentDropX = unitCenterX + unit.dropRelX;
      const isCouple = fAbsX !== null && mAbsX !== null;
      const childRowY = -(unit.depth - 1) * LEVEL_H;
      brackets.push({
        parentX: parentDropX,
        parentY: y + (isCouple ? 12 : 39),
        childY: childRowY - 39,
        midY: (y + childRowY) / 2,
        isDescendant: false,
        children: childBracketTargets,
      });
    }

    if (unit.fUp && !unit.fUp.isCutoff && fAbsX !== null) {
      const fTargets = [
        ...unit.fSibNodes.map((s) => ({ x: unitCenterX + s.relX, isDirect: false })),
        { x: fAbsX, isDirect: true },
      ];
      placeUpUnit(unit.fUp, unitCenterX + unit.fUpOffsetX, fTargets);
    }

    if (unit.mUp && !unit.mUp.isCutoff && mAbsX !== null) {
      const mTargets =
        fAbsX !== null
          ? [
              { x: mAbsX, isDirect: true },
              ...unit.mSibNodes.map((s) => ({ x: unitCenterX + s.relX, isDirect: false })),
            ]
          : [
              ...unit.mSibNodes.map((s) => ({ x: unitCenterX + s.relX, isDirect: false })),
              { x: mAbsX, isDirect: true },
            ];
      placeUpUnit(unit.mUp, unitCenterX + unit.mUpOffsetX, mTargets);
    }
  }

  // Execute Downward Layout
  const downRoot = buildDownNode(rootId, 0);
  if (downRoot) assignDownCoords(downRoot, 0);

  // Place Focused Person's Siblings at Level 0 (to the left of focused person)
  const rootPrimaryX = downRoot ? downRoot.primaryX : 0;
  const rootSibs = state.showSiblings
    ? getSiblings(rootId).filter((s) => !visitedDown.has(s.id) && !visitedUp.has(s.id))
    : [];
  const level0Targets = [];

  rootSibs.forEach((sib, idx) => {
    visitedUp.add(sib.id);
    const sibX = rootPrimaryX - (rootSibs.length - idx) * SIB_STEP;
    nodes.push({
      id: sib.id,
      person: sib,
      x: sibX,
      y: 0,
      width: COLLATERAL_W,
      isFocused: false,
      isDirectAncestor: false,
      isSpouse: false,
      isCollateral: true,
      hiddenChildrenCount: 0,
      hiddenParentsCount: 0,
    });
    level0Targets.push({ x: sibX, isDirect: false });
  });

  level0Targets.push({ x: rootPrimaryX, isDirect: true });

  // Execute Upward Layout
  const upRoot = buildUpUnit(rootId, 1);
  let upCenterX =
    level0Targets.length > 0
      ? (level0Targets[0].x + level0Targets[level0Targets.length - 1].x) / 2
      : 0;

  // Collect Primary Spouse Upward Units at Level 0 (e.g. Yunus Nadi & Nadiye or Adem & Cevahir)
  const spouseUnits = [];
  if (downRoot && downRoot.spouses && downRoot.spouses.length > 0) {
    for (const sp of downRoot.spouses) {
      if (sp.person && !visitedUp.has(sp.person.id)) {
        const spUp = buildUpUnit(sp.person.id, 1);
        if (spUp) {
          const spNode = nodes.find((n) => n.id === sp.person.id);
          const defaultSpX = spNode ? spNode.x : rootPrimaryX + COUPLE_STEP;
          spouseUnits.push({
            sp,
            node: spNode,
            unit: spUp,
            targetX: defaultSpX,
          });
        }
      }
    }
  }

  // Resolve collisions between upRoot and spouseUnits, and between consecutive spouseUnits across all ancestor depths
  if (upRoot && !upRoot.isCutoff) {
    for (const su of spouseUnits) {
      if (su.unit && !su.unit.isCutoff) {
        let maxOverlap = 0;
        for (const dStr of Object.keys(upRoot.maxXByDepth)) {
          const d = Number(dStr);
          if (su.unit.minXByDepth[d] !== undefined) {
            const leftMax = upCenterX + upRoot.maxXByDepth[d];
            const rightMin = su.targetX + su.unit.minXByDepth[d];
            const overlap = leftMax + SUBTREE_GAP - rightMin;
            if (overlap > maxOverlap) maxOverlap = overlap;
          }
        }
        if (maxOverlap > 0) {
          upCenterX -= maxOverlap / 2;
          su.targetX += maxOverlap / 2;
        }
      }
    }
  }

  for (let i = 0; i < spouseUnits.length - 1; i++) {
    const leftSu = spouseUnits[i];
    const rightSu = spouseUnits[i + 1];
    if (leftSu.unit && !leftSu.unit.isCutoff && rightSu.unit && !rightSu.unit.isCutoff) {
      let maxOverlap = 0;
      for (const dStr of Object.keys(leftSu.unit.maxXByDepth)) {
        const d = Number(dStr);
        if (rightSu.unit.minXByDepth[d] !== undefined) {
          const leftMax = leftSu.targetX + leftSu.unit.maxXByDepth[d];
          const rightMin = rightSu.targetX + rightSu.unit.minXByDepth[d];
          const overlap = leftMax + SUBTREE_GAP - rightMin;
          if (overlap > maxOverlap) maxOverlap = overlap;
        }
      }
      if (maxOverlap > 0) {
        leftSu.targetX -= maxOverlap / 2;
        rightSu.targetX += maxOverlap / 2;
      }
    }
  }

  // Place Upward Units
  if (upRoot) {
    if (upRoot.isCutoff) {
      const rootNodeObj = nodes.find((n) => n.isFocused);
      if (rootNodeObj) rootNodeObj.hiddenParentsCount = upRoot.count;
    } else {
      placeUpUnit(upRoot, upCenterX, level0Targets);
    }
  }

  for (const su of spouseUnits) {
    if (su.unit.isCutoff) {
      if (su.node) su.node.hiddenParentsCount = su.unit.count;
    } else {
      placeUpUnit(su.unit, su.targetX, [{ x: su.node ? su.node.x : su.targetX, isDirect: true }]);
    }
  }

  return { nodes, marriages, brackets };
}

function renderTree(autoFit = false) {
  if (!state.data) return;
  if (!state.data.individuals[state.focusedId]) {
    state.focusedId = Object.keys(state.data.individuals)[0] || "I0";
  }
  const focusedPerson = state.data.individuals[state.focusedId];
  if (!focusedPerson) return;

  document.getElementById("focused-person-name").textContent = focusedPerson.name;
  document.getElementById("focused-person-meta").textContent = `(${formatLifeYears(focusedPerson)} • ${focusedPerson.descendantCount} ${t("descendantsBadge")})`;
  document.getElementById("history-back-btn").disabled = state.focusHistory.length === 0;
  document.title =
    state.lang === "tr"
      ? `${focusedPerson.name} — Niksiç & Hociç Soy Ağacı`
      : `${focusedPerson.name} — Niksiç & Hociç Family Tree`;

  document.querySelectorAll(".branch-pill").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.personId === state.focusedId);
  });

  const sibBtn = document.getElementById("toggle-siblings-btn");
  const sibLbl = document.getElementById("toggle-siblings-label");
  if (sibBtn && sibLbl) {
    sibBtn.classList.toggle("active", state.showSiblings);
    sibLbl.textContent = state.showSiblings ? t("siblingsToggleOn") : t("siblingsToggleOff");
  }

  const spBtn = document.getElementById("toggle-spouses-btn");
  const spLbl = document.getElementById("toggle-spouses-label");
  if (spBtn && spLbl) {
    spBtn.classList.toggle("active", state.showSpouses);
    spLbl.textContent = state.showSpouses ? t("spousesToggleOn") : t("spousesToggleOff");
  }

  const ancSelect = document.getElementById("ancestor-depth-select") as HTMLSelectElement;
  if (ancSelect && ancSelect.value !== String(state.ancestorDepth)) {
    ancSelect.value = String(state.ancestorDepth);
  }

  const descSelect = document.getElementById("descendant-depth-select") as HTMLSelectElement;
  if (descSelect && descSelect.value !== String(state.descendantDepth)) {
    descSelect.value = String(state.descendantDepth);
  }

  const { nodes, marriages, brackets } = buildHourglassLayout(
    state.focusedId,
    state.ancestorDepth,
    state.descendantDepth
  );

  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;

  for (const n of nodes) {
    const halfW = (n.width || NODE_W) / 2 + 40;
    if (n.x - halfW < minX) minX = n.x - halfW;
    if (n.x + halfW > maxX) maxX = n.x + halfW;
    if (n.y - 80 < minY) minY = n.y - 80;
    if (n.y + 80 > maxY) maxY = n.y + 80;
  }

  if (!isFinite(minX)) {
    minX = -400;
    maxX = 400;
    minY = -300;
    maxY = 300;
  }

  const pad = 64;
  const offsetX = -minX + pad;
  const offsetY = -minY + pad;
  const stageW = Math.max(360, maxX - minX + pad * 2);
  const stageH = Math.max(320, maxY - minY + pad * 2);

  state.treeBounds = { width: stageW, height: stageH };

  const svgEl = document.getElementById("tree-svg");
  const nodesLayer = document.getElementById("tree-nodes-layer");
  svgEl.setAttribute("width", String(stageW));
  svgEl.setAttribute("height", String(stageH));
  nodesLayer.style.width = `${stageW}px`;
  nodesLayer.style.height = `${stageH}px`;

  const svgParts = [];

  // 1. Render Spouse Marriage Bridges (solid green for current, dashed purple/slate with break for divorced)
  for (const m of marriages) {
    const x1 = m.x1 + offsetX;
    const x2 = m.x2 + offsetX;
    const y = m.y + offsetY;
    const isDivorced = m.status === "divorced";
    const bridgeClass = isDivorced ? "tree-link marriage-bridge is-divorced" : "tree-link marriage-bridge";
    svgParts.push(`<path class="${bridgeClass}" d="M ${x1} ${y} L ${x2} ${y}" />`);
  }

  // 2. Render Orthogonal Parent-to-Child Family Brackets
  for (const b of brackets) {
    if (!b.children || b.children.length === 0) continue;
    const px = b.parentX + offsetX;
    const py = b.parentY + offsetY;
    const cy = b.childY + offsetY;
    const midY = b.midY + offsetY;

    const childXs = b.children.map((c) => c.x + offsetX);
    const barMinX = Math.min(px, ...childXs);
    const barMaxX = Math.max(px, ...childXs);

    // Collateral / Sibling Bar Base Path
    const baseCls = b.isDescendant ? "descendant-link" : "collateral-link";
    svgParts.push(`<path class="tree-link ${baseCls}" d="M ${px} ${py} V ${midY} M ${barMinX} ${midY} H ${barMaxX}" />`);

    for (const ch of b.children) {
      const cx = ch.x + offsetX;
      svgParts.push(`<path class="tree-link ${baseCls}" d="M ${cx} ${midY} V ${cy}" />`);
    }

    // Highlight Direct Lineage Path on Top
    if (!b.isDescendant) {
      for (const ch of b.children) {
        if (ch.isDirect) {
          const cx = ch.x + offsetX;
          svgParts.push(
            `<path class="tree-link direct-lineage-link" d="M ${px} ${py} V ${midY} H ${cx} V ${cy}" />`
          );
        }
      }
    }
  }

  svgEl.innerHTML = svgParts.join("");

  // 3. Render Individual Person Cards & Marriage Badges
  nodesLayer.innerHTML = "";
  const frag = document.createDocumentFragment();
  state.layoutNodes = [];

  for (const n of nodes) {
    const cx = n.x + offsetX;
    const cy = n.y + offsetY;
    if (n.isFocused) state.focusedNodeCoords = { x: cx, y: cy };

    state.layoutNodes.push({
      id: n.id,
      x: cx,
      y: cy,
      relY: n.y,
      width: n.width || NODE_W,
      sex: n.person.sex,
      isFocused: n.isFocused,
      isDirectAncestor: n.isDirectAncestor,
      isSpouse: n.isSpouse,
      isCollateral: n.isCollateral,
    });

    const wrapEl = document.createElement("div");
    wrapEl.className = "tree-node-group";
    wrapEl.style.left = `${cx}px`;
    wrapEl.style.top = `${cy}px`;

    wrapEl.appendChild(
      createPersonNodeCard(n.person, {
        isFocused: n.isFocused,
        isDirectAncestor: n.isDirectAncestor,
        isSpouse: n.isSpouse,
        isCollateral: n.isCollateral,
        hiddenChildrenCount: n.hiddenChildrenCount,
        hiddenParentsCount: n.hiddenParentsCount,
      })
    );

    frag.appendChild(wrapEl);
  }

  for (const m of marriages) {
    const badgeWrap = document.createElement("div");
    badgeWrap.className = "tree-node-group";
    badgeWrap.style.left = `${m.midX + offsetX}px`;
    badgeWrap.style.top = `${m.y + offsetY}px`;
    badgeWrap.style.zIndex = "4";

    const isDivorced = m.status === "divorced";
    const badge = document.createElement("div");
    badge.className = `spouse-connector-badge${isDivorced ? " is-divorced" : ""}`;
    badge.title = isDivorced ? `${t("spouseDivorced")} (Ayrılmış / Boşanmış)` : t("spouseCurrent");
    badge.innerHTML = isDivorced 
      ? `<span class="divorce-slash">/</span>`
      : `⚭`;
    badge.style.cursor = "pointer";
    badge.addEventListener("click", (e) => {
      e.stopPropagation();
      if (m.familyId) requireEditorThen(() => openEditMarriageModal(m.familyId));
    });

    badgeWrap.appendChild(badge);
    frag.appendChild(badgeWrap);
  }

  nodesLayer.appendChild(frag);
  renderMinimapNodes();

  if (autoFit) smartFocusCamera();
  else applyStageTransform();
}

function createPersonNodeCard(person, opts) {
  const el = document.createElement("div");
  const isDeceased = Boolean(person.deceased);
  const classes = [
    "person-node",
    `sex-${person.sex}`,
    isDeceased ? "is-deceased" : "is-living",
    opts.isFocused ? "is-focused" : "",
    opts.isDirectAncestor ? "is-direct-ancestor" : "",
    opts.isSpouse ? "is-spouse" : "",
    opts.isCollateral ? "is-collateral" : "",
  ]
    .filter(Boolean)
    .join(" ");
  el.className = classes;

  const avatarIcon = person.avatarUrl
    ? `<img src="${escapeHtml(person.avatarUrl)}" alt="${escapeHtml(person.name)}" style="width:100%;height:100%;object-fit:cover;border-radius:6px;display:block;">`
    : renderAvatarSilhouette(person.sex, isDeceased);
  const starHtml = opts.isFocused
    ? `<span class="node-star is-focused-star" title="Odak Kişi">⭐</span>`
    : `<button type="button" class="node-star-btn" data-star-focus-id="${person.id}" title="${state.lang === 'tr' ? 'Bu kişiyi odakla' : 'Focus this person'}">☆</button>`;
  const surnameHtml = person.surname ? `<span class="node-surname">${escapeHtml(person.surname)}</span>` : "";

  // Check if collateral relative (sibling/aunt/uncle) or spouse has their own branch to explore
  const hasOwnBranch =
    (opts.isCollateral || opts.isSpouse) &&
    ((person.descendantCount && person.descendantCount > 0) ||
      (person.fams && person.fams.length > 0) ||
      (opts.isSpouse && person.famc && person.famc.length > 0));

  let actionBtnHtml = "";
  if (opts.isFocused) {
    actionBtnHtml = `<button type="button" class="node-focus-btn" data-add-id="${person.id}" title="Akraba Ekle">＋</button>`;
  } else if (hasOwnBranch) {
    const badgeCount =
      person.descendantCount && person.descendantCount > 0
        ? `+${person.descendantCount}`
        : opts.isSpouse && person.ancestorCount
          ? `↑${person.ancestorCount}`
          : "";
    actionBtnHtml = `<button type="button" class="collateral-branch-pill" data-focus-id="${person.id}" title="Bu kolu aç ve odakla">🌿${badgeCount ? ` ${badgeCount}` : ""}</button>`;
  }

  const branchInfo = getMaternalBranchInfo(person.id);
  const branchBadgeHtml = branchInfo
    ? `<div class="node-branch-row"><span class="node-branch-pill branch-style-${branchInfo.colorIndex}" title="${state.lang === 'tr' ? 'Evlilik / Anne Kolu: ' : 'Maternal/Family Branch: '}${escapeHtml(branchInfo.spouseName || branchInfo.label)} (${escapeHtml(branchInfo.parentName)})">${branchInfo.icon} ${escapeHtml(branchInfo.label)}</span></div>`
    : "";

  const givenName = person.given || person.name || "";
  const isLongName = givenName.length > 16 || (person.name && person.name.length > 22);
  const nameStyle = isLongName ? ' style="font-size: 0.72rem; line-height: 1.15;"' : '';

  el.innerHTML = `
    <div>
      <div class="node-top-row">
        <span class="node-avatar ${person.avatarUrl ? 'has-custom-avatar' : ''}" aria-hidden="true">${avatarIcon}</span>
        <div class="node-title-wrap">
          <div class="node-name"${nameStyle}>${escapeHtml(givenName)}</div>
          ${surnameHtml}
        </div>
        ${starHtml}
      </div>
      ${branchBadgeHtml}
    </div>
    <div class="node-bottom-row">
      <span class="node-lifespan" title="${isDeceased ? (state.lang === 'tr' ? 'Vefat Etmiş' : 'Deceased') : (state.lang === 'tr' ? 'Hayatta' : 'Living')}">${escapeHtml(formatLifeYears(person))}</span>
      ${actionBtnHtml}
    </div>
  `;

  if (opts.hiddenChildrenCount > 0) {
    const expDown = document.createElement("div");
    expDown.className = "expand-branch-badge";
    expDown.textContent = `+${opts.hiddenChildrenCount} ↓`;
    expDown.addEventListener("click", (e) => {
      e.stopPropagation();
      focusPersonInTree(person.id);
    });
    el.appendChild(expDown);
  }

  if (opts.hiddenParentsCount > 0) {
    const expUp = document.createElement("div");
    expUp.className = "expand-ancestor-badge";
    expUp.textContent = `+${opts.hiddenParentsCount} ↑`;
    expUp.addEventListener("click", (e) => {
      e.stopPropagation();
      focusPersonInTree(person.id);
    });
    el.appendChild(expUp);
  }

  const focusBtn = el.querySelector("[data-focus-id]");
  if (focusBtn) {
    focusBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      focusPersonInTree(person.id);
    });
  }

  const starBtn = el.querySelector("[data-star-focus-id]");
  if (starBtn) {
    starBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      focusPersonInTree(person.id);
    });
  }

  const addBtn = el.querySelector("[data-add-id]");
  if (addBtn) {
    addBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      requireEditorThen(() => openAddRelativeModal(person.id));
    });
  }

  el.addEventListener("click", () => openPersonDrawer(person.id));
  return el;
}

function focusPersonInTree(personId, pushHistory = true) {
  if (!state.data || !state.data.individuals[personId]) return;
  if (pushHistory && state.focusedId && state.focusedId !== personId) {
    state.focusHistory.push(state.focusedId);
  }
  state.focusedId = personId;
  history.replaceState(null, "", `#person=${personId}`);
  if (state.activeView !== "tree" && state.activeView !== "fan") {
    switchView("tree");
  }
  renderTree(true);
  if (state.activeView === "fan") renderFanChart();
}

/* ================= CONCENTRIC SVG FAN CHART (YELPAZE ÇİZELGESİ) ================= */
// Modularized in ./views/fan.ts

/* ================= VIEW 3: GEOGRAPHICAL MIGRATION MAP (HISTORICAL ATLAS) ================= */
// Modularized in ./views/map.ts

/* ================= PERSON DETAIL DRAWER ================= */
function openPersonDrawer(pid) {
  const p = state.data.individuals[pid];
  if (!p) return;
  state.selectedDrawerId = pid;

  const genderBadge = document.getElementById("drawer-gender-badge");
  genderBadge.className = `gender-pill sex-${p.sex}`;
  genderBadge.textContent = p.sex === "M" ? t("male") : p.sex === "F" ? t("female") : t("unknown");

  const isDeceased = Boolean(p.deceased);
  const statusBadge = document.getElementById("drawer-status-badge");
  statusBadge.className = `status-pill ${isDeceased ? "is-deceased" : "is-living"}`;
  statusBadge.textContent = isDeceased ? t("deceased") : t("living");

  const { father, mother } = getParents(pid);
  const unions = getSpousesAndChildren(pid);
  const siblings = getSiblings(pid);

  const parentsHtml = [
    father ? renderDrawerRelItem(father, t("fatherRole"), null, true) : "",
    mother ? renderDrawerRelItem(mother, t("motherRole"), null, true) : "",
  ]
    .filter(Boolean)
    .join("");

  const spousesHtml = unions
    .filter((u) => u.spouse)
    .map((u) =>
      renderDrawerRelItem(
        u.spouse,
        `${u.status === "divorced" ? `⚮ ${t("spouseDivorced")}` : `⚭ ${t("spouseCurrent")}`}${u.marriageYear ? ` (${u.marriageYear})` : ""}`,
        u.familyId
      )
    )
    .join("");

  const hasMultipleUnions = unions.length > 1;
  const childrenList = [];
  for (const u of unions) {
    for (const ch of u.children) childrenList.push({ person: ch, familyId: u.familyId, spouse: u.spouse });
  }
  const childrenHtml = childrenList
    .map((c) => {
      const baseRole =
        c.person.sex === "F"
          ? (t("stepDaughter") || "Kızı")
          : c.person.sex === "M"
            ? (t("stepSon") || "Oğlu")
            : (t("stepChild") || "Çocuğu");
      const uIdx = unions.findIndex((un) => un.familyId === c.familyId);
      const branchPill =
        hasMultipleUnions && c.spouse
          ? ` • <span class="drawer-rel-branch-pill branch-style-${Math.max(0, uIdx) % 4}">🌸 ${escapeHtml(c.spouse.given || c.spouse.name.split(" ")[0])} Kolu</span>`
          : "";
      return renderDrawerRelItem(c.person, `${baseRole}${branchPill}`);
    })
    .join("");

  const hasMultipleUnionsForParents =
    (father && father.fams && father.fams.length > 1) ||
    (mother && mother.fams && mother.fams.length > 1);
  const siblingsDetails = getSiblingsDetails(pid);
  const siblingsHtml = siblingsDetails
    .map((s) => {
      const baseRole =
        s.person.sex === "F"
          ? (t("stepSister") || "Kız Kardeşi")
          : s.person.sex === "M"
            ? (t("stepBrother") || "Erkek Kardeşi")
            : (t("stepSibling") || "Kardeşi");
      let badge = "";
      if (s.isHalf) {
        const br = getMaternalBranchInfo(s.person.id);
        const brLabel = br
          ? br.label
          : s.sharedParent === "father"
            ? state.lang === "tr"
              ? "Aynı Babadan"
              : "Paternal Half"
            : state.lang === "tr"
              ? "Aynı Anneden"
              : "Maternal Half";
        const colorIdx = br ? br.colorIndex : 1;
        const icon = br ? br.icon : "🌿";
        badge = ` • <span class="drawer-rel-branch-pill branch-style-${colorIdx}">${icon} ${escapeHtml(brLabel)}</span>`;
      } else if (hasMultipleUnionsForParents) {
        badge = ` • <span class="drawer-rel-branch-pill branch-style-0">🌸 ${state.lang === "tr" ? "Öz Kardeş" : "Full Sibling"}</span>`;
      }
      return renderDrawerRelItem(s.person, `${baseRole}${badge}`);
    })
    .join("");

  const personBranch = getMaternalBranchInfo(pid);

  const extraMeta = [
    p.birthPlace
      ? `📍 Doğum: <strong>${escapeHtml(p.birthPlace)}</strong> <button type="button" class="mini-btn map-locate-btn" data-place="${escapeHtml(p.birthPlace)}" title="Haritada Konumlandır" style="padding: 1px 6px; font-size: 0.72rem; margin-left: 3px; border-radius: 4px; cursor: pointer; vertical-align: middle;">🗺️ Harita</button>`
      : "",
    p.deathPlace
      ? `📍 Vefat: <strong>${escapeHtml(p.deathPlace)}</strong> <button type="button" class="mini-btn map-locate-btn" data-place="${escapeHtml(p.deathPlace)}" title="Haritada Konumlandır" style="padding: 1px 6px; font-size: 0.72rem; margin-left: 3px; border-radius: 4px; cursor: pointer; vertical-align: middle;">🗺️ Harita</button>`
      : "",
    p.occupation ? `💼 Meslek: ${escapeHtml(p.occupation)}` : "",
    p.birthSurname ? `🏷️ Kızlık/Doğum Soyadı: ${escapeHtml(p.birthSurname)}` : "",
  ]
    .filter(Boolean)
    .join(" • ");

  const body = document.getElementById("drawer-body-content");
  body.innerHTML = `
    <div class="drawer-header-profile">
      <span class="drawer-avatar sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" aria-hidden="true" style="${p.avatarUrl ? 'overflow:hidden;padding:0;background:transparent;' : ''}">
        ${p.avatarUrl ? `<img src="${escapeHtml(p.avatarUrl)}" alt="${escapeHtml(p.name)}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block;">` : renderAvatarSilhouette(p.sex, isDeceased)}
      </span>
      <div style="min-width:0;flex:1;">
        <h2 class="drawer-person-title" style="margin:0 0 0.25rem 0;">${escapeHtml(p.name)}</h2>
        <p class="drawer-dates-line" style="margin:0;">${escapeHtml(formatLifeYears(p))}</p>
        ${
          personBranch
            ? `<div style="margin-top:0.35rem;"><span class="node-branch-pill branch-style-${personBranch.colorIndex}" style="font-size:0.75rem;padding:0.12rem 0.55rem;cursor:default;">${personBranch.icon} ${escapeHtml(personBranch.label)} (${state.lang === 'tr' ? 'Ebeveyn:' : 'Parent:'} ${escapeHtml(personBranch.parentName)})</span></div>`
            : ""
        }
      </div>
    </div>
    ${extraMeta ? `<p class="drawer-dates-line" style="font-size:0.8rem;margin-top:0.35rem;">${extraMeta}</p>` : ""}
    ${p.notes ? `<div class="notes-box">${escapeHtml(p.notes)}</div>` : ""}

    <div class="drawer-action-row">
      <button type="button" class="drawer-btn primary" id="drawer-edit-btn">${t("editPersonBtn")}</button>
      <button type="button" class="drawer-btn primary" id="drawer-add-rel-btn">${t("addRelDrawerBtn")}</button>
    </div>

    <div class="drawer-action-row">
      <button type="button" class="drawer-btn" id="drawer-focus-tree-btn">${t("focusInTreeBtn")}</button>
      <button type="button" class="drawer-btn" id="drawer-kinship-btn">${t("kinshipWithBtn")}</button>
      <button type="button" class="drawer-btn" id="drawer-copy-link-btn">${t("copyLinkBtn")}</button>
    </div>

    <div class="drawer-action-row" style="margin-top: 0.35rem;">
      <button type="button" class="drawer-btn" id="drawer-share-card-btn" style="border-color: #d97706; color: #d97706; font-weight: 600;">📲 ${state.lang === 'tr' ? 'Akraba Kartı Paylaş' : 'Share Card'}</button>
      <button type="button" class="drawer-btn" id="drawer-photos-btn" style="border-color: #0284c7; color: #0284c7; font-weight: 600;">📸 ${state.lang === 'tr' ? 'Fotoğraflar & Belgeler' : 'Photos & Docs'}</button>
    </div>
    ${
      p.name && (p.name.includes("Hasan Naşit") || p.name.includes("Hasan") || p.name.includes("Adem") || pid === "I227")
        ? `<div class="drawer-action-row" style="margin-top: 0.35rem;">
            <button type="button" class="drawer-btn" id="drawer-archive-link-btn" style="border-color: var(--accent-gold); color: var(--accent-gold); font-weight: 700; width: 100%; display: flex; align-items: center; justify-content: center; gap: 0.4rem;">
              📁 ${state.lang === 'tr' ? 'Resmi Arşiv Yazışmaları & Belgeleri (CİMER & Arşiv) ➔' : 'Official Archive Petitions & Records ➔'}
            </button>
           </div>`
        : ""
    }

    <div class="drawer-stats-row">
      <div class="drawer-stat-box">
        <div class="drawer-stat-num">${p.generation + 1}.</div>
        <div class="drawer-stat-lbl">${t("generationStat")}</div>
      </div>
      <div class="drawer-stat-box">
        <div class="drawer-stat-num">${p.ancestorCount}</div>
        <div class="drawer-stat-lbl">${t("ancestorsStat")}</div>
      </div>
      <div class="drawer-stat-box">
        <div class="drawer-stat-num">${p.descendantCount}</div>
        <div class="drawer-stat-lbl">${t("descendantsStat")}</div>
      </div>
    </div>

    <div class="drawer-rel-group" id="drawer-media-section">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:0.5rem;">
        <div class="drawer-rel-title" style="margin:0;">📸 ${state.lang === 'tr' ? 'Fotoğraf ve Belgeler' : 'Photos & Documents'}</div>
        <button type="button" class="mini-btn" id="drawer-add-photo-btn" style="padding:2px 8px;font-size:0.75rem;cursor:pointer;">＋ ${state.lang === 'tr' ? 'Fotoğraf Ekle' : 'Add Photo'}</button>
      </div>
      <div id="drawer-media-grid" style="display:grid;grid-template-columns:repeat(auto-fill, minmax(75px, 1fr));gap:6px;min-height:30px;">
        <div style="grid-column:1/-1;font-size:0.75rem;color:var(--text-muted);padding:4px 0;">${state.lang === 'tr' ? 'Fotoğraflar yükleniyor...' : 'Loading photos...'}</div>
      </div>
    </div>

    ${
      parentsHtml
        ? `<div class="drawer-rel-group">
             <div class="drawer-rel-title">${t("parentsHeading")}</div>
             <div class="drawer-rel-list">${parentsHtml}</div>
           </div>`
        : ""
    }

    ${
      spousesHtml
        ? `<div class="drawer-rel-group">
             <div class="drawer-rel-title">${t("spousesHeading")}</div>
             <div class="drawer-rel-list">${spousesHtml}</div>
           </div>`
        : ""
    }

    ${
      childrenHtml
        ? `<div class="drawer-rel-group">
             <div class="drawer-rel-title">${t("childrenHeading")} (${childrenList.length})</div>
             <div class="drawer-rel-list">${childrenHtml}</div>
           </div>`
        : ""
    }

    ${
      siblingsHtml
        ? `<div class="drawer-rel-group">
             <div class="drawer-rel-title">${t("siblingsHeading")} (${siblings.length})</div>
             <div class="drawer-rel-list">${siblingsHtml}</div>
           </div>`
        : ""
    }
  `;

  body.querySelector("#drawer-edit-btn").addEventListener("click", () => {
    requireEditorThen(() => openEditPersonModal(pid));
  });

  body.querySelector("#drawer-add-rel-btn").addEventListener("click", () => {
    requireEditorThen(() => openAddRelativeModal(pid));
  });

  body.querySelector("#drawer-focus-tree-btn").addEventListener("click", () => {
    closePersonDrawer();
    focusPersonInTree(pid);
  });

  body.querySelector("#drawer-kinship-btn").addEventListener("click", () => {
    state.kinshipA = pid;
    closePersonDrawer();
    switchView("kinship");
    renderKinshipView();
  });

  body.querySelector("#drawer-share-card-btn").addEventListener("click", () => {
    openShareCardModal(pid);
  });

  body.querySelector("#drawer-photos-btn").addEventListener("click", () => {
    openPersonMediaModal(pid);
  });

  const archLinkBtn = body.querySelector("#drawer-archive-link-btn");
  if (archLinkBtn) {
    archLinkBtn.addEventListener("click", () => {
      closePersonDrawer();
      switchView("archive");
      const searchInput = document.getElementById("archive-search-input");
      if (searchInput) searchInput.value = "Hasan Naşit";
      state.archiveFilters.query = "Hasan Naşit";
      const clearBtn = document.getElementById("archive-clear-search-btn");
      if (clearBtn) clearBtn.classList.remove("hidden");
      renderArchiveView();
    });
  }

  body.querySelector("#drawer-add-photo-btn").addEventListener("click", () => {
    requireEditorThen(() => openAddPhotoModal(pid));
  });

  // Async load photos for drawer
  fetch(`/api/media?personId=${encodeURIComponent(pid)}`)
    .then((r) => r.json())
    .then((data) => {
      const grid = body.querySelector("#drawer-media-grid");
      if (!grid) return;
      const mediaList = data.media || [];
      if (mediaList.length === 0) {
        grid.innerHTML = `<div style="grid-column:1/-1;font-size:0.75rem;color:var(--text-muted);padding:4px 0;">${state.lang === 'tr' ? 'Henüz fotoğraf veya belge eklenmemiş.' : 'No photos yet.'}</div>`;
        return;
      }
      grid.innerHTML = mediaList
        .map(
          (m) => `
        <div class="drawer-media-thumb" data-media-id="${m.id}" title="${escapeHtml(m.title || 'Fotoğraf')} ${m.date_taken ? `(${escapeHtml(m.date_taken)})` : ''}" style="position:relative;width:100%;aspect-ratio:1;border-radius:6px;overflow:hidden;cursor:pointer;border:1px solid ${m.is_primary ? '#d97706' : 'var(--border-subtle)'};">
          <img src="${escapeHtml(m.thumbnail_url || m.url)}" alt="${escapeHtml(m.title || 'Fotoğraf')}" style="width:100%;height:100%;object-fit:cover;display:block;">
          ${m.is_primary ? '<span style="position:absolute;top:2px;right:2px;background:rgba(217,119,6,0.9);color:#fff;font-size:0.6rem;padding:1px 3px;border-radius:3px;font-weight:700;">★</span>' : ''}
        </div>
      `
        )
        .join("");

      grid.querySelectorAll(".drawer-media-thumb").forEach((thumb) => {
        thumb.addEventListener("click", () => {
          const m = mediaList.find((item) => item.id === thumb.dataset.mediaId);
          if (m) openPhotoLightboxModal(m, pid);
        });
      });
    })
    .catch(() => {
      const grid = body.querySelector("#drawer-media-grid");
      if (grid) grid.innerHTML = `<div style="grid-column:1/-1;font-size:0.75rem;color:var(--text-muted);padding:4px 0;">${state.lang === 'tr' ? 'Fotoğraflar çevrimdışı' : 'Offline'}</div>`;
    });

  const copyBtn = body.querySelector("#drawer-copy-link-btn");
  copyBtn.addEventListener("click", () => {
    const url = `${window.location.origin}${window.location.pathname}#person=${pid}`;
    navigator.clipboard.writeText(url);
    copyBtn.textContent = t("copiedText");
    setTimeout(() => {
      copyBtn.textContent = t("copyLinkBtn");
    }, 1800);
  });

  body.querySelectorAll(".drawer-rel-item").forEach((item) => {
    item.addEventListener("click", () => openPersonDrawer(item.dataset.personId));
  });

  body.querySelectorAll(".edit-marriage-chip").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      requireEditorThen(() => openEditMarriageModal(btn.dataset.familyId));
    });
  });

  body.querySelectorAll(".edit-rel-person-chip").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      requireEditorThen(() => openEditPersonModal(btn.dataset.personId));
    });
  });

  body.querySelectorAll(".map-locate-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const place = btn.dataset.place;
      if (place) locateCityOnMap(place);
    });
  });

  document.getElementById("drawer-backdrop").classList.remove("hidden");
  document.getElementById("person-drawer").classList.remove("hidden");
}

function renderDrawerRelItem(person, rightLabel, marriageFamId = null, canEditPerson = false) {
  const isDeceased = Boolean(person.deceased);
  return `
    <div class="drawer-rel-item sex-${person.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" data-person-id="${person.id}">
      <div style="display:flex;align-items:center;gap:0.6rem;min-width:0;flex:1;">
        <span class="rel-avatar sex-${person.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" aria-hidden="true">
          ${renderAvatarSilhouette(person.sex, isDeceased)}
        </span>
        <div style="min-width:0;flex:1;">
          <div class="sr-name">${escapeHtml(person.name)}</div>
          <div class="sr-sub">${escapeHtml(formatLifeYears(person))}</div>
        </div>
      </div>
      <div style="display:flex;gap:0.35rem;align-items:center;flex-shrink:0;">
        <span class="chip-btn" style="display:inline-flex;align-items:center;">${rightLabel}</span>
        ${
          marriageFamId
            ? `<button type="button" class="chip-btn edit-marriage-chip" data-family-id="${marriageFamId}" title="Evliliği Düzenle">✎</button>`
            : ""
        }
        ${
          canEditPerson
            ? `<button type="button" class="chip-btn edit-rel-person-chip" data-person-id="${person.id}" title="${escapeHtml(person.name)} Bilgilerini Düzenle">✎</button>`
            : ""
        }
      </div>
    </div>
  `;
}

function closePersonDrawer() {
  document.getElementById("drawer-backdrop").classList.add("hidden");
  document.getElementById("person-drawer").classList.remove("active");
  document.getElementById("person-drawer").classList.add("hidden");
}

/* ================= EDITOR MODE & CRUD MODALS ================= */
function updateEditorBadge() {
  const btn = document.getElementById("editor-mode-btn");
  const icon = document.getElementById("editor-mode-icon");
  const text = document.getElementById("editor-mode-text");
  const archAddBtn = document.getElementById("archive-add-doc-btn");
  if (!btn || !icon || !text) return;

  if (state.role === "admin") {
    btn.classList.add("is-editor");
    btn.classList.add("is-admin");
    icon.textContent = "🛡️";
    text.textContent = state.lang === "tr" ? "Yönetici (Admin)" : "Admin";
    if (archAddBtn) archAddBtn.style.display = "inline-flex";
  } else if (state.role === "editor") {
    btn.classList.add("is-editor");
    btn.classList.remove("is-admin");
    icon.textContent = "✍️";
    text.textContent = t("editorModeBadge");
    if (archAddBtn) archAddBtn.style.display = "none";
  } else {
    btn.classList.remove("is-editor");
    btn.classList.remove("is-admin");
    icon.textContent = "👁️";
    text.textContent = t("viewerModeBadge");
    if (archAddBtn) archAddBtn.style.display = "none";
  }
}

function openModal(title, htmlContent, isWide = false) {
  document.getElementById("modal-title").textContent = title;
  document.getElementById("modal-body-content").innerHTML = htmlContent;
  const card = document.getElementById("editor-modal-card");
  if (card) {
    card.classList.toggle("modal-wide", Boolean(isWide));
  }
  document.getElementById("modal-backdrop").classList.remove("hidden");
}

function closeModal() {
  const card = document.getElementById("editor-modal-card");
  if (card) {
    card.classList.remove("modal-wide");
    card.classList.remove("modal-extra-wide");
  }
  document.getElementById("modal-backdrop").classList.add("hidden");
}

/* ================= 📲 AKRABA KARTI PAYLAŞIMI (CANVAS GENERATOR) ================= */
function openShareCardModal(pid) {
  openShareCardModalView(pid, openModal);
}

/* ================= 🖨️ DEV AİLE POSTERİ & VEKTÖREL BASKI ================= */
function buildPosterSvgFromConfig() {
  return buildPosterSvgFromConfigView(buildHourglassLayout);
}

function openPosterExportModal() {
  openPosterExportModalView({
    buildHourglassLayout,
    openModal,
    closeModal,
  });
}

/* ================= ℹ️ SİSTEM HAKKINDA VE ÖZELLİKLER (ABOUT MODAL) ================= */
function openAboutModal() {
  const isTr = state.lang === "tr";
  const personCount = (state.data?.individuals && Object.keys(state.data.individuals).length)
    || state.data?.meta?.totalIndividuals
    || (state.personList && state.personList.length)
    || 778;
  const unionCount = (state.data?.families && Object.keys(state.data.families).length)
    || state.data?.meta?.totalFamilies
    || 292;
  const archiveCount = (state.archiveData && state.archiveData.length)
    || (state.archiveDocs && state.archiveDocs.length)
    || 109;

  const modalTitle = isTr ? "ℹ️ Sistem Hakkında & Özellikler Rehberi" : "ℹ️ About System & Feature Directory";

  const modalHtml = `
    <div style="display:flex; flex-direction:column; gap:1.25rem; max-width:850px;">
      <!-- System Identity & Abstract -->
      <div style="background:linear-gradient(135deg, rgba(35,83,64,0.08) 0%, rgba(212,175,55,0.12) 100%); padding:1.2rem 1.4rem; border-radius:14px; border:1px solid var(--border-subtle);">
        <div style="display:flex; align-items:center; gap:0.75rem; margin-bottom:0.5rem;">
          <span style="font-size:1.8rem;">🏛️</span>
          <div>
            <h3 style="margin:0; font-size:1.25rem; font-family:'Cormorant Garamond', Georgia, serif; font-weight:700; color:var(--text-primary);">
              Niksiç &amp; Hociç Aile Mirası ve Soy Kütüğü Portalı
            </h3>
            <p style="margin:0; font-size:0.82rem; color:var(--text-secondary); font-weight:500;">
              Karadağ (Nikšić) - Bosna-Hersek - Türkiye Aile Mirası &amp; Dijital Arşivi
            </p>
          </div>
        </div>
        <p style="margin:0.5rem 0 0; font-size:0.86rem; line-height:1.5; color:var(--text-primary);">
          ${isTr 
            ? "Bu platform, Niksiç ve Hociç ailelerinin asırlık soyağacını, göç yollarını, tarihi yazışmalarını ve akrabalık bağlarını en son web teknolojileriyle korumak ve gelecek kuşaklara aktarmak için geliştirilmiş yeni nesil, yüksek performanslı bir aile portalıdır."
            : "This platform is a next-generation, high-performance family portal developed to preserve and pass on the ancestral tree, migration routes, historical correspondence, and kinship ties of the Niksiç & Hociç families using state-of-the-art web technologies."}
        </p>
      </div>

      <!-- Live Metrics Bar -->
      <div class="about-metrics-bar">
        <div class="about-metric-item">
          <span class="about-metric-value">${personCount}</span>
          <span class="about-metric-label">${isTr ? "Kayıtlı Birey" : "Individuals"}</span>
        </div>
        <div class="about-metric-item">
          <span class="about-metric-value">${unionCount}</span>
          <span class="about-metric-label">${isTr ? "Aile Birliği" : "Family Unions"}</span>
        </div>
        <div class="about-metric-item">
          <span class="about-metric-value">${archiveCount}</span>
          <span class="about-metric-label">${isTr ? "Arşiv Belgesi" : "Historical Docs"}</span>
        </div>
        <div class="about-metric-item">
          <span class="about-metric-value">0</span>
          <span class="about-metric-label">${isTr ? "Graf Çakışması" : "Graph Overlaps"}</span>
        </div>
        <div class="about-metric-item">
          <span class="about-metric-value">100%</span>
          <span class="about-metric-label">Native TypeScript</span>
        </div>
        <div class="about-metric-item">
          <span class="about-metric-value">AES-256</span>
          <span class="about-metric-label">${isTr ? "Uçtan Uca Şifreli" : "Encrypted"}</span>
        </div>
      </div>

      <!-- Interactive Feature Cards Grid -->
      <div>
        <h4 style="margin:0 0 0.75rem; font-size:0.95rem; font-weight:700; color:var(--text-primary); text-transform:uppercase; letter-spacing:0.05em;">
          ${isTr ? "⚡ İnteraktif Özellikler & Modüller (Tıklayarak Geçiş Yapın)" : "⚡ Interactive Features & Modules (Click to Open)"}
        </h4>
        <div class="about-features-grid">
          
          <!-- Tree -->
          <div class="about-feature-card" data-about-action="view-tree">
            <div class="about-feature-header">
              <span class="about-feature-title">🌳 ${isTr ? "Kum Saati Soyağacı" : "Hourglass Pedigree"}</span>
              <span class="about-feature-badge">${isTr ? "Doğrusal Dinamik" : "Linear Dynamic"}</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "Aynı kuşaktaki kardeşleri yaş sırasına göre hizalayan, çakışmasız SVG bağlantıları ve yumuşak pan/zoom destekli ana ağaç görünümü." : "Interactive pedigree tree with generational spacing, birth-order sorting, and smooth zoom/pan."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Soyağacını Aç" : "Open Pedigree"}</span>
          </div>

          <!-- Fan Chart -->
          <div class="about-feature-card" data-about-action="view-fan">
            <div class="about-feature-header">
              <span class="about-feature-title">🪭 ${isTr ? "Konsantrik Yelpaze" : "Concentric Fan Chart"}</span>
              <span class="about-feature-badge">${isTr ? "Kutupsal Geometri" : "Polar Geometry"}</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "Seçilen bireyin 5 kuşağa kadar tüm ata ve torun zincirini polar açılarla sunan estetik dairesel soyağacı çizelgesi." : "Polar coordinate radial tree visualizing up to 5 generations of ancestors and descendants."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Yelpazeyi Aç" : "Open Fan Chart"}</span>
          </div>

          <!-- Migration Map -->
          <div class="about-feature-card" data-about-action="view-map">
            <div class="about-feature-header">
              <span class="about-feature-title">🗺️ ${isTr ? "Tarihsel Göç Atlası" : "Migration Atlas"}</span>
              <span class="about-feature-badge">${isTr ? "Coğrafi Rota" : "Geo Timeline"}</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "Balkanlar'dan Anadolu'ya (Nikšić, Mostar, Saraybosna, İstanbul, İzmir, Karamürsel) aile göç rotalarını harita üzerinde keşfedin." : "Explore historical migration routes across the Balkans and Anatolia with animated coordinates."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Haritayı Aç" : "Open Map"}</span>
          </div>

          <!-- Archive -->
          <div class="about-feature-card" data-about-action="view-archive">
            <div class="about-feature-header">
              <span class="about-feature-title">📁 ${isTr ? "Belge & Yazışma Arşivi" : "Archive & Letters"}</span>
              <span class="about-feature-badge">109 ${isTr ? "Doküman" : "Docs"}</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "Turgay Ener ve aile üyelerinden derlenen Osmanlıca/Türkçe belgeler, fotoğraflar ve kişi katkı filtreli dijital arşiv." : "Searchable archive of historical family letters, certificates, and photos linked to family members."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Arşivi Aç" : "Open Archive"}</span>
          </div>

          <!-- Directory -->
          <div class="about-feature-card" data-about-action="view-directory">
            <div class="about-feature-header">
              <span class="about-feature-title">👥 ${isTr ? "Kişi Rehberi & Filtreler" : "People Directory"}</span>
              <span class="about-feature-badge">${isTr ? "Hızlı Arama" : "Instant Search"}</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "770'ten fazla aile bireyini isim, doğum yılı, unvan ve yaşam durumuna göre anında filtreleyip detaylı biyografilerini inceleyin." : "Filterable biographical index of over 770 family individuals with metadata and quick actions."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Rehberi Aç" : "Open Directory"}</span>
          </div>

          <!-- Kinship BFS -->
          <div class="about-feature-card" data-about-action="view-kinship">
            <div class="about-feature-header">
              <span class="about-feature-title">🔗 ${isTr ? "Akrabalık Rotası (BFS)" : "Kinship Path Finder"}</span>
              <span class="about-feature-badge">${isTr ? "Algoritmik" : "Shortest Path"}</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "Herhangi iki aile üyesi arasındaki en kısa kan bağını (kuzen, amca, kayın vb.) adım adım hesaplayıp görselleştiren akıllı motor." : "Calculates and diagrams the exact shortest kinship path and relationship title between any two members."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Akrabalık Bul" : "Find Kinship"}</span>
          </div>

          <!-- Statistics -->
          <div class="about-feature-card" data-about-action="view-stats">
            <div class="about-feature-header">
              <span class="about-feature-title">📊 ${isTr ? "Demografik İstatistikler" : "Demographics & Stats"}</span>
              <span class="about-feature-badge">${isTr ? "Canlı Analiz" : "Live Metrics"}</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "Kuşaklar arası yaş ortalamaları, cinsiyet dağılımı, en yaygın isimler ve asırlık ömür beklentisi grafikleri." : "Generational demographics, lifespan averages, gender distribution, and popular family names."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "İstatistikleri Gör" : "View Stats"}</span>
          </div>

          <!-- Poster Export -->
          <div class="about-feature-card" data-about-action="action-poster">
            <div class="about-feature-header">
              <span class="about-feature-title">🖨️ ${isTr ? "Dev Duvar Posteri (A0/A1)" : "Wall Poster Export (SVG)"}</span>
              <span class="about-feature-badge">Vector / SVG</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "Matbaa ve dev ebatlı tablo baskıları için kayıpsız vektörel SVG, kuşak renklendirmesi ve başlık şablonlu dışa aktarım." : "Print-ready high-resolution vector SVG export with customizable decorative borders and fonts."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Poster Sihirbazı" : "Poster Wizard"}</span>
          </div>

          <!-- Share Card -->
          <div class="about-feature-card" data-about-action="action-sharecard">
            <div class="about-feature-header">
              <span class="about-feature-title">📲 ${isTr ? "Sosyal Akraba Kartı" : "Kinship Share Card"}</span>
              <span class="about-feature-badge">HD Canvas / PNG</span>
            </div>
            <p class="about-feature-desc">
              ${isTr ? "WhatsApp, Instagram ve aile gruplarında paylaşmak üzere altın varaklı estetik dijital akraba kartı üretici." : "Generates elegant gold-bordered portrait cards optimized for WhatsApp and Instagram stories."}
            </p>
            <span class="about-feature-action">➔ ${isTr ? "Kart Üret" : "Generate Card"}</span>
          </div>

        </div>
      </div>

      <!-- Technical Architecture Footer -->
      <div style="background:var(--bg-primary); border:1px solid var(--border-subtle); padding:0.9rem 1.1rem; border-radius:10px; font-size:0.78rem; color:var(--text-secondary); line-height:1.5;">
        <div style="font-weight:700; color:var(--text-primary); margin-bottom:0.25rem;">
          ⚙️ ${isTr ? "Teknik Mimari & Güvenlik Standardı" : "Technical Architecture & Security"}
        </div>
        <div>
          ${isTr 
            ? "Cloudflare Pages & Edge Functions (100% Native TypeScript) • Cloudflare D1 SQL Veritabanı • WebCrypto AES-256-GCM Uçtan Uca İstemci Tarafı Şifreleme • PWA Çevrimdışı Çalışma Kabiliyeti • Bun & esbuild Hızlı Derleme Pipeline'ı."
            : "Cloudflare Pages & Edge Functions (100% Native TypeScript) • Cloudflare D1 SQL Engine • WebCrypto AES-256-GCM End-to-End Encryption • Progressive Web App (Offline-ready) • Bun & esbuild Pipeline."}
        </div>
      </div>
    </div>
  `;

  openModal(modalTitle, modalHtml, true);

  // Bind interactive clicks on cards
  const modalContainer = document.getElementById("modal-container");
  if (!modalContainer) return;

  modalContainer.querySelectorAll(".about-feature-card").forEach((card) => {
    card.addEventListener("click", () => {
      const action = card.getAttribute("data-about-action");
      closeModal();
      if (!action) return;

      if (action.startsWith("view-")) {
        const viewName = action.replace("view-", "");
        switchView(viewName);
      } else if (action === "action-poster") {
        openPosterExportModal();
      } else if (action === "action-sharecard") {
        openShareCardModal(state.focusedId || "I227");
      }
    });
  });
}

/* ================= 📥 AKILLI GEDCOM İÇE AKTARMA SİHİRBAZI ================= */
function parseGedcom(text) {
  if (!text) return { individuals: [], families: [] };
  const lines = text.split(/\r?\n/);
  const indis = [];
  const fams = [];
  let curIndi = null;
  let curFam = null;
  let curContext = null;

  for (let rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const m = line.match(/^(\d+)\s+(@\w+@|[A-Za-z0-9_]+)?\s*(.*)$/);
    if (!m) continue;
    const level = parseInt(m[1], 10);
    const tagOrId = m[2];
    const rest = m[3] || "";

    if (level === 0) {
      if (rest === "INDI" || (tagOrId && tagOrId.startsWith("@I") && rest === "INDI")) {
        const id = (tagOrId || "").replace(/@/g, "");
        curIndi = { id, given: "", surname: "", sex: "U", birthYear: "", birthPlace: "", deathYear: "", deathPlace: "", deceased: 0, occupation: "", notes: "", famc: [], fams: [] };
        indis.push(curIndi);
        curFam = null;
        curContext = "INDI";
      } else if (rest === "FAM" || (tagOrId && tagOrId.startsWith("@F") && rest === "FAM")) {
        const id = (tagOrId || "").replace(/@/g, "");
        curFam = { id, husb: null, wife: null, children: [], marriageYear: "", marriagePlace: "" };
        fams.push(curFam);
        curIndi = null;
        curContext = "FAM";
      } else {
        curIndi = null;
        curFam = null;
        curContext = null;
      }
      continue;
    }

    if (curContext === "INDI" && curIndi) {
      if (level === 1) {
        if (tagOrId === "NAME") {
          const nameParts = rest.split("/");
          curIndi.given = (nameParts[0] || "").trim();
          curIndi.surname = (nameParts[1] || "").trim();
          curIndi.name = `${curIndi.given} ${curIndi.surname}`.trim();
        } else if (tagOrId === "SEX") {
          curIndi.sex = rest.toUpperCase().startsWith("M") ? "M" : rest.toUpperCase().startsWith("F") ? "F" : "U";
        } else if (tagOrId === "BIRT") {
          curIndi._sub = "BIRT";
        } else if (tagOrId === "DEAT") {
          curIndi._sub = "DEAT";
          curIndi.deceased = 1;
        } else if (tagOrId === "OCCU") {
          curIndi.occupation = rest;
        } else if (tagOrId === "NOTE") {
          curIndi.notes = (curIndi.notes ? curIndi.notes + " " : "") + rest;
        } else if (tagOrId === "FAMC") {
          curIndi.famc.push(rest.replace(/@/g, ""));
        } else if (tagOrId === "FAMS") {
          curIndi.fams.push(rest.replace(/@/g, ""));
        }
      } else if (level === 2 && curIndi._sub) {
        if (tagOrId === "DATE") {
          const yearMatch = rest.match(/\b(\d{4})\b/);
          if (curIndi._sub === "BIRT" && yearMatch) curIndi.birthYear = yearMatch[1];
          if (curIndi._sub === "DEAT" && yearMatch) curIndi.deathYear = yearMatch[1];
        } else if (tagOrId === "PLAC") {
          if (curIndi._sub === "BIRT") curIndi.birthPlace = rest;
          if (curIndi._sub === "DEAT") curIndi.deathPlace = rest;
        }
      }
    } else if (curContext === "FAM" && curFam) {
      if (level === 1) {
        if (tagOrId === "HUSB") curFam.husb = rest.replace(/@/g, "");
        else if (tagOrId === "WIFE") curFam.wife = rest.replace(/@/g, "");
        else if (tagOrId === "CHIL") curFam.children.push(rest.replace(/@/g, ""));
        else if (tagOrId === "MARR") curFam._sub = "MARR";
      } else if (level === 2 && curFam._sub === "MARR") {
        if (tagOrId === "DATE") {
          const yearMatch = rest.match(/\b(\d{4})\b/);
          if (yearMatch) curFam.marriageYear = yearMatch[1];
        } else if (tagOrId === "PLAC") {
          curFam.marriagePlace = rest;
        }
      }
    }
  }

  return { individuals: indis, families: fams };
}

function diffGedcomWithCurrentData(parsed) {
  const currentIndis = state.data?.individuals || {};
  const currentFams = state.data?.families || {};

  const newIndividuals = [];
  const updateIndividuals = [];
  const sameIndividuals = [];

  for (const incoming of parsed.individuals) {
    let match = currentIndis[incoming.id];
    if (!match) {
      const inNameNorm = normalizeText(`${incoming.given} ${incoming.surname}`);
      match = Object.values(currentIndis).find((p) => {
        if (normalizeText(p.name) !== inNameNorm) return false;
        if (incoming.birthYear && p.birthYear && incoming.birthYear !== p.birthYear) return false;
        return true;
      });
    }

    if (!match) {
      newIndividuals.push({
        id: incoming.id || ("I" + Date.now() + Math.floor(Math.random() * 100)),
        given: incoming.given,
        surname: incoming.surname,
        name: `${incoming.given} ${incoming.surname}`.trim(),
        sex: incoming.sex,
        birthYear: incoming.birthYear,
        birthPlace: incoming.birthPlace,
        deathYear: incoming.deathYear,
        deathPlace: incoming.deathPlace,
        deceased: incoming.deceased,
        occupation: incoming.occupation,
        notes: incoming.notes,
        action: "NEW",
      });
    } else {
      const diffs = [];
      if (incoming.birthYear && !match.birthYear) diffs.push(`Doğum Yılı: ${incoming.birthYear}`);
      if (incoming.birthPlace && !match.birthPlace) diffs.push(`Doğum Yeri: ${incoming.birthPlace}`);
      if (incoming.deathYear && !match.deathYear) diffs.push(`Vefat Yılı: ${incoming.deathYear}`);
      if (incoming.deathPlace && !match.deathPlace) diffs.push(`Vefat Yeri: ${incoming.deathPlace}`);
      if (incoming.occupation && !match.occupation) diffs.push(`Meslek: ${incoming.occupation}`);
      if (incoming.notes && !match.notes) diffs.push(`Not: ${incoming.notes}`);

      if (diffs.length > 0) {
        updateIndividuals.push({
          id: match.id,
          given: match.given,
          surname: match.surname,
          name: match.name,
          diffText: diffs.join(", "),
          birthYear: incoming.birthYear || match.birthYear,
          birthPlace: incoming.birthPlace || match.birthPlace,
          deathYear: incoming.deathYear || match.deathYear,
          deathPlace: incoming.deathPlace || match.deathPlace,
          occupation: incoming.occupation || match.occupation,
          notes: incoming.notes || match.notes,
          action: "UPDATE",
        });
      } else {
        sameIndividuals.push(match);
      }
    }
  }

  const newFamilies = [];
  const newFamilyChildren = [];
  for (const f of parsed.families) {
    if (!currentFams[f.id]) {
      newFamilies.push({
        id: f.id || ("F" + Date.now() + Math.floor(Math.random() * 100)),
        husb: f.husb,
        wife: f.wife,
        marriageYear: f.marriageYear,
        marriagePlace: f.marriagePlace,
      });
      for (let i = 0; i < (f.children || []).length; i++) {
        newFamilyChildren.push({
          familyId: f.id,
          childId: f.children[i],
          sortOrder: i,
        });
      }
    }
  }

  return {
    newIndividuals,
    updateIndividuals,
    sameIndividuals,
    newFamilies,
    newFamilyChildren,
  };
}

function openGedcomImportModal() {
  const modalHtml = `
    <div style="display:flex;flex-direction:column;gap:1rem;">
      <p class="section-subtitle">
        ${state.lang === "tr" ? "Family Tree Maker, MyHeritage, Ancestry veya Gramps'tan dışa aktardığınız .ged dosyasını yükleyin. Sistem mevcut 778 kişilik veritabanıyla akıllı eşleştirme ve fark analizi yapar." : "Upload a .ged file to diff and import individuals."}
      </p>

      <div id="gedcom-upload-step">
        <div id="gedcom-dropzone" class="gedcom-dropzone">
          <input type="file" id="gedcom-file-input" accept=".ged" style="display:none;" />
          <div style="font-size:2.4rem;margin-bottom:0.4rem;">📤</div>
          <div style="font-weight:700;font-size:1rem;margin-bottom:0.25rem;">${state.lang === "tr" ? "GEDCOM (.ged) dosyasını buraya sürükleyin" : "Drop GEDCOM file here"}</div>
          <div style="font-size:0.8rem;color:var(--text-muted);margin-bottom:0.85rem;">${state.lang === "tr" ? "veya bilgisayarınızdan seçmek için tıklayın" : "or click to select"}</div>
          <button type="button" class="primary-btn mini-btn" id="browse-ged-btn">${state.lang === "tr" ? "Dosya Seç (.ged)" : "Select File"}</button>
        </div>
      </div>

      <div id="gedcom-preview-step" style="display:none;flex-direction:column;gap:0.75rem;">
        <div class="gedcom-stats-row">
          <div class="gedcom-stat-pill" style="border-color:#10b981;">
            <div class="gedcom-stat-num" style="color:#10b981;" id="stat-ged-new">0</div>
            <div class="gedcom-stat-lbl">✨ Yeni Kişi</div>
          </div>
          <div class="gedcom-stat-pill" style="border-color:#f59e0b;">
            <div class="gedcom-stat-num" style="color:#f59e0b;" id="stat-ged-update">0</div>
            <div class="gedcom-stat-lbl">🔄 Güncelleme</div>
          </div>
          <div class="gedcom-stat-pill" style="border-color:#94a3b8;">
            <div class="gedcom-stat-num" style="color:#94a3b8;" id="stat-ged-same">0</div>
            <div class="gedcom-stat-lbl">✓ Değişmeyen</div>
          </div>
          <div class="gedcom-stat-pill" style="border-color:#3b82f6;">
            <div class="gedcom-stat-num" style="color:#3b82f6;" id="stat-ged-fams">0</div>
            <div class="gedcom-stat-lbl">👥 Yeni Aile Bağı</div>
          </div>
        </div>

        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h4 style="margin:0;font-size:0.95rem;">Değişiklik Önizlemesi</h4>
          <span style="font-size:0.78rem;color:var(--text-muted);">İçe aktarmak istediklerinizi onaylayın</span>
        </div>

        <div class="gedcom-diff-table-wrap">
          <table class="gedcom-diff-table">
            <thead>
              <tr>
                <th style="width:36px;"><input type="checkbox" id="ged-check-all" checked></th>
                <th>Durum</th>
                <th>Kişi</th>
                <th>Fark / Eklenen Bilgi</th>
              </tr>
            </thead>
            <tbody id="gedcom-diff-tbody"></tbody>
          </table>
        </div>

        <div id="gedcom-import-error" class="auth-error hidden"></div>

        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:0.5rem;">
          <button type="button" class="mini-btn" id="ged-back-btn">← Yeniden Seç</button>
          <div style="display:flex;gap:0.5rem;">
            <button type="button" class="mini-btn" id="ged-cancel-btn">İptal</button>
            <button type="button" class="primary-btn small-btn" id="ged-apply-btn" style="background:#10b981;border-color:#10b981;color:#fff;font-weight:700;">
              ✓ Seçilenleri İçe Aktar
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  openModal(`📥 ${state.lang === "tr" ? "Akıllı GEDCOM İçe Aktarma Sihirbazı" : "Smart GEDCOM Import Wizard"}`, modalHtml, true);

  const dropzone = document.getElementById("gedcom-dropzone");
  const fileInput = document.getElementById("gedcom-file-input");
  const browseBtn = document.getElementById("browse-ged-btn");
  const uploadStep = document.getElementById("gedcom-upload-step");
  const previewStep = document.getElementById("gedcom-preview-step");

  browseBtn?.addEventListener("click", () => fileInput?.click());
  dropzone?.addEventListener("click", () => fileInput?.click());

  dropzone?.addEventListener("dragover", (e) => {
    e.preventDefault();
    dropzone.classList.add("dragover");
  });
  dropzone?.addEventListener("dragleave", () => dropzone.classList.remove("dragover"));
  dropzone?.addEventListener("drop", (e) => {
    e.preventDefault();
    dropzone.classList.remove("dragover");
    const file = e.dataTransfer?.files?.[0];
    if (file) handleGedcomFile(file);
  });

  fileInput?.addEventListener("change", (e) => {
    const file = e.target.files?.[0];
    if (file) handleGedcomFile(file);
  });

  let currentDiff = null;

  function handleGedcomFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseGedcom(text);
      currentDiff = diffGedcomWithCurrentData(parsed);

      document.getElementById("stat-ged-new").textContent = currentDiff.newIndividuals.length;
      document.getElementById("stat-ged-update").textContent = currentDiff.updateIndividuals.length;
      document.getElementById("stat-ged-same").textContent = currentDiff.sameIndividuals.length;
      document.getElementById("stat-ged-fams").textContent = currentDiff.newFamilies.length;

      const tbody = document.getElementById("gedcom-diff-tbody");
      const rows = [];

      for (let i = 0; i < currentDiff.newIndividuals.length; i++) {
        const item = currentDiff.newIndividuals[i];
        rows.push(`
          <tr>
            <td><input type="checkbox" class="ged-item-check" data-type="new" data-idx="${i}" checked></td>
            <td><span class="gedcom-status-tag gedcom-status-new">✨ Yeni</span></td>
            <td><strong>${escapeHtml(item.name || item.given || "İsimsiz")}</strong> <span style="font-size:0.75rem;color:var(--text-muted);">${formatLifeYears(item)}</span></td>
            <td style="color:var(--text-secondary);">${[item.birthPlace ? `Doğum: ${item.birthPlace}` : '', item.occupation ? `Meslek: ${item.occupation}` : ''].filter(Boolean).join(" • ") || "Yeni kayıt olarak eklenecek"}</td>
          </tr>
        `);
      }

      for (let i = 0; i < currentDiff.updateIndividuals.length; i++) {
        const item = currentDiff.updateIndividuals[i];
        rows.push(`
          <tr>
            <td><input type="checkbox" class="ged-item-check" data-type="update" data-idx="${i}" checked></td>
            <td><span class="gedcom-status-tag gedcom-status-update">🔄 Güncelleme</span></td>
            <td><strong>${escapeHtml(item.name)}</strong></td>
            <td style="color:#f59e0b;">${escapeHtml(item.diffText)}</td>
          </tr>
        `);
      }

      if (rows.length === 0) {
        rows.push(`<tr><td colspan="4" style="text-align:center;padding:1.5rem;color:var(--text-muted);">Yüklenen GEDCOM dosyasındaki tüm kayıtlar mevcut veritabanınızla birebir aynıdır. Eklenecek yeni kişi veya güncelleme bulunamadı.</td></tr>`);
      }

      tbody.innerHTML = rows.join("");

      uploadStep.style.display = "none";
      previewStep.style.display = "flex";
    };
    reader.readAsText(file);
  }

  document.getElementById("ged-back-btn")?.addEventListener("click", () => {
    previewStep.style.display = "none";
    uploadStep.style.display = "block";
    fileInput.value = "";
  });

  document.getElementById("ged-cancel-btn")?.addEventListener("click", closeModal);

  document.getElementById("ged-check-all")?.addEventListener("change", (e) => {
    const ch = e.target.checked;
    document.querySelectorAll(".ged-item-check").forEach((inp) => (inp.checked = ch));
  });

  document.getElementById("ged-apply-btn")?.addEventListener("click", () => {
    requireEditorThen(async () => {
      if (!currentDiff) return;

      const checkedNew = [];
      const checkedUpdate = [];
      document.querySelectorAll(".ged-item-check:checked").forEach((inp) => {
        const type = inp.dataset.type;
        const idx = parseInt(inp.dataset.idx, 10);
        if (type === "new" && currentDiff.newIndividuals[idx]) checkedNew.push(currentDiff.newIndividuals[idx]);
        if (type === "update" && currentDiff.updateIndividuals[idx]) checkedUpdate.push(currentDiff.updateIndividuals[idx]);
      });

      if (checkedNew.length === 0 && checkedUpdate.length === 0) {
        alert("Lütfen içe aktarılacak en az bir kayıt seçiniz.");
        return;
      }

      const applyBtn = document.getElementById("ged-apply-btn");
      applyBtn.textContent = "İçe Aktarılıyor...";
      applyBtn.disabled = true;

      try {
        const res = await apiRequest("gedcom-import", "POST", {
          newIndividuals: checkedNew,
          updateIndividuals: checkedUpdate,
          newFamilies: currentDiff.newFamilies,
          newFamilyChildren: currentDiff.newFamilyChildren,
        });

        if (res && res.ok) {
          alert(`GEDCOM Başarıyla İçe Aktarıldı!\n\n✨ ${checkedNew.length} yeni kişi eklendi\n🔄 ${checkedUpdate.length} kişi güncellendi\n👥 ${currentDiff.newFamilies.length} aile bağı kuruldu.`);
          closeModal();
          await syncFromCloud();
        } else {
          throw new Error(res?.error || "İçe aktarım başarısız oldu");
        }
      } catch (err) {
        const errBox = document.getElementById("gedcom-import-error");
        errBox.textContent = err.message || "İçe aktarma hatası oluştu";
        errBox.classList.remove("hidden");
        applyBtn.textContent = "✓ Seçilenleri İçe Aktar";
        applyBtn.disabled = false;
      }
    });
  });
}

/* ================= 📸 FOTOĞRAF VE TARİHİ BELGE ARŞİVİ ================= */
async function openPersonMediaModal(pid) {
  const p = state.data?.individuals[pid];
  if (!p) return;

  openModal(`📸 ${p.name} - ${state.lang === 'tr' ? 'Fotoğraf ve Tarihi Belgeler' : 'Photos & Documents'}`, `
    <div style="display:flex;flex-direction:column;gap:1rem;">
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:0.85rem;color:var(--text-secondary);">${state.lang === 'tr' ? 'Arşivlenen fotoğraflar ve tarihi aile vesikaları.' : 'Archived photographs and historical documents.'}</span>
        <button type="button" class="primary-btn mini-btn" id="modal-add-media-btn">＋ ${state.lang === 'tr' ? 'Yeni Yükle' : 'Upload New'}</button>
      </div>
      <div id="media-modal-gallery" class="media-gallery-grid">
        <div style="grid-column:1/-1;text-align:center;padding:2rem;color:var(--text-muted);">${state.lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
      </div>
    </div>
  `, true);

  document.getElementById("modal-add-media-btn")?.addEventListener("click", () => {
    requireEditorThen(() => openAddPhotoModal(pid));
  });

  try {
    const res = await fetch(`/api/media?personId=${encodeURIComponent(pid)}`);
    const data = await res.json();
    const list = data.media || [];
    const galleryEl = document.getElementById("media-modal-gallery");
    if (!galleryEl) return;

    if (list.length === 0) {
      galleryEl.innerHTML = `
        <div style="grid-column: 1 / -1; text-align: center; padding: 2.5rem 1rem; color: var(--text-muted); background: var(--bg-primary); border-radius: 12px; border: 1px dashed var(--border-subtle);">
          <div style="font-size: 2.2rem; margin-bottom: 0.5rem;">📷</div>
          <div style="font-weight: 600; font-size: 0.95rem; margin-bottom: 0.25rem;">${state.lang === 'tr' ? 'Henüz fotoğraf veya belge eklenmemiş' : 'No photos yet'}</div>
          <div style="font-size: 0.8rem; margin-bottom: 1rem;">${state.lang === 'tr' ? 'Bu kişi için ilk hatıra fotoğrafını veya tarihi vesikayı hemen ekleyin.' : 'Upload the first portrait or historical document.'}</div>
          <button type="button" class="primary-btn mini-btn" id="gallery-empty-add-btn">＋ ${state.lang === 'tr' ? 'Fotoğraf Yükle' : 'Upload Photo'}</button>
        </div>
      `;
      document.getElementById("gallery-empty-add-btn")?.addEventListener("click", () => {
        requireEditorThen(() => openAddPhotoModal(pid));
      });
      return;
    }

    galleryEl.innerHTML = list.map((m) => `
      <div class="media-card-item" data-media-id="${m.id}">
        <div class="media-card-thumb-wrap">
          <img src="${escapeHtml(m.thumbnail_url || m.url)}" alt="${escapeHtml(m.title || 'Fotoğraf')}">
          ${m.is_primary ? `<span style="position:absolute;top:4px;right:4px;background:rgba(217,119,6,0.9);color:#fff;font-size:0.65rem;padding:2px 5px;border-radius:4px;font-weight:700;">★ ${state.lang === 'tr' ? 'Profil' : 'Primary'}</span>` : ''}
        </div>
        <div class="media-card-info">
          <div class="media-card-title">${escapeHtml(m.title || (state.lang === 'tr' ? 'İsimsiz Fotoğraf' : 'Untitled'))}</div>
          ${m.date_taken ? `<div class="media-card-year">📅 ${escapeHtml(m.date_taken)}</div>` : ''}
        </div>
      </div>
    `).join("");

    galleryEl.querySelectorAll(".media-card-item").forEach((card) => {
      card.addEventListener("click", () => {
        const item = list.find((m) => m.id === card.dataset.mediaId);
        if (item) openPhotoLightboxModal(item, pid);
      });
    });
  } catch (_) {
    const galleryEl = document.getElementById("media-modal-gallery");
    if (galleryEl) galleryEl.innerHTML = `<div style="grid-column:1/-1;text-align:center;padding:1.5rem;color:var(--text-muted);">Çevrimdışı / Veri alınamadı</div>`;
  }
}

function openAddPhotoModal(pid) {
  const p = state.data?.individuals[pid];
  if (!p) return;

  const modalHtml = `
    <form id="add-photo-form" style="display:flex;flex-direction:column;gap:1rem;">
      <p class="section-subtitle">
        ${state.lang === "tr" ? `${p.name} için fotoğraf veya tarihi belge ekleyin. Fotoğraflar otomatik olarak optimize edilir ve D1 buluta yüklenir.` : `Upload photo or document for ${p.name}.`}
      </p>

      <div class="form-field">
        <label>🖼️ ${state.lang === "tr" ? "Fotoğraf / Belge Seçin" : "Select Image"}</label>
        <input type="file" id="photo-file-input" accept="image/*" class="form-input" required />
      </div>

      <div id="photo-preview-box" style="display:none; text-align:center; padding: 0.5rem; background: var(--bg-primary); border-radius: 10px; border: 1px solid var(--border-subtle);">
        <img id="photo-preview-img" style="max-height: 220px; max-width: 100%; border-radius: 8px; object-fit: contain;" />
        <div id="photo-size-badge" style="font-size: 0.75rem; color: var(--text-muted); margin-top: 0.3rem;"></div>
      </div>

      <div class="form-field">
        <label>🏷️ ${state.lang === "tr" ? "Başlık / Tanım" : "Title"}</label>
        <input type="text" id="photo-title-input" class="form-input" placeholder="Örn: 1965 Niksic Hatırası, Düğün Fotoğrafı..." />
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.75rem;">
        <div class="form-field">
          <label>📅 ${state.lang === "tr" ? "Çekildiği Yıl / Tarih" : "Year / Date"}</label>
          <input type="text" id="photo-year-input" class="form-input" placeholder="Örn: 1968" />
        </div>
        <div class="form-field">
          <label>📂 ${state.lang === "tr" ? "Tür" : "Type"}</label>
          <select id="photo-type-select" class="form-input">
            <option value="photo">📷 Portre / Hatıra Fotoğrafı</option>
            <option value="document">📜 Tarihi Vesika / Belge</option>
            <option value="certificate">🎖️ Nüfus / Kimlik Belgesi</option>
          </select>
        </div>
      </div>

      <div class="form-field">
        <label>📝 ${state.lang === "tr" ? "Açıklama / Arka Not" : "Description / Notes"}</label>
        <textarea id="photo-desc-input" class="form-input" rows="2" placeholder="Fotoğrafın arkasındaki notlar, kimlerle çekildiği veya hikayesi..."></textarea>
      </div>

      <label style="display:flex;align-items:center;gap:0.5rem;font-size:0.85rem;cursor:pointer;">
        <input type="checkbox" id="photo-is-primary-input" checked />
        <span>★ ${state.lang === "tr" ? "Bu fotoğrafı kişinin ana profil fotoğrafı yap" : "Set as primary portrait avatar"}</span>
      </label>

      <div id="photo-upload-error" class="auth-error hidden"></div>

      <div class="modal-actions" style="margin-top:0.5rem;">
        <button type="button" class="mini-btn" id="photo-cancel-btn">${state.lang === "tr" ? "İptal" : "Cancel"}</button>
        <button type="submit" class="primary-btn small-btn" id="photo-submit-btn">${state.lang === "tr" ? "Yükle ve Kaydet" : "Upload & Save"}</button>
      </div>
    </form>
  `;

  openModal(`📸 ${state.lang === "tr" ? "Fotoğraf Yükle" : "Upload Photo"}: ${p.name}`, modalHtml, false);

  let compressedDataUrl = "";
  const fileInput = document.getElementById("photo-file-input");
  const previewBox = document.getElementById("photo-preview-box");
  const previewImg = document.getElementById("photo-preview-img");
  const sizeBadge = document.getElementById("photo-size-badge");
  const errBox = document.getElementById("photo-upload-error");

  fileInput?.addEventListener("change", (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (re) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1200;
        let w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round((h * maxDim) / w); w = maxDim; }
          else { w = Math.round((w * maxDim) / h); h = maxDim; }
        }
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const cx = c.getContext("2d");
        cx.drawImage(img, 0, 0, w, h);
        compressedDataUrl = c.toDataURL("image/jpeg", 0.82);

        previewImg.src = compressedDataUrl;
        previewBox.style.display = "block";
        const kbSize = Math.round((compressedDataUrl.length * 3) / 4 / 1024);
        sizeBadge.textContent = `Optimize edildi: ${w}x${h}px • ~${kbSize} KB`;
      };
      img.src = String(re.target.result);
    };
    reader.readAsDataURL(file);
  });

  document.getElementById("photo-cancel-btn")?.addEventListener("click", closeModal);

  document.getElementById("add-photo-form")?.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!compressedDataUrl) {
      errBox.textContent = "Lütfen bir fotoğraf seçiniz.";
      errBox.classList.remove("hidden");
      return;
    }

    const submitBtn = document.getElementById("photo-submit-btn");
    submitBtn.textContent = "Kaydediliyor...";
    submitBtn.disabled = true;

    try {
      const title = document.getElementById("photo-title-input").value.trim() || `${p.name} Fotoğrafı`;
      const dateTaken = document.getElementById("photo-year-input").value.trim();
      const mediaType = document.getElementById("photo-type-select").value;
      const description = document.getElementById("photo-desc-input").value.trim();
      const isPrimary = document.getElementById("photo-is-primary-input").checked;

      const res = await apiRequest("media", "POST", {
        personId: pid,
        url: compressedDataUrl,
        title,
        dateTaken,
        mediaType,
        description,
        isPrimary,
      });

      if (res && res.ok) {
        if (isPrimary) {
          if (!state.primaryPhotos) state.primaryPhotos = {};
          state.primaryPhotos[pid] = compressedDataUrl;
          p.avatarUrl = compressedDataUrl;
          renderTree(true);
        }
        closeModal();
        openPersonDrawer(pid);
      } else {
        throw new Error(res?.error || "Fotoğraf yüklenemedi");
      }
    } catch (err) {
      errBox.textContent = err.message || "Yükleme hatası oluştu";
      errBox.classList.remove("hidden");
      submitBtn.textContent = "Yükle ve Kaydet";
      submitBtn.disabled = false;
    }
  });
}

function openPhotoLightboxModal(item, pid) {
  const p = state.data?.individuals[pid];
  const isPrimary = Boolean(item.is_primary);

  const modalHtml = `
    <div style="display:flex;flex-direction:column;gap:1rem;align-items:center;">
      <div class="lightbox-preview-wrap">
        <img src="${escapeHtml(item.url)}" alt="${escapeHtml(item.title || 'Fotoğraf')}">
      </div>
      <div style="width:100%;text-align:left;background:var(--bg-primary);padding:0.85rem 1rem;border-radius:10px;border:1px solid var(--border-subtle);">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h4 style="margin:0;font-size:1.05rem;">${escapeHtml(item.title || 'İsimsiz Fotoğraf')}</h4>
          ${item.date_taken ? `<span style="font-size:0.8rem;color:var(--text-muted);font-weight:600;">📅 ${escapeHtml(item.date_taken)}</span>` : ''}
        </div>
        ${item.description ? `<p style="margin:0.5rem 0 0 0;font-size:0.82rem;color:var(--text-secondary);">${escapeHtml(item.description)}</p>` : ''}
      </div>

      <div style="display:flex;gap:0.5rem;width:100%;justify-content:space-between;flex-wrap:wrap;">
        <div style="display:flex;gap:0.5rem;">
          ${!isPrimary ? `<button type="button" class="primary-btn mini-btn" id="lightbox-set-primary-btn" style="background:#d97706;border-color:#d97706;color:#fff;">★ Profil Fotoğrafı Yap</button>` : `<span style="padding:0.35rem 0.6rem;font-size:0.75rem;background:rgba(217,119,6,0.15);color:#d97706;border-radius:6px;font-weight:700;">★ Ana Profil Fotoğrafı</span>`}
          <a href="${escapeHtml(item.url)}" download="${p ? p.name.replace(/\s+/g, '_') : 'Niksic'}_Foto.jpg" class="mini-btn" style="text-decoration:none;display:inline-flex;align-items:center;">📥 İndir</a>
        </div>
        <div style="display:flex;gap:0.5rem;">
          <button type="button" class="mini-btn" id="lightbox-delete-btn" style="color:#ef4444;border-color:rgba(239,68,68,0.4);">🗑️ Sil</button>
          <button type="button" class="mini-btn" id="lightbox-close-btn">✕ Kapat</button>
        </div>
      </div>
    </div>
  `;

  openModal(`📷 ${item.title || 'Fotoğraf Detayı'}`, modalHtml, true);

  document.getElementById("lightbox-close-btn")?.addEventListener("click", closeModal);

  document.getElementById("lightbox-set-primary-btn")?.addEventListener("click", () => {
    requireEditorThen(async () => {
      const res = await apiRequest("media/set-primary", "POST", { mediaId: item.id, personId: pid });
      if (res && res.ok) {
        if (!state.primaryPhotos) state.primaryPhotos = {};
        state.primaryPhotos[pid] = item.url;
        if (p) p.avatarUrl = item.url;
        renderTree(true);
        closeModal();
        openPersonDrawer(pid);
      }
    });
  });

  document.getElementById("lightbox-delete-btn")?.addEventListener("click", () => {
    requireEditorThen(async () => {
      if (!confirm("Bu fotoğrafı silmek istediğinizden emin misiniz?")) return;
      const res = await apiRequest("media", "DELETE", { mediaId: item.id });
      if (res && res.ok) {
        if (state.primaryPhotos && state.primaryPhotos[pid] === item.url) {
          delete state.primaryPhotos[pid];
          if (p) p.avatarUrl = null;
          renderTree(true);
        }
        closeModal();
        openPersonDrawer(pid);
      }
    });
  });
}

function requireEditorThen(callback) {
  if (state.role === "editor" || state.role === "admin") {
    callback();
    return;
  }
  openModal(
    "✍️ Editör / Yönetici Girişi",
    `
    <p class="section-subtitle">
      Soy ağacına yeni kişi eklemek veya mevcut bilgileri düzenlemek için aile editör veya yönetici parolasını giriniz.
      Yapılan tüm değişiklikler anında Cloudflare D1 bulut veritabanına kaydedilir.
    </p>
    <form id="unlock-editor-form" class="modal-body" style="padding:0;">
      <div class="form-field">
        <label>Adınız (Değişiklik geçmişinde görünür)</label>
        <input type="text" id="editor-name-input" value="${escapeHtml(state.editorName)}" required />
      </div>
      <div class="form-field">
        <label>Editör / Yönetici Parolası</label>
        <input type="password" id="editor-pass-input" placeholder="Parola" required autofocus />
      </div>
      <div id="editor-unlock-err" class="auth-error hidden">Geçersiz parola. Lütfen tekrar deneyiniz.</div>
      <div class="modal-actions">
        <button type="button" class="mini-btn" id="cancel-modal-btn">İptal</button>
        <button type="submit" class="primary-btn small-btn">Giriş Yap</button>
      </div>
    </form>
  `
  );

  document.getElementById("cancel-modal-btn").addEventListener("click", closeModal);
  document.getElementById("unlock-editor-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const rawPass = (document.getElementById("editor-pass-input") as HTMLInputElement).value;
    const norm = normalizePasscode(rawPass);
    const edName = (document.getElementById("editor-name-input") as HTMLInputElement).value.trim() || "Aile Editörü";
    const isAdmin = ADMIN_CODES.has(norm);
    const isEditor = EDITOR_CODES.has(norm);

    if (isAdmin || isEditor) {
      state.editorName = edName;
      state.role = isAdmin ? "admin" : "editor";
      localStorage.setItem(STORAGE_KEY_EDITOR_NAME, edName);
      localStorage.setItem(STORAGE_KEY_SESSION_ROLE, state.role);
      sessionStorage.setItem(STORAGE_KEY_SESSION_ROLE, state.role);

      // Obtain/upgrade session token with editor/admin privileges
      try {
        const res = await fetch("/api/auth-check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ passcode: norm, deviceName: getDeviceSummary() }),
        });
        if (res.ok) {
          const d = await res.json();
          if (d && d.sessionToken) {
            state.sessionToken = d.sessionToken;
            localStorage.setItem(STORAGE_KEY_SESSION_TOKEN, d.sessionToken);
            sessionStorage.setItem(STORAGE_KEY_SESSION_TOKEN, d.sessionToken);
            if (d.role) state.role = d.role;
          }
        }
      } catch (_) {}

      updateEditorBadge();
      closeModal();
      callback();
    } else {
      document.getElementById("editor-unlock-err").classList.remove("hidden");
    }
  });
}

function requireAdminThen(callback) {
  if (state.role === "admin") {
    callback();
    return;
  }
  openModal(
    "🛡️ Yönetici (Admin) Girişi",
    `
    <p class="section-subtitle">
      Arşiv belgelerini yüklemek, düzenlemek veya silmek için yalnızca <strong>Yönetici (Admin)</strong> yetkisi gerekmektedir. Editör parolası bu işlem için geçerli değildir.
    </p>
    <form id="unlock-admin-form" class="modal-body" style="padding:0;">
      <div class="form-field">
        <label>Adınız (Değişiklik geçmişinde görünür)</label>
        <input type="text" id="admin-name-input" value="${escapeHtml(state.editorName)}" required />
      </div>
      <div class="form-field">
        <label>Yönetici (Admin) Parolası</label>
        <input type="password" id="admin-pass-input" placeholder="Admin Parolası" required autofocus />
      </div>
      <div id="admin-unlock-err" class="auth-error hidden">Geçersiz yönetici parolası. Lütfen admin şifresini giriniz.</div>
      <div class="modal-actions">
        <button type="button" class="mini-btn" id="cancel-admin-modal-btn">İptal</button>
        <button type="submit" class="primary-btn small-btn">Yönetici Modunu Aç</button>
      </div>
    </form>
  `
  );

  document.getElementById("cancel-admin-modal-btn")?.addEventListener("click", closeModal);
  document.getElementById("unlock-admin-form")?.addEventListener("submit", async (e: any) => {
    e.preventDefault();
    const rawPass = (document.getElementById("admin-pass-input") as HTMLInputElement).value;
    const norm = normalizePasscode(rawPass);
    const edName = (document.getElementById("admin-name-input") as HTMLInputElement).value.trim() || "Sistem Yöneticisi";

    if (ADMIN_CODES.has(norm)) {
      state.editorName = edName;
      state.role = "admin";
      localStorage.setItem(STORAGE_KEY_EDITOR_NAME, edName);
      localStorage.setItem(STORAGE_KEY_SESSION_ROLE, "admin");
      sessionStorage.setItem(STORAGE_KEY_SESSION_ROLE, "admin");

      try {
        const res = await fetch("/api/auth-check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ passcode: norm, deviceName: getDeviceSummary() }),
        });
        if (res.ok) {
          const d = await res.json();
          if (d && d.sessionToken) {
            state.sessionToken = d.sessionToken;
            localStorage.setItem(STORAGE_KEY_SESSION_TOKEN, d.sessionToken);
            sessionStorage.setItem(STORAGE_KEY_SESSION_TOKEN, d.sessionToken);
            if (d.role) state.role = d.role;
          }
        }
      } catch (_) {}

      updateEditorBadge();
      closeModal();
      callback();
    } else {
      document.getElementById("admin-unlock-err")?.classList.remove("hidden");
    }
  });
}

function openEditPersonModal(pid = null) {
  const isEdit = Boolean(pid && state.data.individuals[pid]);
  const p = isEdit
    ? state.data.individuals[pid]
    : {
        id: "",
        name: "",
        given: "",
        surname: "",
        birthSurname: "",
        nickname: "",
        sex: "M",
        birthYear: "",
        birthPlace: "",
        deathYear: "",
        deathPlace: "",
        deathCause: "",
        deceased: false,
        occupation: "",
        notes: "",
        tags: "",
        fams: [],
        famc: [],
      };

  const candidateFamilies = isEdit ? getCandidateParentFamilies(pid) : [];
  let parentFamilySectionHtml = "";

  if (isEdit && candidateFamilies.length > 0) {
    const hasMultiple = candidateFamilies.length > 1;
    const optionsHtml = candidateFamilies
      .map(
        (c) =>
          `<option value="${c.familyId}" ${c.isCurrent ? "selected" : ""}>${escapeHtml(c.label)}</option>`
      )
      .join("");

    parentFamilySectionHtml = `
      <div class="form-field" style="background:var(--accent-soft);padding:0.75rem 0.85rem;border-radius:9px;border:1.5px solid rgba(212,175,55,0.35);margin-top:0.2rem;">
        <label style="display:flex;align-items:center;gap:0.4rem;font-weight:700;color:var(--text-primary);margin-bottom:0.35rem;">
          <span>🌸</span> ${state.lang === 'tr' ? 'Bağlı Olduğu Evlilik / Anne-Baba Kolu' : 'Parental Marriage / Mother Branch'}
        </label>
        ${
          hasMultiple
            ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-bottom:0.5rem;line-height:1.35;">
                 ✨ <em>${state.lang === 'tr' ? 'Bu kişinin ebeveynlerinin birden fazla evliliği tespit edildi. Çocuğun hangi anneden/evlilikten olduğunu aşağıdan kolayca seçip değiştirebilirsiniz:' : 'Multiple marriages detected for parents. You can assign or switch this child to any branch below:'}</em>
               </div>`
            : ""
        }
        <select name="parentFamilyId" class="form-select" style="width:100%;font-size:0.86rem;padding:0.45rem 0.6rem;background:var(--bg-surface);border:1.5px solid var(--border-strong);border-radius:7px;color:var(--text-primary);">
          ${optionsHtml}
          <option value="">${state.lang === 'tr' ? '🚫 (Ebeveyn Bağlantısını Kaldır / Bağımsız Yap)' : '🚫 (Unlink Parent Family)'}</option>
        </select>
        <div style="font-size:0.72rem;color:var(--text-muted);margin-top:0.35rem;">
          ${state.lang === 'tr' ? 'Kaydettiğinizde soy ağacı, zaman çizelgesi ve yelpaze grafiği yeni seçtiğiniz kola göre anında güncellenir.' : 'When saved, tree and fan chart will immediately update to the selected branch.'}
        </div>
      </div>
    `;
  }

  openModal(
    isEdit ? `✎ Kişi Bilgilerini Düzenle: ${p.name}` : "＋ Yeni Aile Üyesi Oluştur",
    `
    <form id="person-crud-form" style="display:flex;flex-direction:column;gap:0.8rem;">
      <div class="form-grid-2">
        <div class="form-field">
          <label>Ad (Given Name) *</label>
          <input type="text" name="given" value="${escapeHtml(p.given)}" required />
        </div>
        <div class="form-field">
          <label>Soyadı (Surname)</label>
          <input type="text" name="surname" value="${escapeHtml(p.surname)}" />
        </div>
      </div>

      <div class="form-grid-2">
        <div class="form-field">
          <label>Kızlık / Doğum Soyadı</label>
          <input type="text" name="birthSurname" value="${escapeHtml(p.birthSurname || "")}" />
        </div>
        <div class="form-field">
          <label>Lakap / Ön Ad (örn. Hacı, Molla)</label>
          <input type="text" name="nickname" value="${escapeHtml(p.nickname || "")}" />
        </div>
      </div>

      <div class="form-grid-2">
        <div class="form-field">
          <label>Cinsiyet</label>
          <select name="sex">
            <option value="M" ${p.sex === "M" ? "selected" : ""}>Erkek</option>
            <option value="F" ${p.sex === "F" ? "selected" : ""}>Kadın</option>
          </select>
        </div>
        <div class="form-field">
          <label>Hayat Durumu</label>
          <select name="deceased" id="crud-deceased-select">
            <option value="0" ${!p.deceased ? "selected" : ""}>Hayatta</option>
            <option value="1" ${p.deceased ? "selected" : ""}>Vefat Etmiş</option>
          </select>
        </div>
      </div>

      <div class="form-grid-2">
        <div class="form-field">
          <label>Doğum Yılı / Tarihi (örn. 1977)</label>
          <input type="text" name="birthYear" value="${escapeHtml(p.birthYear || "")}" />
        </div>
        <div class="form-field">
          <label>Doğum Yeri (örn. Nikšić, İstanbul)</label>
          <input type="text" name="birthPlace" value="${escapeHtml(p.birthPlace || "")}" />
        </div>
      </div>

      <div class="form-grid-2">
        <div class="form-field">
          <label>Vefat Yılı / Tarihi</label>
          <input type="text" name="deathYear" value="${escapeHtml(p.deathYear || "")}" />
        </div>
        <div class="form-field">
          <label>Vefat Yeri</label>
          <input type="text" name="deathPlace" value="${escapeHtml(p.deathPlace || "")}" />
        </div>
      </div>

      <div class="form-grid-2">
        <div class="form-field">
          <label>Meslek / Unvan</label>
          <input type="text" name="occupation" value="${escapeHtml(p.occupation || "")}" />
        </div>
        <div class="form-field">
          <label>Etiketler</label>
          <input type="text" name="tags" value="${escapeHtml(p.tags || "")}" />
        </div>
      </div>

      ${parentFamilySectionHtml}

      <div class="form-field">
        <label>Notlar ve Biyografi Hikayesi</label>
        <textarea name="notes" placeholder="Aile hikayesi, göç bilgileri veya notlar...">${escapeHtml(p.notes || "")}</textarea>
      </div>

      <div class="modal-actions" style="justify-content:space-between;">
        ${
          isEdit
            ? `<button type="button" class="danger-btn" id="delete-person-btn">🗑️ Kişiyi Sil</button>`
            : `<span></span>`
        }
        <div style="display:flex;gap:0.5rem;">
          <button type="button" class="mini-btn" id="cancel-crud-btn">İptal</button>
          <button type="submit" class="primary-btn small-btn" id="save-crud-btn">💾 Buluta Kaydet</button>
        </div>
      </div>
    </form>
  `
  );

  document.getElementById("cancel-crud-btn").addEventListener("click", closeModal);

  if (isEdit) {
    document.getElementById("delete-person-btn").addEventListener("click", async () => {
      if (!confirm(`${p.name} kaydını silmek istediğinize emin misiniz? (Geçmişten geri alınabilir)`)) return;
      const res = await apiRequest(`person/${pid}`, "DELETE");
      if (res.ok) {
        closeModal();
        closePersonDrawer();
        if (state.focusedId === pid) state.focusedId = "I227";
        if (res.data && res.data.individuals) {
          prepareDataset(res.data);
        }
        refreshActiveViews(true);
        showSyncToast("✓ Kişi kaydı silindi ve tüm listeler eşitlendi");
      }
    });
  }

  document.getElementById("person-crud-form").addEventListener("submit", async (e: any) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const getS = (k: string) => String(fd.get(k) || "").trim();
    const payload: any = {
      given: getS("given").toUpperCase(),
      surname: getS("surname").toUpperCase(),
      birthSurname: getS("birthSurname").toUpperCase(),
      nickname: getS("nickname"),
      sex: fd.get("sex"),
      deceased: fd.get("deceased") === "1" || Boolean(getS("deathYear")),
      birthYear: getS("birthYear"),
      birthPlace: getS("birthPlace"),
      deathYear: getS("deathYear"),
      deathPlace: getS("deathPlace"),
      occupation: getS("occupation"),
      tags: getS("tags"),
      notes: getS("notes"),
    };

    if (isEdit && fd.has("parentFamilyId")) {
      payload.parentFamilyId = fd.get("parentFamilyId");
    }

    const res = isEdit
      ? await apiRequest(`person/${pid}`, "PUT", payload)
      : await apiRequest("person", "POST", payload);

    if (res.ok) {
      closeModal();
      if (res.data && res.data.individuals) {
        prepareDataset(res.data);
      }
      refreshActiveViews(false);
      const targetId = isEdit ? pid : res.data.newId;
      if (targetId) {
        focusPersonInTree(targetId);
        openPersonDrawer(targetId);
      }
      showSyncToast("✓ Kişi bilgisi kaydedildi ve tüm listeler güncellendi");
    }
  });
}

function openAddRelativeModal(anchorId) {
  const anchor = state.data.individuals[anchorId];
  if (!anchor) return;
  const unions = getSpousesAndChildren(anchorId);

  const unionOptionsHtml =
    unions.length > 0
      ? unions
          .map(
            (u) =>
              `<option value="${u.familyId}">Eş: ${escapeHtml(u.spouse ? u.spouse.name : "Tek Ebeveyn")}</option>`
          )
          .join("")
      : `<option value="">Yeni Aile Bağı Oluştur</option>`;

  openModal(
    `＋ Akraba Ekle / Bağla: ${anchor.name}`,
    `
    <div style="display:flex;gap:0.5rem;margin-bottom:0.5rem;">
      <button type="button" class="chip-btn active" id="tab-new-rel-btn" style="flex:1;padding:0.5rem;">Yeni Kişi Oluştur</button>
      <button type="button" class="chip-btn" id="tab-existing-rel-btn" style="flex:1;padding:0.5rem;">Ağaçtan Mevcut Kişiyi Bağla</button>
    </div>

    <form id="add-relative-form" style="display:flex;flex-direction:column;gap:0.8rem;">
      <input type="hidden" name="mode" id="rel-mode-input" value="new" />

      <div class="form-grid-2">
        <div class="form-field">
          <label>${escapeHtml(anchor.given || anchor.name)} Kişisinin Neyi Oluyor? *</label>
          <select name="relType" id="rel-type-select">
            <option value="child" selected>Çocuğu (Oğlu / Kızı)</option>
            <option value="spouse">Eşi (Evlilik)</option>
            <option value="father">Babası</option>
            <option value="mother">Annesi</option>
            <option value="sibling">Kardeşi</option>
          </select>
        </div>

        <div class="form-field" id="child-union-field">
          <label>Hangi Evlilikten?</label>
          <select name="familyId">
            ${unionOptionsHtml}
          </select>
        </div>

        <div class="form-field hidden" id="spouse-status-field">
          <label>Evlilik Durumu ve Yılı</label>
          <div style="display:flex;gap:0.4rem;">
            <select name="marriageStatus" style="flex:1;">
              <option value="current">Evli</option>
              <option value="divorced">Boşanmış</option>
            </select>
            <input type="text" name="marriageYear" placeholder="Yıl" style="width:80px;" />
          </div>
        </div>
      </div>

      <!-- Mode 1: Create New Relative Fields -->
      <div id="new-rel-fields" style="display:flex;flex-direction:column;gap:0.75rem;">
        <div class="form-grid-2">
          <div class="form-field">
            <label>Ad (Given Name) *</label>
            <input type="text" name="given" id="new-rel-given" required />
          </div>
          <div class="form-field">
            <label>Soyadı (Surname)</label>
            <input type="text" name="surname" value="${escapeHtml(anchor.surname || "")}" />
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label>Cinsiyet</label>
            <select name="sex" id="new-rel-sex">
              <option value="M">Erkek</option>
              <option value="F">Kadın</option>
            </select>
          </div>
          <div class="form-field">
            <label>Doğum Yılı (örn. 1995)</label>
            <input type="text" name="birthYear" />
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label>Durum</label>
            <select name="deceased">
              <option value="0">Hayatta</option>
              <option value="1">Vefat Etmiş</option>
            </select>
          </div>
          <div class="form-field">
            <label>Vefat Yılı (varsa)</label>
            <input type="text" name="deathYear" />
          </div>
        </div>
      </div>

      <!-- Mode 2: Connect Existing Person Fields -->
      <div id="existing-rel-fields" class="hidden" style="display:flex;flex-direction:column;gap:0.65rem;">
        <div class="form-field">
          <label>Ağaçtaki Kişiyi Arayıp Seçin *</label>
          <input type="text" id="existing-rel-search" placeholder="İsim veya soyadı yazın..." autocomplete="off" />
          <input type="hidden" name="targetId" id="existing-rel-target-id" />
          <div id="existing-rel-results" class="autocomplete-list hidden" style="position:static;max-height:180px;margin-top:4px;"></div>
          <div id="existing-rel-picked" class="selected-person-pill" style="margin-top:4px;">Henüz kişi seçilmedi</div>
        </div>
      </div>

      <div class="modal-actions">
        <button type="button" class="mini-btn" id="cancel-rel-btn">İptal</button>
        <button type="submit" class="primary-btn small-btn">💾 Ekle ve Bağla</button>
      </div>
    </form>
  `
  );

  const relTypeSelect = document.getElementById("rel-type-select");
  const sexSelect = document.getElementById("new-rel-sex");
  relTypeSelect.addEventListener("change", () => {
    const val = relTypeSelect.value;
    document.getElementById("child-union-field").classList.toggle("hidden", val !== "child");
    document.getElementById("spouse-status-field").classList.toggle("hidden", val !== "spouse");
    if (val === "father") sexSelect.value = "M";
    if (val === "mother") sexSelect.value = "F";
    if (val === "spouse") sexSelect.value = anchor.sex === "M" ? "F" : "M";
  });

  // Tab switching between New vs Existing
  const tabNew = document.getElementById("tab-new-rel-btn");
  const tabEx = document.getElementById("tab-existing-rel-btn");
  const modeInp = document.getElementById("rel-mode-input");
  const newFields = document.getElementById("new-rel-fields");
  const exFields = document.getElementById("existing-rel-fields");
  const givenInp = document.getElementById("new-rel-given");

  tabNew.addEventListener("click", () => {
    modeInp.value = "new";
    tabNew.classList.add("active");
    tabEx.classList.remove("active");
    newFields.classList.remove("hidden");
    exFields.classList.add("hidden");
    givenInp.required = true;
  });

  tabEx.addEventListener("click", () => {
    modeInp.value = "existing";
    tabEx.classList.add("active");
    tabNew.classList.remove("active");
    exFields.classList.remove("hidden");
    newFields.classList.add("hidden");
    givenInp.required = false;
  });

  // Autocomplete for existing person
  const exSearch = document.getElementById("existing-rel-search");
  const exResults = document.getElementById("existing-rel-results");
  exSearch.addEventListener("input", () => {
    const rawQ = exSearch.value.trim();
    const q = normalizeText(rawQ);
    const matches = searchPersons(state.personList, rawQ, { excludeId: anchorId, limit: 10 });
    if (matches.length === 0 && !rawQ) {
      exResults.classList.add("hidden");
      return;
    }

    exResults.innerHTML = matches
      .map((p) => {
        const isDeceased = Boolean(p.deceased);
        return `
        <div class="search-result-item sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" data-person-id="${p.id}">
          <span class="sr-avatar sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" aria-hidden="true">${renderAvatarSilhouette(p.sex, isDeceased)}</span>
          <div class="sr-main">
            <span class="sr-name">${highlightMatch(p.name, q)}</span>
            <span class="sr-sub">${escapeHtml(formatLifeYears(p))}</span>
          </div>
          <div class="sr-badges">
            <span class="sr-gen-pill">${p.generation + 1}. Kuşak</span>
          </div>
        </div>
      `;
      })
      .join("");
    exResults.classList.remove("hidden");
    exResults.querySelectorAll(".search-result-item").forEach((item) => {
      item.addEventListener("click", () => {
        const picked = state.data.individuals[item.dataset.personId];
        document.getElementById("existing-rel-target-id").value = picked.id;
        document.getElementById("existing-rel-picked").textContent = `${picked.name} (${formatLifeYears(picked)})`;
        exResults.classList.add("hidden");
      });
    });
  });

  document.getElementById("cancel-rel-btn").addEventListener("click", closeModal);

  document.getElementById("add-relative-form").addEventListener("submit", async (e: any) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const getS = (k: string) => String(fd.get(k) || "").trim();
    const mode = fd.get("mode");
    const relType = fd.get("relType");

    if (mode === "existing") {
      const targetId = fd.get("targetId");
      if (!targetId) return alert("Lütfen bağlanacak kişiyi seçiniz.");
      const res = await apiRequest("relationship", "POST", {
        anchorId,
        targetId,
        relType,
        familyId: fd.get("familyId") || "",
        marriageStatus: fd.get("marriageStatus") || "current",
        marriageYear: fd.get("marriageYear") || "",
      });
      if (res.ok) {
        closeModal();
        if (res.data && res.data.individuals) {
          prepareDataset(res.data);
        }
        refreshActiveViews(false);
        openPersonDrawer(anchorId);
        showSyncToast("✓ Akrabalık bağı eklendi ve veriler eşitlendi");
      }
    } else {
      const res = await apiRequest("person", "POST", {
        anchorId,
        relType,
        familyId: fd.get("familyId") || "",
        marriageStatus: fd.get("marriageStatus") || "current",
        marriageYear: fd.get("marriageYear") || "",
        given: getS("given").toUpperCase(),
        surname: getS("surname").toUpperCase(),
        sex: fd.get("sex"),
        birthYear: getS("birthYear"),
        deathYear: getS("deathYear"),
        deceased: fd.get("deceased") === "1" || Boolean(getS("deathYear")),
      });
      if (res.ok) {
        closeModal();
        if (res.data && res.data.individuals) {
          prepareDataset(res.data);
        }
        refreshActiveViews(false);
        openPersonDrawer(anchorId);
        showSyncToast("✓ Yeni akraba eklendi ve tüm listeler güncellendi");
      }
    }
  });
}

function openEditMarriageModal(familyId) {
  const fam = state.data.families[familyId];
  if (!fam) return;
  const h = fam.husb ? state.data.individuals[fam.husb] : null;
  const w = fam.wife ? state.data.individuals[fam.wife] : null;

  let wifeBirthSurname = w ? (w.birthSurname || "").trim() : "";
  if (!wifeBirthSurname && w && w.famc && w.famc.length > 0) {
    const parentFam = state.data.families[w.famc[0]];
    if (parentFam && parentFam.husb && state.data.individuals[parentFam.husb]) {
      wifeBirthSurname = (state.data.individuals[parentFam.husb].surname || "").trim();
    }
  }

  const isInitiallyDivorced = fam.status === "divorced";
  const wifeCurrentSurname = w ? (w.surname || "") : "";
  const suggestedMaidenSurname = wifeBirthSurname || "";

  const wifeSurnameSectionHtml = w
    ? `
      <div id="marr-divorce-surname-box" style="display:${isInitiallyDivorced ? 'flex' : 'none'}; flex-direction:column; gap:0.5rem; background:var(--accent-soft); padding:0.75rem 0.85rem; border-radius:8px; border:1px solid rgba(212,175,55,0.35);">
        <label style="font-weight:700; color:var(--text-primary); font-size:0.86rem; display:flex; align-items:center; gap:0.4rem;">
          <span>🌸</span> ${state.lang === 'tr' ? `Boşanma Sonrası Hanımın (${escapeHtml(w.given)}) Soyadı:` : `Post-Divorce Surname for ${escapeHtml(w.given)}:`}
        </label>
        <div style="font-size:0.76rem; color:var(--text-secondary); line-height:1.35;">
          ${state.lang === 'tr' ? 'Seçtiğiniz soyadı soy ağacında, kişi rehberinde ve soyadı istatistiklerinde anında güncellenir.' : 'This surname will update across tree, directory, and statistics.'}
        </div>
        <div style="display:flex; flex-direction:column; gap:0.4rem; margin-top:0.15rem;">
          ${suggestedMaidenSurname ? `
            <label style="font-size:0.85rem; display:flex; align-items:center; gap:0.45rem; cursor:pointer;">
              <input type="radio" name="marrWifeSurnameChoice" value="birth" ${wifeCurrentSurname !== h?.surname || wifeCurrentSurname === suggestedMaidenSurname ? 'checked' : 'checked'} />
              <span>${state.lang === 'tr' ? 'Kızlık / Doğum Soyadına Döndür:' : 'Revert to Maiden Surname:'} <strong>${escapeHtml(suggestedMaidenSurname)}</strong></span>
            </label>
          ` : ''}
          <label style="font-size:0.85rem; display:flex; align-items:center; gap:0.45rem; cursor:pointer;">
            <input type="radio" name="marrWifeSurnameChoice" value="keep" ${!suggestedMaidenSurname ? 'checked' : ''} />
            <span>${state.lang === 'tr' ? 'Mevcut Soyadını Koru:' : 'Keep Current Surname:'} <strong>${escapeHtml(wifeCurrentSurname || '—')}</strong></span>
          </label>
          <label style="font-size:0.85rem; display:flex; align-items:center; gap:0.45rem; cursor:pointer;">
            <input type="radio" name="marrWifeSurnameChoice" value="custom" />
            <span>${state.lang === 'tr' ? 'Farklı Bir Soyadı Yaz:' : 'Specify Different Surname:'}</span>
          </label>
          <input type="text" id="marr-custom-surname-input" class="form-input" style="padding:0.35rem 0.6rem; font-size:0.85rem; display:none;" placeholder="${state.lang === 'tr' ? 'Yeni soyadı yazın (örn: ' + (suggestedMaidenSurname || 'HOCİÇ') + ')' : 'Enter new surname'}" />
        </div>
      </div>
    `
    : "";

  openModal(
    `⚭ Evlilik Bilgisi Düzenle`,
    `
    <p class="section-subtitle">
      <strong>${escapeHtml(h ? h.name : "—")}</strong> &amp; <strong>${escapeHtml(w ? w.name : "—")}</strong>
    </p>
    <form id="edit-marriage-form" style="display:flex;flex-direction:column;gap:0.8rem;">
      <div class="form-grid-2">
        <div class="form-field">
          <label>Evlilik Durumu</label>
          <select name="status" id="marr-status-select">
            <option value="current" ${fam.status !== "divorced" ? "selected" : ""}>Evli (Current)</option>
            <option value="divorced" ${fam.status === "divorced" ? "selected" : ""}>Boşanmış (Divorced)</option>
          </select>
        </div>
        <div class="form-field">
          <label>Evlilik Yılı / Tarihi</label>
          <input type="text" name="marriageYear" value="${escapeHtml(fam.marriageYear || "")}" />
        </div>
      </div>
      <div class="form-grid-2">
        <div class="form-field">
          <label>Evlilik Yeri</label>
          <input type="text" name="marriagePlace" value="${escapeHtml(fam.marriagePlace || "")}" />
        </div>
        <div class="form-field">
          <label>Boşanma Yılı (varsa)</label>
          <input type="text" name="divorceYear" value="${escapeHtml(fam.divorceYear || "")}" />
        </div>
      </div>

      ${wifeSurnameSectionHtml}

      <div class="modal-actions" style="justify-content:space-between;">
        <button type="button" class="danger-btn" id="delete-marriage-btn">Evlilik Bağını Kaldır</button>
        <div style="display:flex;gap:0.5rem;">
          <button type="button" class="mini-btn" id="cancel-marr-btn">İptal</button>
          <button type="submit" id="save-marriage-btn" class="primary-btn small-btn">💾 Kaydet</button>
        </div>
      </div>
    </form>
  `
  );

  const statusSelect = document.getElementById("marr-status-select");
  const divorceSurnameBox = document.getElementById("marr-divorce-surname-box");
  const customSurnameInput = document.getElementById("marr-custom-surname-input");

  statusSelect?.addEventListener("change", (e: any) => {
    if (divorceSurnameBox) {
      divorceSurnameBox.style.display = e.target.value === "divorced" ? "flex" : "none";
    }
  });

  document.querySelectorAll("input[name='marrWifeSurnameChoice']").forEach((radio) => {
    radio.addEventListener("change", (e: any) => {
      if (customSurnameInput) {
        customSurnameInput.style.display = e.target.value === "custom" ? "block" : "none";
        if (e.target.value === "custom") customSurnameInput.focus();
      }
    });
  });

  document.getElementById("cancel-marr-btn").addEventListener("click", closeModal);
  document.getElementById("delete-marriage-btn").addEventListener("click", async () => {
    if (state.role !== "editor") {
      requireEditorThen(() => openEditMarriageModal(familyId));
      return;
    }
    if (!confirm("Bu evlilik bağını kaldırmak istediğinize emin misiniz?")) return;
    const res = await apiRequest("relationship", "POST", { operation: "delete_family", familyId });
    if (res.ok) {
      closeModal();
      if (res.data && res.data.individuals) {
        prepareDataset(res.data);
      }
      refreshActiveViews(false);
      showSyncToast("✓ Evlilik bağı kaldırıldı ve tüm listeler eşitlendi");
    } else {
      alert(state.lang === 'tr' ? `Hata: ${res.error || 'İşlem gerçekleştirilemedi'}` : `Error: ${res.error || 'Action failed'}`);
    }
  });

  document.getElementById("edit-marriage-form").addEventListener("submit", async (e: any) => {
    e.preventDefault();
    if (state.role !== "editor") {
      requireEditorThen(() => openEditMarriageModal(familyId));
      return;
    }

    const submitBtn = document.getElementById("save-marriage-btn") as HTMLButtonElement;
    const origBtnHtml = submitBtn ? submitBtn.innerHTML : "💾 Kaydet";
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = `⏳ ${state.lang === 'tr' ? 'Kaydediliyor...' : 'Saving...'}`;
    }

    try {
      const fd = new FormData(e.currentTarget as HTMLFormElement);
      const getS = (k: string) => String(fd.get(k) || "").trim();
      const statusVal = fd.get("status");

      let wifeSurname = null;
      let wifeBirthSurname = suggestedMaidenSurname;
      if (w && statusVal === "divorced") {
        const choice = fd.get("marrWifeSurnameChoice");
        if (choice === "birth" && suggestedMaidenSurname) {
          wifeSurname = suggestedMaidenSurname;
        } else if (choice === "custom") {
          const cust = ((document.getElementById("marr-custom-surname-input") as HTMLInputElement)?.value || "").trim().toUpperCase();
          if (cust) wifeSurname = cust;
        } else if (choice === "keep") {
          wifeSurname = w.surname;
        }
      }

      const payload: any = {
        operation: "update_family",
        familyId,
        status: statusVal,
        marriageYear: getS("marriageYear"),
        marriagePlace: getS("marriagePlace"),
        divorceYear: getS("divorceYear"),
      };
      if (wifeSurname) {
        payload.wifeSurname = wifeSurname;
        if (wifeBirthSurname) payload.wifeBirthSurname = wifeBirthSurname;
      }

      const res = await apiRequest("relationship", "POST", payload);
      if (res.ok) {
        closeModal();
        if (res.data && res.data.individuals) {
          prepareDataset(res.data);
        }
        refreshActiveViews(false);
        if (state.selectedDrawerId) openPersonDrawer(state.selectedDrawerId);
        showSyncToast("✓ Evlilik ve soyadı bilgisi başarıyla güncellendi");
      } else {
        alert(state.lang === 'tr'
          ? `Kayıt başarısız oldu: ${res.error || 'Yetki veya bağlantı hatası'}`
          : `Failed to save: ${res.error || 'Authorization or network error'}`);
        if (res.error && (res.error.includes("Editor") || res.error.includes("403") || res.error.includes("401"))) {
          requireEditorThen(() => openEditMarriageModal(familyId));
        }
      }
    } catch (err: any) {
      alert(`Hata: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = origBtnHtml;
      }
    }
  });
}

function openMergeModal(defaultSourceId = null, defaultTargetId = null) {
  openModal(
    "🔀 Çift Kayıtları Birleştir (Merge Duplicate People)",
    `
    <p class="section-subtitle">
      Ağaca yanlışlıkla iki kez eklenmiş bir kişinin tüm aile bağlarını (ebeveyn, eş, çocuk) ana kayda aktarır.
    </p>
    <form id="merge-people-form" style="display:flex;flex-direction:column;gap:0.8rem;">
      <div class="form-field">
        <label>Silinecek / Aktarılacak Kişi ID veya İsim Ara</label>
        <select name="sourceId" required>
          ${state.personList
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name, "tr"))
            .map((p) => `<option value="${p.id}" ${p.id === defaultSourceId ? "selected" : ""}>${escapeHtml(p.name)} (${escapeHtml(formatLifeYears(p))}) [${p.id}]</option>`)
            .join("")}
        </select>
      </div>
      <div class="form-field">
        <label>Korunacak Ana Kişi</label>
        <select name="targetId" required>
          ${state.personList
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name, "tr"))
            .map((p) => `<option value="${p.id}" ${p.id === defaultTargetId ? "selected" : ""}>${escapeHtml(p.name)} (${escapeHtml(formatLifeYears(p))}) [${p.id}]</option>`)
            .join("")}
        </select>
      </div>
      <div class="modal-actions">
        <button type="button" class="mini-btn" id="cancel-merge-btn">İptal</button>
        <button type="submit" class="primary-btn small-btn">🔀 Kayıtları Birleştir</button>
      </div>
    </form>
  `
  );
  document.getElementById("cancel-merge-btn").addEventListener("click", closeModal);
  document.getElementById("merge-people-form").addEventListener("submit", async (e: any) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const sourceId = fd.get("sourceId");
    const targetId = fd.get("targetId");
    if (sourceId === targetId) return alert("Lütfen iki farklı kayıt seçiniz.");
    const res = await apiRequest("merge", "POST", { sourceId, targetId });
    if (res.ok) {
      closeModal();
      if (res.data && res.data.individuals) prepareDataset(res.data);
      refreshActiveViews(false);
      showSyncToast("✓ Kayıtlar birleştirildi ve listeler eşitlendi");
    }
  });
}

function openFindReplaceModal() {
  openModal(
    "🔍 Toplu Bul ve Değiştir (Find & Replace)",
    `
    <p class="section-subtitle">
      Soyadı, isim, doğum yeri veya meslek alanlarında toplu düzeltme yapın.
    </p>
    <form id="find-replace-form" style="display:flex;flex-direction:column;gap:0.8rem;">
      <div class="form-field">
        <label>Alan (Field)</label>
        <select name="field">
          <option value="surname">Soyadı (Surname)</option>
          <option value="given">Ad (Given Name)</option>
          <option value="birthPlace">Doğum Yeri</option>
          <option value="occupation">Meslek</option>
        </select>
      </div>
      <div class="form-grid-2">
        <div class="form-field">
          <label>Aranan Metin *</label>
          <input type="text" name="findText" required />
        </div>
        <div class="form-field">
          <label>Yeni Metin *</label>
          <input type="text" name="replaceText" required />
        </div>
      </div>
      <div class="modal-actions">
        <button type="button" class="mini-btn" id="cancel-fr-btn">İptal</button>
        <button type="submit" class="primary-btn small-btn">Değiştir</button>
      </div>
    </form>
  `
  );
  document.getElementById("cancel-fr-btn").addEventListener("click", closeModal);
  document.getElementById("find-replace-form").addEventListener("submit", async (e: any) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const res = await apiRequest("find-replace", "POST", {
      field: fd.get("field"),
      findText: fd.get("findText"),
      replaceText: fd.get("replaceText"),
    });
    if (res.ok) {
      closeModal();
      if (res.data && res.data.individuals) prepareDataset(res.data);
      refreshActiveViews(false);
      showSyncToast(`✓ ${res.data?.updatedCount || 0} kayıt güncellendi`);
    }
  });
}

/* ================= SOY AĞACI SAĞLIK VE TUTARLILIK DENETLEYİCİSİ ================= */
function runTreeHealthAudit() {
  if (!state.data) return { score: 100, totalIndividuals: 0, errorCount: 0, warningCount: 0, infoCount: 0, issues: [] };
  const individuals = state.data.individuals;
  const families = state.data.families;
  const curYear = new Date().getFullYear();
  const issues = [];
  const tr = state.lang === "tr";

  const getYear = (val) => extractNumericYear(val);

  for (const [id, p] of Object.entries(individuals)) {
    const b = getYear(p.birthYear);
    const d = getYear(p.deathYear);

    // 1. Ölüm yılı doğum yılından önce
    if (b && d && d < b) {
      issues.push({
        type: "error",
        category: "chronology",
        personId: id,
        personName: p.name,
        sex: p.sex,
        deceased: p.deceased,
        title: tr ? "Ölüm yılı doğum yılından önce" : "Death year is before birth year",
        detail: tr ? `Doğum: ${b}, Ölüm: ${d} (Çelişki: ${b - d} yıl)` : `Birth: ${b}, Death: ${d} (${b - d} yr conflict)`
      });
    }

    // 2. Gelecek doğum yılı
    if (b && b > curYear) {
      issues.push({
        type: "error",
        category: "chronology",
        personId: id,
        personName: p.name,
        sex: p.sex,
        deceased: p.deceased,
        title: tr ? "Gelecekteki doğum yılı" : "Future birth year",
        detail: tr ? `Doğum yılı ${b} mevcut yıldan (${curYear}) sonra` : `Birth year ${b} is after current year (${curYear})`
      });
    }

    // 3. Gelecek ölüm yılı
    if (d && d > curYear) {
      issues.push({
        type: "error",
        category: "chronology",
        personId: id,
        personName: p.name,
        sex: p.sex,
        deceased: p.deceased,
        title: tr ? "Gelecekteki ölüm yılı" : "Future death year",
        detail: tr ? `Ölüm yılı ${d} mevcut yıldan (${curYear}) sonra` : `Death year ${d} is after current year (${curYear})`
      });
    }

    // 4. Aşırı uzun ömür (>115)
    if (b && d && d - b > 115) {
      issues.push({
        type: "warning",
        category: "chronology",
        personId: id,
        personName: p.name,
        sex: p.sex,
        deceased: p.deceased,
        title: tr ? "Olağandışı uzun ömür (>115 yaş)" : "Unusual lifespan (>115 years)",
        detail: tr ? `Doğum: ${b}, Ölüm: ${d} (${d - b} yıl) - Muhtemel yazım hatası` : `Birth: ${b}, Death: ${d} (${d - b} yrs) - Likely typo`
      });
    }

    // 5. 115+ yaşında fakat vefat işareti yok
    if (b && !p.deceased && curYear - b > 115) {
      issues.push({
        type: "warning",
        category: "chronology",
        personId: id,
        personName: p.name,
        sex: p.sex,
        deceased: p.deceased,
        title: tr ? "115+ yaşında fakat vefat işareti yok" : "115+ years old but marked living",
        detail: tr ? `Doğum: ${b} (${curYear - b} yaşında) - Vefat durumu güncellenmeli` : `Born: ${b} (${curYear - b} yrs old) - Should be marked deceased`
      });
    }

    // 6. Ebeveyn - Çocuk yaş kontrolleri
    for (const famcId of p.famc || []) {
      const fam = families[famcId];
      if (!fam) continue;

      if (fam.wife && individuals[fam.wife] && b) {
        const mother = individuals[fam.wife];
        const mB = getYear(mother.birthYear);
        if (mB) {
          const mAge = b - mB;
          if (mAge <= 0) {
            issues.push({
              type: "error",
              category: "chronology",
              personId: id,
              personName: p.name,
              sex: p.sex,
              deceased: p.deceased,
              title: tr ? `Anne (${mother.name}) çocuktan sonra veya aynı yıl doğmuş` : `Mother born after/same year as child`,
              detail: tr ? `Anne doğum: ${mB}, Çocuk doğum: ${b}` : `Mother: ${mB}, Child: ${b}`
            });
          } else if (mAge < 12) {
            issues.push({
              type: "error",
              category: "chronology",
              personId: id,
              personName: p.name,
              sex: p.sex,
              deceased: p.deceased,
              title: tr ? `Anne (${mother.name}) doğumda çok küçük (${mAge} yaş)` : `Mother too young (${mAge} yrs)`,
              detail: tr ? `Anne doğum: ${mB}, Çocuk doğum: ${b}` : `Mother: ${mB}, Child: ${b}`
            });
          } else if (mAge > 56) {
            issues.push({
              type: "warning",
              category: "chronology",
              personId: id,
              personName: p.name,
              sex: p.sex,
              deceased: p.deceased,
              title: tr ? `Anne (${mother.name}) doğumda ileri yaşta (${mAge} yaş)` : `Mother advanced age (${mAge} yrs)`,
              detail: tr ? `Anne doğum: ${mB}, Çocuk doğum: ${b}` : `Mother: ${mB}, Child: ${b}`
            });
          }
        }
      }

      if (fam.husb && individuals[fam.husb] && b) {
        const father = individuals[fam.husb];
        const fB = getYear(father.birthYear);
        const fD = getYear(father.deathYear);
        if (fB) {
          const fAge = b - fB;
          if (fAge <= 0) {
            issues.push({
              type: "error",
              category: "chronology",
              personId: id,
              personName: p.name,
              sex: p.sex,
              deceased: p.deceased,
              title: tr ? `Baba (${father.name}) çocuktan sonra veya aynı yıl doğmuş` : `Father born after/same year as child`,
              detail: tr ? `Baba doğum: ${fB}, Çocuk doğum: ${b}` : `Father: ${fB}, Child: ${b}`
            });
          } else if (fAge < 12) {
            issues.push({
              type: "error",
              category: "chronology",
              personId: id,
              personName: p.name,
              sex: p.sex,
              deceased: p.deceased,
              title: tr ? `Baba (${father.name}) doğumda çok küçük (${fAge} yaş)` : `Father too young (${fAge} yrs)`,
              detail: tr ? `Baba doğum: ${fB}, Çocuk doğum: ${b}` : `Father: ${fB}, Child: ${b}`
            });
          }
        }
        if (fD && b > fD + 1) {
          issues.push({
            type: "warning",
            category: "chronology",
            personId: id,
            personName: p.name,
            sex: p.sex,
            deceased: p.deceased,
            title: tr ? `Çocuk, babanın (${father.name}) vefatından sonra doğmuş` : `Child born after father passed away`,
            detail: tr ? `Baba ölüm: ${fD}, Çocuk doğum: ${b}` : `Father death: ${fD}, Child birth: ${b}`
          });
        }
      }
    }

    // 7. İzole kişi (hiçbir ebeveyne, eşe veya çocuğa bağlı değil)
    const hasParents = (p.famc || []).length > 0;
    const hasSpouseOrKids = (p.fams || []).some((fid) => {
      const fam = families[fid];
      return fam && ((fam.husb && fam.husb !== id) || (fam.wife && fam.wife !== id) || (fam.children || []).length > 0);
    });
    if (!hasParents && !hasSpouseOrKids) {
      issues.push({
        type: "info",
        category: "isolated",
        personId: id,
        personName: p.name,
        sex: p.sex,
        deceased: p.deceased,
        title: tr ? "Bağlantısız bağımsız kayıt (İzole Kişi)" : "Disconnected person (Orphan record)",
        detail: tr ? "Soy ağacında hiçbir ebeveyne, eşe veya çocuğa bağlı değil" : "Not connected to any parent, spouse, or child"
      });
    }
  }

  // 8. Olası Çift / Mükerrer Kayıt Denetimi
  const namesMap = {};
  for (const [id, p] of Object.entries(individuals)) {
    const norm = normalizePasscode(p.name);
    if (!norm || norm === "bilinmiyor" || norm === "unknown" || norm.length < 3) continue;
    if (!namesMap[norm]) namesMap[norm] = [];
    namesMap[norm].push(p);
  }

  for (const [norm, group] of Object.entries(namesMap)) {
    if (group.length > 1) {
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const p1 = group[i];
          const p2 = group[j];
          const b1 = getYear(p1.birthYear);
          const b2 = getYear(p2.birthYear);

          // Aynı ad soyad ve (aynı doğum yılı VEYA ikisinin de doğum yılı boş)
          if ((b1 && b2 && Math.abs(b1 - b2) <= 1) || (!b1 && !b2)) {
            issues.push({
              type: "warning",
              category: "duplicate",
              personId: p1.id,
              personName: p1.name,
              sex: p1.sex,
              deceased: p1.deceased,
              targetId: p2.id,
              targetName: p2.name,
              targetSex: p2.sex,
              targetDeceased: p2.deceased,
              title: tr ? `Olası çift kayıt: ${p1.name}` : `Potential duplicate: ${p1.name}`,
              detail: b1
                ? tr
                  ? `[${p1.id}] ve [${p2.id}] aynı ad-soyada ve aynı/yakın doğum yılına sahip (${b1})`
                  : `[${p1.id}] and [${p2.id}] have same name and birth year (${b1})`
                : tr
                  ? `[${p1.id}] ve [${p2.id}] aynı ad-soyada sahip ve her ikisinde de doğum yılı boş`
                  : `[${p1.id}] and [${p2.id}] have same name and both birth years are empty`
            });
          }
        }
      }
    }
  }

  const errorCount = issues.filter((i) => i.type === "error").length;
  const warningCount = issues.filter((i) => i.type === "warning").length;
  const infoCount = issues.filter((i) => i.type === "info").length;
  const penalty = errorCount * 5 + warningCount * 2 + infoCount * 0.5;
  const score = Math.max(0, Math.min(100, Math.round(100 - penalty)));

  return {
    score,
    totalIndividuals: Object.keys(individuals).length,
    errorCount,
    warningCount,
    infoCount,
    issues
  };
}

function openHealthReportModal() {
  const audit = runTreeHealthAudit();
  const tr = state.lang === "tr";

  let scoreClass = "score-good";
  let scoreStatus = t("healthScorePerfect");
  if (audit.score < 75) {
    scoreClass = "score-danger";
    scoreStatus = t("healthScoreAttention");
  } else if (audit.score < 90) {
    scoreClass = "score-warning";
    scoreStatus = t("healthScoreGood");
  }

  const dupCount = audit.issues.filter((i) => i.category === "duplicate").length;
  const chronCount = audit.issues.filter((i) => i.category === "chronology").length;
  const isoCount = audit.issues.filter((i) => i.category === "isolated").length;

  const renderIssueItems = (filterCategory = "all") => {
    const list = filterCategory === "all" ? audit.issues : audit.issues.filter((i) => i.category === filterCategory);
    if (list.length === 0) {
      return `
        <div class="health-success-box">
          <span class="health-success-icon">🎉</span>
          <div class="health-success-title">${escapeHtml(t("healthNoIssues"))}</div>
          <p class="health-success-desc">
            ${tr ? `${audit.totalIndividuals} aile üyesinin tüm tarihleri ve bağları tutarlı.` : `All ${audit.totalIndividuals} family member dates and links are consistent.`}
          </p>
        </div>
      `;
    }

    return list
      .map((item) => {
        const isDeceased = Boolean(item.deceased);
        const avatarSvg = renderAvatarSilhouette(item.sex || "U", isDeceased);
        const isDuplicate = item.category === "duplicate" && item.targetId;

        return `
          <div class="health-issue-card issue-${item.type}">
            <div class="health-issue-main">
              <span class="health-issue-avatar">${avatarSvg}</span>
              <div class="health-issue-content">
                <div class="health-issue-title">
                  <span>${escapeHtml(item.title)}</span>
                  <span class="node-id-badge" style="font-size:0.68rem;padding:0.1rem 0.35rem;">${item.personId}</span>
                </div>
                <div class="health-issue-detail">${escapeHtml(item.detail)}</div>
              </div>
            </div>
            <div class="health-issue-actions">
              <button type="button" class="mini-btn health-action-focus" data-person-id="${item.personId}" title="${t("healthFocusBtn")}">
                👁️ ${t("healthFocusBtn")}
              </button>
              <button type="button" class="mini-btn health-action-edit" data-person-id="${item.personId}" title="${t("healthEditBtn")}">
                ✏️ ${t("healthEditBtn")}
              </button>
              ${
                isDuplicate
                  ? `<button type="button" class="primary-btn mini-btn health-action-merge" data-source-id="${item.targetId}" data-target-id="${item.personId}" title="${t("healthMergeBtn")}">
                      🔀 ${t("healthMergeBtn")}
                    </button>`
                  : ""
              }
            </div>
          </div>
        `;
      })
      .join("");
  };

  const html = `
    <div class="health-scorecard">
      <div class="health-score-ring">
        <span class="health-score-val ${scoreClass}">${audit.score}%</span>
        <div class="health-score-meta">
          <span class="health-score-title">${escapeHtml(t("healthScoreLabel"))}</span>
          <span class="health-score-subtitle">${escapeHtml(scoreStatus)} · ${audit.totalIndividuals} ${tr ? "birey" : "people"}</span>
        </div>
      </div>
      <div class="health-stats-row">
        <span class="health-stat-pill pill-error" title="Kritik Mantık Hataları">🔴 ${audit.errorCount} ${t("healthTabErrors")}</span>
        <span class="health-stat-pill pill-warning" title="Olası Çift Kayıtlar">🔀 ${dupCount} ${t("healthTabDuplicates")}</span>
        <span class="health-stat-pill pill-warning" title="Tarih ve Yaş Uyuşmazlıkları">⏳ ${chronCount} ${t("healthTabChronology")}</span>
        <span class="health-stat-pill pill-info" title="Bağlantısız İzole Kişiler">🏝️ ${isoCount} ${t("healthTabIsolated")}</span>
      </div>
    </div>

    <div class="health-tabs">
      <button type="button" class="health-tab-btn active" data-cat="all">${t("healthTabAll")} (${audit.issues.length})</button>
      <button type="button" class="health-tab-btn" data-cat="duplicate">🔀 ${t("healthTabDuplicates")} (${dupCount})</button>
      <button type="button" class="health-tab-btn" data-cat="chronology">⏳ ${t("healthTabChronology")} (${chronCount})</button>
      <button type="button" class="health-tab-btn" data-cat="isolated">🏝️ ${t("healthTabIsolated")} (${isoCount})</button>
    </div>

    <div class="health-issues-container" id="health-issues-list">
      ${renderIssueItems("all")}
    </div>

    <div class="modal-actions" style="margin-top:0.5rem;">
      <button type="button" class="primary-btn small-btn" id="close-health-modal-btn">${tr ? "Tamam" : "Close"}</button>
    </div>
  `;

  openModal(t("healthModalTitle"), html, true);

  const container = document.getElementById("health-issues-list");

  // Tab switching
  document.querySelectorAll(".health-tab-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      document.querySelectorAll(".health-tab-btn").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      const cat = btn.getAttribute("data-cat");
      container.innerHTML = renderIssueItems(cat);
      bindIssueActions();
    });
  });

  const bindIssueActions = () => {
    container.querySelectorAll(".health-action-focus").forEach((btn) => {
      btn.addEventListener("click", () => {
        const pid = btn.getAttribute("data-person-id");
        closeModal();
        switchView("tree");
        focusPersonInTree(pid);
      });
    });

    container.querySelectorAll(".health-action-edit").forEach((btn) => {
      btn.addEventListener("click", () => {
        const pid = btn.getAttribute("data-person-id");
        requireEditorThen(() => openEditPersonModal(pid));
      });
    });

    container.querySelectorAll(".health-action-merge").forEach((btn) => {
      btn.addEventListener("click", () => {
        const sourceId = btn.getAttribute("data-source-id");
        const targetId = btn.getAttribute("data-target-id");
        requireEditorThen(() => openMergeModal(sourceId, targetId));
      });
    });
  };

  bindIssueActions();
  document.getElementById("close-health-modal-btn").addEventListener("click", closeModal);
}

/* ================= LOCAL FALLBACK WHEN RUNNING WITHOUT WORKER ================= */
function applyLocalMutationFallback(path, method, body) {
  const indis = state.data.individuals;
  const fams = state.data.families;

  if (path === "person" && method === "POST") {
    const newId = `I${Object.keys(indis).length + 100}`;
    indis[newId] = {
      id: newId,
      uid: crypto.randomUUID(),
      given: body.given || "",
      surname: body.surname || "",
      name: `${body.given || ""} ${body.surname || ""}`.trim(),
      sex: body.sex || "U",
      birthYear: body.birthYear || "",
      deathYear: body.deathYear || "",
      deceased: Boolean(body.deceased),
      fams: [],
      famc: [],
    };
    if (body.relType && body.anchorId) {
      attachLocalRelationship(body.anchorId, newId, body.relType, body);
    }
    prepareDataset(state.data);
    return { ok: true, data: { newId } };
  }
  if (path.startsWith("person/") && method === "PUT") {
    const pid = path.split("/")[1];
    if (indis[pid]) {
      Object.assign(indis[pid], body);
      if (body.parentFamilyId !== undefined) {
        const targetFamId = typeof body.parentFamilyId === "string" ? body.parentFamilyId.trim() : "";
        for (const f of Object.values(fams)) {
          if (f.children && f.children.includes(pid)) {
            f.children = f.children.filter((c) => c !== pid);
          }
        }
        if (targetFamId && fams[targetFamId]) {
          if (!fams[targetFamId].children) fams[targetFamId].children = [];
          if (!fams[targetFamId].children.includes(pid)) {
            fams[targetFamId].children.push(pid);
          }
        }
      }
    }
    prepareDataset(state.data);
    return { ok: true, data: {} };
  }
  if (path.startsWith("person/") && method === "DELETE") {
    const pid = path.split("/")[1];
    delete indis[pid];
    for (const f of Object.values(fams)) {
      if (f.husb === pid) f.husb = null;
      if (f.wife === pid) f.wife = null;
      f.children = (f.children || []).filter((c) => c !== pid);
    }
    prepareDataset(state.data);
    return { ok: true, data: {} };
  }
  if (path === "relationship" && method === "POST") {
    if (body.operation === "update_family" && body.familyId && fams[body.familyId]) {
      const fam = fams[body.familyId];
      if (body.status !== undefined) fam.status = body.status;
      if (body.marriageYear !== undefined) fam.marriageYear = body.marriageYear;
      if (body.marriagePlace !== undefined) fam.marriagePlace = body.marriagePlace;
      if (body.divorceYear !== undefined) fam.divorceYear = body.divorceYear;
      if (body.wifeSurname && fam.wife && indis[fam.wife]) {
        indis[fam.wife].surname = body.wifeSurname;
        if (body.wifeBirthSurname) indis[fam.wife].birthSurname = body.wifeBirthSurname;
        indis[fam.wife].name = `${indis[fam.wife].given || ""} ${indis[fam.wife].surname || ""}`.trim();
      }
    } else if (body.operation === "delete_family" && body.familyId && fams[body.familyId]) {
      delete fams[body.familyId];
    } else if (body.anchorId && body.targetId && body.relType) {
      attachLocalRelationship(body.anchorId, body.targetId, body.relType, body);
    }
    prepareDataset(state.data);
    return { ok: true, data: { individuals: indis, families: fams } };
  }
  return { ok: true, data: {} };
}

function attachLocalRelationship(anchorId, targetId, relType, opts) {
  const indis = state.data.individuals;
  const fams = state.data.families;
  const anchor = indis[anchorId];
  const target = indis[targetId];
  if (!anchor || !target) return;

  if (relType === "child") {
    let fid = opts.familyId || anchor.fams[0];
    if (!fid) {
      fid = `F${Object.keys(fams).length + 50}`;
      fams[fid] = {
        id: fid,
        husb: anchor.sex === "F" ? null : anchorId,
        wife: anchor.sex === "F" ? anchorId : null,
        children: [],
        status: "current",
      };
    }
    if (!fams[fid].children.includes(targetId)) fams[fid].children.push(targetId);
  } else if (relType === "spouse") {
    const fid = `F${Object.keys(fams).length + 50}`;
    const isHusb = anchor.sex !== "F";
    fams[fid] = {
      id: fid,
      husb: isHusb ? anchorId : targetId,
      wife: isHusb ? targetId : anchorId,
      children: [],
      status: opts.marriageStatus || "current",
      marriageYear: opts.marriageYear || "",
    };
  } else if (relType === "father" || relType === "mother") {
    let fid = anchor.famc[0];
    if (!fid) {
      fid = `F${Object.keys(fams).length + 50}`;
      fams[fid] = { id: fid, husb: null, wife: null, children: [anchorId], status: "current" };
    }
    if (relType === "father") fams[fid].husb = targetId;
    else fams[fid].wife = targetId;
  } else if (relType === "sibling") {
    let fid = anchor.famc[0];
    if (!fid) {
      fid = `F${Object.keys(fams).length + 50}`;
      fams[fid] = { id: fid, husb: null, wife: null, children: [anchorId], status: "current" };
    }
    if (!fams[fid].children.includes(targetId)) fams[fid].children.push(targetId);
  }
}

/* ================= DIRECTORY, TIMELINE, KINSHIP, STATS & MANAGE VIEWS ================= */
function populateSurnameFilter() {
  const select = document.getElementById("dir-surname-filter");
  if (!select) return;
  const currentVal = select.value;
  const counts = {};
  for (const p of state.personList) {
    if (p.surname && !p.surname.includes("?")) {
      counts[p.surname] = (counts[p.surname] || 0) + 1;
    }
    if (p.birthSurname && !p.birthSurname.includes("?") && p.birthSurname !== p.surname) {
      counts[p.birthSurname] = (counts[p.birthSurname] || 0) + 1;
    }
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  select.innerHTML = `<option value="">${t("allSurnamesOption")}</option>`;
  for (const [surn, cnt] of sorted) {
    const opt = document.createElement("option");
    opt.value = surn;
    opt.textContent = `${surn} (${cnt})`;
    select.appendChild(opt);
  }
  if (currentVal && counts[currentVal]) {
    select.value = currentVal;
  } else {
    select.value = "";
  }
}

function renderDirectory() {
  if (!state.data) return;
  const rawQ = document.getElementById("dir-search-input").value;
  const q = normalizeText(rawQ);
  const qPhonetic = normalizePhonetic(rawQ);
  const surnameFilter = document.getElementById("dir-surname-filter").value;
  const genderFilter = document.getElementById("dir-gender-filter").value;
  const statusFilter = document.getElementById("dir-status-filter").value;
  const sortMode = document.getElementById("dir-sort-select").value;

  const filtered = state.personList.filter((p) => {
    if (q && !p._searchNorm.includes(q) && !(qPhonetic && p._phoneticNorm && p._phoneticNorm.includes(qPhonetic))) return false;
    if (surnameFilter && p.surname !== surnameFilter && p.birthSurname !== surnameFilter) return false;
    if (genderFilter && p.sex !== genderFilter) return false;
    if (statusFilter === "living" && p.deceased) return false;
    if (statusFilter === "deceased" && !p.deceased) return false;
    return true;
  });

  filtered.sort((a, b) => {
    if (sortMode === "descendants") return b.descendantCount - a.descendantCount;
    if (sortMode === "name") return a.name.localeCompare(b.name, "tr");
    if (sortMode === "surname") return (a.surname || "ZZZ").localeCompare(b.surname || "ZZZ", "tr");
    if (sortMode === "birthAsc") return (extractNumericYear(a.birthYear) || 9999) - (extractNumericYear(b.birthYear) || 9999);
    if (sortMode === "birthDesc") return (extractNumericYear(b.birthYear) || 0) - (extractNumericYear(a.birthYear) || 0);
    return 0;
  });

  const totalCount = state.personList.length;
  const countLabelEl = document.getElementById("directory-count-label");
  if (countLabelEl) {
    countLabelEl.textContent =
      state.lang === "tr"
        ? (filtered.length === totalCount
            ? `Toplam ${totalCount} aile üyesi`
            : `Toplam ${totalCount} aile üyesi (${filtered.length} listeleniyor)`)
        : (filtered.length === totalCount
            ? `Total ${totalCount} family members`
            : `Total ${totalCount} family members (${filtered.length} showing)`);
  }

  const grid = document.getElementById("directory-grid");
  grid.innerHTML = filtered
    .map(
      (p) => {
        const isDeceased = Boolean(p.deceased);
        const surnameLabel = p.surname
          ? `${escapeHtml(p.surname)}${p.birthSurname && p.birthSurname !== p.surname ? ` <span style="font-weight:400;opacity:0.75;font-size:0.85em;">(${escapeHtml(p.birthSurname)})</span>` : ""}`
          : "";
        return `
        <div class="dir-card sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" data-person-id="${p.id}">
          <div class="dir-card-top">
            <div style="display:flex;align-items:center;gap:0.6rem;min-width:0;flex:1;">
              <span class="rel-avatar sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" aria-hidden="true">${renderAvatarSilhouette(p.sex, isDeceased)}</span>
              <div style="min-width:0;flex:1;">
                <div class="dir-card-name">${escapeHtml(p.name)}</div>
                <div class="dir-card-dates">${escapeHtml(formatLifeYears(p))}</div>
              </div>
            </div>
            ${surnameLabel ? `<span class="node-surname">${surnameLabel}</span>` : ""}
          </div>
          <div class="dir-card-meta">
            <span>${p.generation + 1}. ${t("generationStat")}</span>
            <span>${p.descendantCount} ${t("descendantsBadge")}</span>
          </div>
        </div>
      `;
      }
    )
    .join("");

  if (!grid._hasClickListener) {
    grid.addEventListener("click", (e) => {
      const card = e.target.closest(".dir-card");
      if (card && card.dataset.personId) openPersonDrawer(card.dataset.personId);
    });
    grid._hasClickListener = true;
  }
}

function populateTimelineBranchFilter() {
  const sel = document.getElementById("timeline-branch-filter");
  if (!sel) return;
  const currentVal = state.timelineBranch || "all";
  let html = `<option value="all">${state.lang === "tr" ? "Tüm Kollar" : "All Branches"}</option>`;
  FEATURED_BRANCHES.forEach((b) => {
    const lbl = state.lang === "tr" ? b.labelTr : b.labelEn;
    html += `<option value="${b.id}" ${currentVal === b.id ? "selected" : ""}>${escapeHtml(lbl)}</option>`;
  });
  sel.innerHTML = html;
}

function renderTimeline() {
  if (!state.data || !state.personList) return;
  populateTimelineBranchFilter();

  const searchInput = document.getElementById("timeline-search-input");
  const q = searchInput ? normalizeText(searchInput.value) : "";
  const branchFilter = state.timelineBranch || "all";
  const genFilter = state.timelineGen || "all";
  const typeFilter = state.timelineType || "all";
  const mode = state.timelineMode || "lifespans";

  const branchSet = branchFilter !== "all" ? getBranchPersonIds(branchFilter) : null;

  const filtered = state.personList.filter((p) => {
    if (branchSet && !branchSet.has(p.id)) return false;

    if (genFilter !== "all") {
      const g = p.generation !== undefined ? p.generation : 0;
      if (genFilter === "7+") {
        if (g < 7) return false;
      } else {
        if (g !== parseInt(genFilter, 10)) return false;
      }
    }

    if (typeFilter === "exact" && p.isBirthEstimated) return false;
    if (typeFilter === "living" && p.deceased) return false;
    if (typeFilter === "deceased" && !p.deceased) return false;

    if (q) {
      const combined = normalizeText(
        `${p.name} ${p.surname || ""} ${p.computedBirthYear || ""} ${p.computedDeathYear || ""} ${p.birthPlace || ""} ${p.deathPlace || ""} ${p.occupation || ""}`
      );
      if (!combined.includes(q)) return false;
    }

    return true;
  });

  const listEl = document.getElementById("timeline-list");
  if (!listEl) return;

  if (mode === "lifespans") {
    renderTimelineLifespans(filtered, listEl);
  } else {
    renderTimelineEvents(filtered, listEl, q);
  }
}

function renderTimelineLifespans(individuals, listEl) {
  if (individuals.length === 0) {
    listEl.innerHTML = `
      <div class="empty-state-card" style="text-align:center;padding:3rem 1.5rem;background:var(--bg-surface);border-radius:14px;border:1px dashed var(--border-strong);">
        <div style="font-size:2.5rem;margin-bottom:0.75rem;">🔍</div>
        <h4 style="margin-bottom:0.35rem;">${state.lang === "tr" ? "Filtreye uygun aile üyesi bulunamadı" : "No matching family members found"}</h4>
        <p style="font-size:0.85rem;color:var(--text-muted);">${state.lang === "tr" ? "Arama teriminizi veya filtrelerinizi değiştirerek tekrar deneyebilirsiniz." : "Try adjusting your search query or filters."}</p>
      </div>
    `;
    return;
  }

  // Sort: earliest computed birth year first, root ancestors and older generations first, then alphabetically
  individuals.sort((a, b) => {
    const byA = a.computedBirthYear || 1800;
    const byB = b.computedBirthYear || 1800;
    if (byA !== byB) return byA - byB;
    const genA = a.generation !== undefined ? a.generation : 0;
    const genB = b.generation !== undefined ? b.generation : 0;
    if (genA !== genB) return genA - genB;
    return a.name.localeCompare(b.name, "tr");
  });

  const globalMin = 1780;
  const globalMax = 2030;
  const totalYears = globalMax - globalMin;
  const currentYear = new Date().getFullYear();

  let lastEraId = null;
  const htmlParts = [];

  for (const p of individuals) {
    const era = getTimelineEra(p.computedBirthYear);
    if (era && era.id !== lastEraId) {
      lastEraId = era.id;
      htmlParts.push(`
        <div class="timeline-era-divider">
          <div class="timeline-era-pill">${escapeHtml(state.lang === "tr" ? era.titleTr : era.titleEn)}</div>
          <div class="timeline-era-desc">${escapeHtml(state.lang === "tr" ? era.descTr : era.descEn)}</div>
        </div>
      `);
    }

    const isRoot = p.id === "I227";
    const bYear = p.computedBirthYear || 1800;
    const dYear = p.computedDeathYear || currentYear;

    const startPct = Math.max(0, Math.min(100, ((bYear - globalMin) / totalYears) * 100));
    const endPct = Math.max(startPct, Math.min(100, ((dYear - globalMin) / totalYears) * 100));
    const widthPct = Math.max(3.8, Math.min(100 - startPct, endPct - startPct));

    const fillCls = p.isBirthEstimated ? "is-est" : p.deceased ? "is-deceased" : "is-living";

    const bDisplay = p.isBirthEstimated ? `~${bYear}` : (p.birthYear || `${bYear}`);
    const dDisplay = p.deceased
      ? p.isDeathEstimated
        ? `~${dYear}`
        : (p.deathYear || `${dYear}`)
      : state.lang === "tr"
      ? "Günümüz"
      : "Present";

    const rangeDisplay = `${bDisplay} — ${dDisplay}`;

    const certBadge = !p.isBirthEstimated
      ? `<span class="tl-cert-pill is-exact">✓ ${state.lang === "tr" ? "Kesin Kayıt" : "Verified Record"}</span>`
      : `<span class="tl-cert-pill is-est" title="${state.lang === "tr" ? "Kuşak aktarımı ile hesaplanan yaklaşık dönem" : "Estimated period from generational constraint propagation"}">⏳ ~${state.lang === "tr" ? "Takribi Dönem" : "Estimated Period"}</span>`;

    const ageLabel = p.deceased
      ? `${p.lifeSpanYears} ${state.lang === "tr" ? "Yaşında" : "yrs old"}`
      : `${p.lifeSpanYears} ${state.lang === "tr" ? "Yaşında (Hayatta)" : "yrs old (Living)"}`;

    const genLabel =
      p.generation === 0
        ? `<span class="tl-gen-badge gen-root">👑 1. Kuşak (Kök Ecdat)</span>`
        : `<span class="tl-gen-badge">${(p.generation || 0) + 1}. Kuşak</span>`;

    const metaParts = [];
    if (p.birthPlace) metaParts.push(`📍 D: ${escapeHtml(p.birthPlace)}`);
    if (p.deceased && p.deathPlace) metaParts.push(`🕊️ V: ${escapeHtml(p.deathPlace)}`);
    if (p.occupation) metaParts.push(`💼 ${escapeHtml(p.occupation)}`);
    const metaHtml = metaParts.length > 0 ? `<div class="tl-meta-row">${metaParts.join(" • ")}</div>` : "";

    const avatarHtml =
      p.sex === "F"
        ? `<div class="tl-avatar" style="display:flex;align-items:center;justify-content:center;background:rgba(236,72,153,0.12);color:#ec4899;font-size:1.1rem;">👩</div>`
        : `<div class="tl-avatar" style="display:flex;align-items:center;justify-content:center;background:rgba(37,99,235,0.12);color:#2563eb;font-size:1.1rem;">👨</div>`;

    const barLabel = `${bYear} — ${p.deceased ? dYear : (state.lang === "tr" ? "Günümüz" : "Present")} (${p.lifeSpanYears} ${state.lang === "tr" ? "yıl" : "yrs"})`;

    htmlParts.push(`
      <div class="timeline-lifespan-card ${isRoot ? "is-root-card" : ""}" data-person-id="${p.id}">
        <div class="tl-card-top">
          <div class="tl-person-header">
            ${avatarHtml}
            <div class="tl-info-wrap">
              <div class="tl-name-row">
                <span class="tl-name">${escapeHtml(p.name)}</span>
                ${genLabel}
              </div>
              ${metaHtml}
            </div>
          </div>
          <div class="tl-dates-block">
            <span class="tl-year-range">${escapeHtml(rangeDisplay)}</span>
            <div class="tl-badge-row">
              ${certBadge}
              <span class="tl-age-pill">${escapeHtml(ageLabel)}</span>
            </div>
          </div>
        </div>

        <div class="tl-bar-track" title="${escapeHtml(barLabel)}">
          <div class="tl-bar-fill ${fillCls}" style="left:${startPct.toFixed(2)}%;width:${widthPct.toFixed(2)}%;">
            <span class="tl-bar-label">${escapeHtml(barLabel)}</span>
          </div>
        </div>

        <div class="tl-card-actions">
          <button type="button" class="mini-btn tl-action-btn tl-focus-btn" data-person-id="${p.id}" title="${state.lang === "tr" ? "Soy ağacında odaklan" : "Focus in tree"}">
            🌿 ${state.lang === "tr" ? "Ağaçta Odakla" : "Focus in Tree"}
          </button>
          <button type="button" class="primary-btn small-btn tl-action-btn tl-profile-btn" data-person-id="${p.id}">
            ${state.lang === "tr" ? "Profili Aç" : "Open Profile"}
          </button>
        </div>
      </div>
    `);
  }

  listEl.innerHTML = htmlParts.join("");
  bindTimelineActions(listEl);
}

function renderTimelineEvents(individuals, listEl, query) {
  const events = [];

  for (const p of individuals) {
    const by = p.computedBirthYear;
    if (by && by > 1700) {
      events.push({
        year: by,
        type: "birth",
        isEstimated: p.isBirthEstimated,
        person: p,
        label: `${p.isBirthEstimated ? "⏳ ~Takribi Doğum" : "👶 Doğum"}: ${p.name}`,
      });
    }
    const dy = p.computedDeathYear;
    if (dy && dy > 1700 && p.deceased) {
      events.push({
        year: dy,
        type: "death",
        isEstimated: p.isDeathEstimated,
        person: p,
        label: `${p.isDeathEstimated ? "⏳ ~Takribi Vefat" : "🕊️ Vefat"}: ${p.name}`,
      });
    }
  }

  events.sort((a, b) => a.year - b.year);

  if (events.length === 0) {
    listEl.innerHTML = `
      <div class="empty-state-card" style="text-align:center;padding:3rem 1.5rem;background:var(--bg-surface);border-radius:14px;border:1px dashed var(--border-strong);">
        <div style="font-size:2.5rem;margin-bottom:0.75rem;">📜</div>
        <h4 style="margin-bottom:0.35rem;">${state.lang === "tr" ? "Olay bulunamadı" : "No events found"}</h4>
        <p style="font-size:0.85rem;color:var(--text-muted);">${state.lang === "tr" ? "Arama ve filtre kriterlerinizi genişletiniz." : "Broaden your search and filters."}</p>
      </div>
    `;
    return;
  }

  let lastEraId = null;
  const htmlParts = [];

  for (const ev of events) {
    const era = getTimelineEra(ev.year);
    if (era && era.id !== lastEraId) {
      lastEraId = era.id;
      htmlParts.push(`
        <div class="timeline-era-divider">
          <div class="timeline-era-pill">${escapeHtml(state.lang === "tr" ? era.titleTr : era.titleEn)}</div>
          <div class="timeline-era-desc">${escapeHtml(state.lang === "tr" ? era.descTr : era.descEn)}</div>
        </div>
      `);
    }

    const yearDisplay = ev.isEstimated ? `~${ev.year}` : `${ev.year}`;
    const certText = ev.isEstimated
      ? (state.lang === "tr" ? "⏳ ~Takribi Dönem" : "⏳ ~Estimated")
      : (state.lang === "tr" ? "✓ Kesin Kayıt" : "✓ Verified");

    htmlParts.push(`
      <div class="timeline-item" data-person-id="${ev.person.id}">
        <div style="display:flex;align-items:center;gap:1rem;flex:1;min-width:0;">
          <span class="timeline-year">${escapeHtml(yearDisplay)}</span>
          <div style="min-width:0;">
            <div class="sr-name" style="font-weight:700;">${escapeHtml(ev.label)}</div>
            <div class="sr-sub" style="font-size:0.75rem;color:var(--text-muted);">${certText} • ${escapeHtml(formatLifeYears(ev.person))}</div>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:0.5rem;flex-shrink:0;">
          ${ev.person.surname ? `<span class="node-surname">${escapeHtml(ev.person.surname)}</span>` : ""}
          <button type="button" class="mini-btn tl-action-btn tl-focus-btn" data-person-id="${ev.person.id}" title="${state.lang === "tr" ? "Soy ağacında odaklan" : "Focus in tree"}">
            🌿
          </button>
        </div>
      </div>
    `);
  }

  listEl.innerHTML = htmlParts.join("");
  bindTimelineActions(listEl);
}

function bindTimelineActions(container) {
  container.querySelectorAll(".tl-focus-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const pid = btn.dataset.personId;
      if (pid) {
        focusPersonInTree(pid);
        switchView("tree");
      }
    });
  });

  container.querySelectorAll(".tl-profile-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const pid = btn.dataset.personId;
      if (pid) openPersonDrawer(pid);
    });
  });

  container.querySelectorAll(".timeline-lifespan-card, .timeline-item").forEach((card) => {
    card.addEventListener("click", (e) => {
      if (e.target.closest(".tl-action-btn")) return;
      const pid = card.dataset.personId;
      if (pid) openPersonDrawer(pid);
    });
  });
}

function renderKinshipView() {
  if (!state.data) return;
  const pA = state.data.individuals[state.kinshipA];
  const pB = state.data.individuals[state.kinshipB];

  document.getElementById("kinship-selected-a").innerHTML = pA
    ? `<span>${escapeHtml(pA.name)}</span><small>${escapeHtml(formatLifeYears(pA))}</small>`
    : "—";
  document.getElementById("kinship-selected-b").innerHTML = pB
    ? `<span>${escapeHtml(pB.name)}</span><small>${escapeHtml(formatLifeYears(pB))}</small>`
    : "—";

  const out = document.getElementById("kinship-result-area");
  if (!pA || !pB) {
    out.innerHTML = "";
    return;
  }
  if (pA.id === pB.id) {
    out.innerHTML = `<div class="kinship-summary-banner">${t("samePerson")}</div>`;
    return;
  }

  const chain = findShortestKinshipPath(pA.id, pB.id);
  if (!chain) {
    out.innerHTML = `<div class="kinship-summary-banner">${t("noConnection")}</div>`;
    return;
  }

  const stepsCount = chain.length - 1;
  const relSummary = chain
    .slice(1)
    .map((s) => translateRelStep(s.relFromPrev))
    .join(" → ");

  out.innerHTML = `
    <div class="kinship-summary-banner">
      <div class="kinship-summary-title">${escapeHtml(relSummary)}</div>
      <div class="section-subtitle">${stepsCount} ${t("stepsDegree")}</div>
    </div>
    <div class="kinship-steps-chain">
      ${chain
        .map(
          (step, idx) => `
          ${idx > 0 ? `<div class="kinship-arrow-row">↓ ${escapeHtml(translateRelStep(step.relFromPrev))}</div>` : ""}
          <div class="kinship-step-card" data-person-id="${step.person.id}">
            <div>
              <div class="sr-name">${escapeHtml(step.person.name)}</div>
              <div class="sr-sub">${escapeHtml(formatLifeYears(step.person))}</div>
            </div>
            <span class="chip-btn">${step.person.generation + 1}. ${t("generationStat")}</span>
          </div>
        `
        )
        .join("")}
    </div>
  `;

  out.querySelectorAll(".kinship-step-card").forEach((card) => {
    card.addEventListener("click", () => openPersonDrawer(card.dataset.personId));
  });
}

/* ================= STATISTICS VIEW ================= */
function renderStats() {
  if (!state.data) return;
  const totalP = state.personList.length;
  const totalF = Object.keys(state.data.families).length;
  const males = state.personList.filter((p) => p.sex === "M").length;
  const females = state.personList.filter((p) => p.sex === "F").length;
  const deceased = state.personList.filter((p) => p.deceased).length;
  const living = totalP - deceased;

  const surnameCounts: Record<string, number> = {};
  const maleNameCounts: Record<string, number> = {};
  const femaleNameCounts: Record<string, number> = {};
  const placeCounts: Record<string, number> = {};
  const generationCounts: Record<number, number> = {};
  let minBirth = 9999;
  let maxGen = 0;
  const decadeCounts: Record<number, number> = {};

  const lifespans: number[] = [];
  const eldestPeople: Array<{ person: PersonRecord; age: number; isLiving: boolean }> = [];

  for (const p of state.personList) {
    if (p.surname && !p.surname.includes("?")) {
      surnameCounts[p.surname] = (surnameCounts[p.surname] || 0) + 1;
    }
    if (p.given && !p.given.includes("?")) {
      const firstToken = p.given.split(" ")[0];
      if (firstToken.length > 1) {
        if (p.sex === "M") maleNameCounts[firstToken] = (maleNameCounts[firstToken] || 0) + 1;
        if (p.sex === "F") femaleNameCounts[firstToken] = (femaleNameCounts[firstToken] || 0) + 1;
      }
    }

    // Places
    const pl = (p.birthPlace || "").trim().replace(/^!|\(!\)$/g, "").trim();
    if (pl && pl.length > 1 && !pl.includes("?")) {
      placeCounts[pl] = (placeCounts[pl] || 0) + 1;
    }

    const by = extractNumericYear(p.birthYear);
    const dy = extractNumericYear(p.deathYear);
    if (by && by >= 1400 && by < minBirth) minBirth = by;
    if (by && by >= 1800 && by <= 2030) {
      const dec = Math.floor(by / 10) * 10;
      decadeCounts[dec] = (decadeCounts[dec] || 0) + 1;
    }

    // Longevity calculation
    if (by && dy && dy >= by) {
      const age = dy - by;
      if (age >= 1 && age <= 120) {
        lifespans.push(age);
        if (age >= 80) eldestPeople.push({ person: p, age, isLiving: false });
      }
    } else if (by && !p.deceased && by >= 1900 && by <= 1946) {
      const age = 2026 - by;
      if (age >= 80 && age <= 115) {
        eldestPeople.push({ person: p, age, isLiving: true });
      }
    }

    const gen = p.generation !== undefined ? p.generation + 1 : 1;
    generationCounts[gen] = (generationCounts[gen] || 0) + 1;
    if (gen > maxGen) maxGen = gen;
  }

  const distinctSurnames = Object.keys(surnameCounts).length;
  const avgLifespan = lifespans.length > 0 ? Math.round(lifespans.reduce((a, b) => a + b, 0) / lifespans.length) : 63;
  
  // Children per family with children
  const activeFamilies = Object.values(state.data.families).filter((f) => (f.children || []).length > 0);
  const avgChildren = activeFamilies.length > 0 
    ? (activeFamilies.reduce((s, f) => s + (f.children || []).length, 0) / activeFamilies.length).toFixed(1)
    : "2.0";

  document.getElementById("stats-kpi-grid").innerHTML = `
    <div class="kpi-card"><div class="kpi-value">${totalP}</div><div class="kpi-label">${t("kpiTotalPeople")}</div></div>
    <div class="kpi-card"><div class="kpi-value">${totalF}</div><div class="kpi-label">${t("kpiTotalFamilies")}</div></div>
    <div class="kpi-card"><div class="kpi-value">${distinctSurnames}</div><div class="kpi-label">${t("kpiSurnamesCount")}</div></div>
    <div class="kpi-card"><div class="kpi-value">${minBirth < 9999 ? minBirth : "—"}</div><div class="kpi-label">${t("kpiEarliestBirth")}</div></div>
    <div class="kpi-card"><div class="kpi-value">${maxGen}</div><div class="kpi-label">${t("kpiMaxGenerations")}</div></div>
    <div class="kpi-card"><div class="kpi-value">${females} / ${males}</div><div class="kpi-label">${t("kpiGenderSplit")}</div></div>
  `;

  function renderBarList(entries: any[], containerId: string, clickHandler: any = null) {
    const maxCnt = entries[0] ? entries[0][1] : 1;
    const el = document.getElementById(containerId);
    if (!el) return;
    el.innerHTML = entries
      .map(
        ([lbl, cnt]) => `
        <div class="bar-row" data-key="${escapeHtml(lbl)}">
          <span class="bar-label" title="${escapeHtml(lbl)}">${escapeHtml(lbl)}</span>
          <div class="bar-track"><div class="bar-fill" style="width:${Math.max(4, Math.round((cnt / maxCnt) * 100))}%"></div></div>
          <span class="bar-count">${cnt}</span>
        </div>
      `
      )
      .join("");
    if (clickHandler) {
      if (!(el as any)._hasBarListener) {
        el.addEventListener("click", (e: any) => {
          const row = e.target.closest(".bar-row");
          if (row && row.dataset.key && (el as any)._barClickHandler) {
            (el as any)._barClickHandler(row.dataset.key);
          }
        });
        (el as any)._hasBarListener = true;
      }
      (el as any)._barClickHandler = clickHandler;
    }
  }

  // 1. Top Surnames (clickable to directory)
  renderBarList(
    Object.entries(surnameCounts).sort((a, b) => b[1] - a[1]).slice(0, 16),
    "stats-surnames-bars",
    (surn: string) => {
      const dirFilter = document.getElementById("dir-surname-filter") as HTMLSelectElement;
      if (dirFilter) dirFilter.value = surn;
      switchView("directory");
      renderDirectory();
    }
  );

  // 2. Male and Female Names
  renderBarList(
    Object.entries(maleNameCounts).sort((a, b) => b[1] - a[1]).slice(0, 10),
    "stats-male-names"
  );

  renderBarList(
    Object.entries(femaleNameCounts).sort((a, b) => b[1] - a[1]).slice(0, 10),
    "stats-female-names"
  );

  // 3. Top Roots
  const topRoots = state.personList
    .filter((p) => p.famc.length === 0 && !p.name.includes("?"))
    .sort((a, b) => b.descendantCount - a.descendantCount)
    .slice(0, 10);

  const rootsEl = document.getElementById("stats-roots-list");
  if (rootsEl) {
    rootsEl.innerHTML = topRoots
      .map(
        (r) => `
        <div class="root-rank-item" data-person-id="${r.id}">
          <div style="min-width:0;">
            <div class="sr-name" style="white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(r.name)}</div>
            <div class="sr-sub">${escapeHtml(formatLifeYears(r))}</div>
          </div>
          <span class="chip-btn" style="flex-shrink:0;">${r.descendantCount} ${t("descendantsBadge")}</span>
        </div>
      `
      )
      .join("");

    if (!(rootsEl as any)._hasClickListener) {
      rootsEl.addEventListener("click", (e: any) => {
        const item = e.target.closest(".root-rank-item");
        if (item && item.dataset.personId) focusPersonInTree(item.dataset.personId);
      });
      (rootsEl as any)._hasClickListener = true;
    }
  }

  // 4. Places Distribution (Clickable to Map or Directory)
  const sortedPlaces = Object.entries(placeCounts).sort((a, b) => b[1] - a[1]).slice(0, 10);
  renderBarList(
    sortedPlaces,
    "stats-places-bars",
    (placeName: string) => {
      // Filter in directory
      const dirSearch = document.getElementById("directory-search-input") as HTMLInputElement;
      if (dirSearch) {
        dirSearch.value = placeName;
        switchView("directory");
        renderDirectory();
      }
    }
  );

  // 5. Eldest Centenarians (Asırlık Ulu Çınarlar - 80+ Yaş)
  const sortedEldest = eldestPeople.sort((a, b) => b.age - a.age).slice(0, 10);
  const eldestEl = document.getElementById("stats-eldest-list");
  if (eldestEl) {
    eldestEl.innerHTML = sortedEldest
      .map(
        (e) => `
        <div class="root-rank-item" data-person-id="${e.person.id}">
          <div style="min-width:0;">
            <div class="sr-name" style="white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(e.person.name)}</div>
            <div class="sr-sub">${escapeHtml(formatLifeYears(e.person))} • ${escapeHtml(e.person.birthPlace || "Balkanlar/Türkiye")}</div>
          </div>
          <span class="chip-btn" style="flex-shrink:0; background:rgba(212,175,55,0.15); color:var(--accent-gold); font-weight:700;">
            ${e.age} Yaş ${e.isLiving ? "🌱" : "🕊️"}
          </span>
        </div>
      `
      )
      .join("");

    if (!(eldestEl as any)._hasClickListener) {
      eldestEl.addEventListener("click", (e: any) => {
        const item = e.target.closest(".root-rank-item");
        if (item && item.dataset.personId) focusPersonInTree(item.dataset.personId);
      });
      (eldestEl as any)._hasClickListener = true;
    }
  }

  // 6. Generation Depth Breakdown (Piramit)
  const genEntries = Object.entries(generationCounts)
    .map(([g, c]) => [`${g}. Kuşak`, c])
    .sort((a, b) => parseInt(a[0] as string) - parseInt(b[0] as string));
  renderBarList(genEntries, "stats-generation-bars");

  // 7. Vital Status & Longevity Insights
  const vitalEl = document.getElementById("stats-vital-insights");
  if (vitalEl) {
    const livingPct = Math.round((living / totalP) * 100);
    const deceasedPct = 100 - livingPct;
    vitalEl.innerHTML = `
      <div class="vital-insight-row">
        <div class="vital-insight-meta">
          <span class="vital-insight-title">🌱 Hayatta Olan Birey Oranı</span>
          <span class="vital-insight-val">${living} kişi (%${livingPct})</span>
        </div>
        <div class="bar-track"><div class="bar-fill" style="width:${livingPct}%; background:#10b981;"></div></div>
      </div>

      <div class="vital-insight-row" style="margin-top:0.75rem;">
        <div class="vital-insight-meta">
          <span class="vital-insight-title">🕊️ Aramızdan Ayrılanlar</span>
          <span class="vital-insight-val">${deceased} kişi (%${deceasedPct})</span>
        </div>
        <div class="bar-track"><div class="bar-fill" style="width:${deceasedPct}%; background:#94a3b8;"></div></div>
      </div>

      <div class="vital-metric-pills">
        <div class="vital-pill">
          <span class="vp-label">Ortalama Yaşam</span>
          <span class="vp-val">${avgLifespan} Yaş</span>
        </div>
        <div class="vital-pill">
          <span class="vp-label">Aile Başı Çocuk</span>
          <span class="vp-val">${avgChildren} Çocuk</span>
        </div>
        <div class="vital-pill">
          <span class="vp-label">80+ Yaş Ulu Çınar</span>
          <span class="vp-val">${eldestPeople.length} Kişi</span>
        </div>
      </div>
    `;
  }

  // 8. Decades of Birth Distribution
  const decades = Object.keys(decadeCounts).map(Number).sort((a, b) => a - b);
  const maxDecCnt = Math.max(...Object.values(decadeCounts), 1);

  const decadesChart = document.getElementById("stats-decades-chart");
  if (decadesChart) {
    decadesChart.innerHTML = decades
      .map((dec) => {
        const cnt = decadeCounts[dec];
        const hPct = Math.max(6, Math.round((cnt / maxDecCnt) * 100));
        return `
          <div class="decade-col">
            <span class="decade-count">${cnt}</span>
            <div class="decade-bar" style="height:${hPct}%"></div>
            <span class="decade-label">${dec}'ler</span>
          </div>
        `;
      })
      .join("");
  }
}

/* ================= GEDCOM 5.5.1 GENERATOR & AUDIT LOG ================= */
function generateGedcomFile() {
  const lines = [
    "0 HEAD",
    "1 GEDC",
    "2 VERS 5.5.1",
    "2 FORM LINEAGE-LINKED",
    "1 CHAR UTF-8",
    "1 SOUR NiksicHocicCloud",
    "2 NAME Niksiç & Hociç Soy Ağacı Bulut Sistemi",
    "1 FILE Niksic-Hocic.ged",
  ];

  for (const p of Object.values(state.data.individuals)) {
    lines.push(`0 @${p.id}@ INDI`);
    lines.push(`1 NAME ${p.given || ""} /${p.surname || ""}/`);
    if (p.given) lines.push(`2 GIVN ${p.given}`);
    if (p.surname) lines.push(`2 SURN ${p.surname}`);
    if (p.sex && p.sex !== "U") lines.push(`1 SEX ${p.sex}`);
    if (p.birthYear || p.birthPlace) {
      lines.push("1 BIRT");
      if (p.birthYear) lines.push(`2 DATE (${p.birthYear})`);
      if (p.birthPlace) lines.push(`2 PLAC ${p.birthPlace}`);
    }
    if (p.deceased || p.deathYear || p.deathPlace) {
      lines.push(p.deathYear || p.deathPlace ? "1 DEAT" : "1 DEAT Y");
      if (p.deathYear) lines.push(`2 DATE (${p.deathYear})`);
      if (p.deathPlace) lines.push(`2 PLAC ${p.deathPlace}`);
    }
    if (p.occupation) lines.push(`1 OCCU ${p.occupation}`);
    if (p.notes) lines.push(`1 NOTE ${p.notes.replace(/\r?\n/g, " ")}`);
    for (const fc of p.famc || []) lines.push(`1 FAMC @${fc}@`);
    for (const fs of p.fams || []) lines.push(`1 FAMS @${fs}@`);
  }

  for (const f of Object.values(state.data.families)) {
    lines.push(`0 @${f.id}@ FAM`);
    if (f.husb) lines.push(`1 HUSB @${f.husb}@`);
    if (f.wife) lines.push(`1 WIFE @${f.wife}@`);
    if (f.marriageYear || f.marriagePlace) {
      lines.push("1 MARR");
      if (f.marriageYear) lines.push(`2 DATE (${f.marriageYear})`);
      if (f.marriagePlace) lines.push(`2 PLAC ${f.marriagePlace}`);
    }
    if (f.status) lines.push(`1 _MSTAT ${f.status}`);
    for (const cid of f.children || []) lines.push(`1 CHIL @${cid}@`);
  }

  lines.push("0 TRLR");
  return lines.join("\r\n");
}

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function wrapSvgTextLines(text, maxCharsPerLine = 17) {
  const clean = String(text || "").trim();
  if (clean.length <= maxCharsPerLine) return [clean];
  const words = clean.split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    if (!current) {
      current = w;
    } else if ((current + " " + w).length <= maxCharsPerLine) {
      current += " " + w;
    } else {
      lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  if (lines.length > 2) {
    return [lines[0], lines.slice(1).join(" ").slice(0, maxCharsPerLine + 2) + "…"];
  }
  return lines;
}

function downloadFocusedTreeChart() {
  if (!state.data) return;
  const focusedPerson = state.data.individuals[state.focusedId];
  if (!focusedPerson) return;

  const { nodes, marriages, brackets } = buildHourglassLayout(
    state.focusedId,
    state.ancestorDepth,
    state.descendantDepth
  );

  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;

  for (const n of nodes) {
    const halfW = (n.width || NODE_W) / 2;
    if (n.x - halfW < minX) minX = n.x - halfW;
    if (n.x + halfW > maxX) maxX = n.x + halfW;
    if (n.y - 44 < minY) minY = n.y - 44;
    if (n.y + 44 > maxY) maxY = n.y + 44;
  }

  if (!isFinite(minX)) {
    minX = -400;
    maxX = 400;
    minY = -300;
    maxY = 300;
  }

  const padX = 68;
  const headerH = 68;
  const padTop = headerH + 52;
  const padBottom = 60;

  const exportW = Math.max(920, Math.ceil(maxX - minX + padX * 2));
  const exportH = Math.max(580, Math.ceil(maxY - minY + padTop + padBottom));
  const offsetX = -minX + padX;
  const offsetY = -minY + padTop;

  const isDark = state.theme === "dark";
  const bgCanvas = isDark ? "#0f1715" : "#f4efe6";
  const gridDot = isDark ? "#23352f" : "#dcd3c2";
  const headerBg = isDark ? "#17221f" : "#ffffff";
  const headerBorder = isDark ? "#2d423b" : "#cbd5cf";
  const textPrimary = isDark ? "#f1f5f3" : "#1a2421";
  const textSecondary = isDark ? "#9fb1aa" : "#52635d";
  const accentPrimary = isDark ? "#34d399" : "#235340";
  const accentSoft = isDark ? "#1a382e" : "#e3efe9";
  const accentGold = "#d97706";
  const lineCollateral = isDark ? "#476359" : "#94a39c";
  const cardBorder = isDark ? "#2d423b" : "#c5d1ca";
  const dividerStroke = isDark ? "#243630" : "#e2ddd3";

  const svgParts = [];
  svgParts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${exportW}" height="${exportH}" viewBox="0 0 ${exportW} ${exportH}">`
  );
  svgParts.push(`
    <defs>
      <pattern id="dotGrid" width="24" height="24" patternUnits="userSpaceOnUse">
        <circle cx="12" cy="12" r="1.3" fill="${gridDot}" />
      </pattern>
      <linearGradient id="maleCardGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="${isDark ? "#17221f" : "#ffffff"}" />
        <stop offset="100%" stop-color="${isDark ? "#152538" : "#eef5ff"}" />
      </linearGradient>
      <linearGradient id="femaleCardGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="${isDark ? "#17221f" : "#ffffff"}" />
        <stop offset="100%" stop-color="${isDark ? "#2d1826" : "#fdf0f6"}" />
      </linearGradient>
      <linearGradient id="focusCardGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="${isDark ? "#23322c" : "#fffdf7"}" />
        <stop offset="100%" stop-color="${isDark ? "#2b2618" : "#fef3c7"}" />
      </linearGradient>
      <filter id="cardShadow" x="-8%" y="-8%" width="116%" height="124%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000000" flood-opacity="${isDark ? "0.38" : "0.09"}" />
      </filter>
    </defs>
  `);

  // Background
  svgParts.push(`<rect width="${exportW}" height="${exportH}" fill="${bgCanvas}" />`);
  svgParts.push(`<rect width="${exportW}" height="${exportH}" fill="url(#dotGrid)" />`);

  // Clean Heritage Header Bar (No search bar, no menus, no interactive buttons)
  const titleText =
    state.lang === "tr" ? "NİKSİÇ & HOCİÇ SOY AĞACI" : "NİKSİÇ & HOCİÇ FAMILY TREE";
  const focusLabel =
    state.lang === "tr"
      ? `Odak Kişi: ${focusedPerson.name} (${formatLifeYears(focusedPerson)})`
      : `Focused: ${focusedPerson.name} (${formatLifeYears(focusedPerson)})`;

  svgParts.push(`
    <g>
      <rect x="0" y="0" width="${exportW}" height="${headerH}" fill="${headerBg}" />
      <line x1="0" y1="${headerH}" x2="${exportW}" y2="${headerH}" stroke="${headerBorder}" stroke-width="1.5" />
      <rect x="28" y="16" width="36" height="36" rx="9" fill="${accentSoft}" />
      <circle cx="46" cy="31" r="9" fill="${accentPrimary}" opacity="0.85" />
      <path d="M46 36 V45 M41 45 H51" stroke="${accentPrimary}" stroke-width="2.2" stroke-linecap="round" />
      <text x="76" y="34" fill="${textPrimary}" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" letter-spacing="0.8">${escapeHtml(titleText)}</text>
      <text x="76" y="52" fill="${textSecondary}" font-family="'Segoe UI', Roboto, Arial, sans-serif" font-size="12.5" font-weight="600">${escapeHtml(focusLabel)}</text>
    </g>
  `);

  // 1. Marriage Bridges
  for (const m of marriages) {
    const x1 = m.x1 + offsetX;
    const x2 = m.x2 + offsetX;
    const y = m.y + offsetY;
    const isDivorced = m.status === "divorced";
    const strokeColor = isDivorced ? "#94a3b8" : "#16a34a";
    const dashAttr = isDivorced ? ' stroke-dasharray="6,4"' : "";
    svgParts.push(
      `<path d="M ${x1} ${y} L ${x2} ${y}" fill="none" stroke="${strokeColor}" stroke-width="2.8"${dashAttr} stroke-linecap="round" />`
    );
  }

  // 2. Orthogonal Family Brackets
  for (const b of brackets) {
    if (!b.children || b.children.length === 0) continue;
    const px = b.parentX + offsetX;
    const py = b.parentY + offsetY;
    const cy = b.childY + offsetY;
    const midY = b.midY + offsetY;

    const childXs = b.children.map((c) => c.x + offsetX);
    const barMinX = Math.min(px, ...childXs);
    const barMaxX = Math.max(px, ...childXs);

    const baseStroke = b.isDescendant ? accentPrimary : lineCollateral;
    const baseWidth = b.isDescendant ? "2.3" : "1.9";

    svgParts.push(
      `<path d="M ${px} ${py} V ${midY} M ${barMinX} ${midY} H ${barMaxX}" fill="none" stroke="${baseStroke}" stroke-width="${baseWidth}" stroke-linecap="round" stroke-linejoin="round" />`
    );

    for (const ch of b.children) {
      const cx = ch.x + offsetX;
      svgParts.push(
        `<path d="M ${cx} ${midY} V ${cy}" fill="none" stroke="${baseStroke}" stroke-width="${baseWidth}" stroke-linecap="round" />`
      );
    }

    if (!b.isDescendant) {
      for (const ch of b.children) {
        if (ch.isDirect) {
          const cx = ch.x + offsetX;
          svgParts.push(
            `<path d="M ${px} ${py} V ${midY} H ${cx} V ${cy}" fill="none" stroke="${accentPrimary}" stroke-width="2.7" stroke-linecap="round" stroke-linejoin="round" />`
          );
        }
      }
    }
  }

  // 3. Person Node Cards
  const cardH = 80;
  for (const n of nodes) {
    const cardW = n.width || NODE_W;
    const cx = n.x + offsetX;
    const cy = n.y + offsetY;
    const x = cx - cardW / 2;
    const y = cy - cardH / 2;

    const p = n.person;
    const isFemale = p.sex === "F";
    const accentColor = isFemale ? "#c02669" : "#2563eb";
    const avatarBg = isFemale ? "rgba(192, 38, 105, 0.14)" : "rgba(37, 99, 235, 0.14)";
    const fillUrl = n.isFocused ? "url(#focusCardGrad)" : isFemale ? "url(#femaleCardGrad)" : "url(#maleCardGrad)";
    const borderStroke = n.isFocused
      ? accentGold
      : n.isDirectAncestor
        ? accentPrimary
        : cardBorder;
    const borderWidth = n.isFocused ? "2.5" : "1.5";

    const nameLines = wrapSvgTextLines(p.given || p.name, n.isFocused ? 16 : 18);
    const surname = (p.surname || "").trim();
    const lifeText = formatLifeYears(p);

    svgParts.push(`<g transform="translate(${x.toFixed(1)}, ${y.toFixed(1)})">`);
    svgParts.push(
      `<rect width="${cardW}" height="${cardH}" rx="11" fill="${fillUrl}" stroke="${borderStroke}" stroke-width="${borderWidth}" filter="url(#cardShadow)" />`
    );
    // Left gender accent bar
    svgParts.push(
      `<path d="M 10 0 H 4.5 A 4.5 4.5 0 0 0 0 4.5 V ${cardH - 4.5} A 4.5 4.5 0 0 0 4.5 ${cardH} H 10 Z" fill="${n.isFocused ? accentGold : accentColor}" />`
    );

    // Gender Avatar Silhouette Box
    const isDeceased = Boolean(p.deceased);
    if (isDeceased) {
      svgParts.push(`<rect x="11" y="9" width="22" height="22" rx="6" fill="${avatarBg}" stroke="${accentColor}" stroke-width="1" stroke-dasharray="2.5 2.5" opacity="0.8" />`);
      svgParts.push(`<circle cx="22" cy="16.5" r="3.4" fill="none" stroke="${accentColor}" stroke-width="1.5" />`);
      svgParts.push(
        `<path d="M 15.5 27 C 15.5 22.8, 28.5 22.8, 28.5 27" fill="none" stroke="${accentColor}" stroke-width="1.5" stroke-linecap="round" />`
      );
    } else {
      svgParts.push(`<rect x="11" y="9" width="22" height="22" rx="6" fill="${avatarBg}" stroke="${accentColor}" stroke-width="0.8" opacity="0.9" />`);
      svgParts.push(`<circle cx="22" cy="16.5" r="3.6" fill="${accentColor}" />`);
      svgParts.push(
        `<path d="M 15.5 27 C 15.5 22.8, 28.5 22.8, 28.5 27" fill="${accentColor}" />`
      );
    }

    // Focused Star
    if (n.isFocused) {
      svgParts.push(
        `<text x="${cardW - 18}" y="22" font-family="'Segoe UI', Arial, sans-serif" font-size="13" fill="${accentGold}" font-weight="700">★</text>`
      );
    }

    // Name lines
    if (nameLines.length === 1) {
      svgParts.push(
        `<text x="39" y="21" fill="${textPrimary}" font-family="'Segoe UI', Roboto, Arial, sans-serif" font-size="12.5" font-weight="700">${escapeHtml(nameLines[0])}</text>`
      );
    } else {
      svgParts.push(
        `<text x="39" y="18" fill="${textPrimary}" font-family="'Segoe UI', Roboto, Arial, sans-serif" font-size="11.5" font-weight="700">${escapeHtml(nameLines[0])}</text>`
      );
      svgParts.push(
        `<text x="39" y="31" fill="${textPrimary}" font-family="'Segoe UI', Roboto, Arial, sans-serif" font-size="11.5" font-weight="700">${escapeHtml(nameLines[1])}</text>`
      );
    }

    // Surname badge
    if (surname) {
      const surnameY = nameLines.length === 1 ? 26 : 35;
      const badgeW = Math.min(cardW - 46, Math.max(38, surname.length * 6.5 + 12));
      svgParts.push(
        `<rect x="39" y="${surnameY}" width="${badgeW.toFixed(1)}" height="15" rx="4" fill="${accentSoft}" />`
      );
      svgParts.push(
        `<text x="45" y="${surnameY + 11}" fill="${accentPrimary}" font-family="'Segoe UI', Roboto, Arial, sans-serif" font-size="9.5" font-weight="700">${escapeHtml(surname)}</text>`
      );
    }

    // Bottom Divider & Lifespan + Age
    svgParts.push(
      `<line x1="11" y1="56" x2="${cardW - 10}" y2="56" stroke="${dividerStroke}" stroke-width="1" />`
    );
    svgParts.push(
      `<text x="12" y="71" fill="${textSecondary}" font-family="'Segoe UI', Roboto, Arial, sans-serif" font-size="11" font-weight="600">${escapeHtml(lifeText)}</text>`
    );
    svgParts.push(`</g>`);
  }

  // 4. Marriage Connector Badges
  for (const m of marriages) {
    const mx = m.midX + offsetX;
    const my = m.y + offsetY;
    const isDivorced = m.status === "divorced";
    if (isDivorced) {
      svgParts.push(`
        <g transform="translate(${mx.toFixed(1)}, ${my.toFixed(1)})">
          <circle cx="0" cy="0" r="11.5" fill="${headerBg}" stroke="#94a3b8" stroke-width="1.6" />
          <line x1="-5" y1="5" x2="5" y2="-5" stroke="#ef4444" stroke-width="2" stroke-linecap="round" />
        </g>
      `);
    } else {
      svgParts.push(`
        <g transform="translate(${mx.toFixed(1)}, ${my.toFixed(1)})">
          <circle cx="0" cy="0" r="11.5" fill="${headerBg}" stroke="#16a34a" stroke-width="1.6" />
          <circle cx="-2.8" cy="0" r="3.6" fill="none" stroke="#15803d" stroke-width="1.4" />
          <circle cx="2.8" cy="0" r="3.6" fill="none" stroke="#15803d" stroke-width="1.4" />
        </g>
      `);
    }
  }

  svgParts.push(`</svg>`);
  const svgMarkup = svgParts.join("\n");

  const cleanPersonName = focusedPerson.name
    .replace(/[\\/:*?"<>|]+/g, "")
    .trim()
    .replace(/\s+/g, " ");
  const fileSuffix = state.lang === "tr" ? "Soy Ağacı" : "Family Tree";
  const baseFilename = `${cleanPersonName} - ${fileSuffix}`;

  // Render standalone SVG to 2x Retina High-Res PNG for universal viewing & sharing
  const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);
  const img = new Image();

  img.onload = () => {
    try {
      const maxDim = Math.max(exportW, exportH);
      const dpr = Math.min(2, Math.max(1, 7680 / maxDim));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(exportW * dpr);
      canvas.height = Math.round(exportH * dpr);
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.drawImage(img, 0, 0, exportW, exportH);
        canvas.toBlob((pngBlob) => {
          URL.revokeObjectURL(svgUrl);
          if (pngBlob) {
            const pngUrl = URL.createObjectURL(pngBlob);
            const a = document.createElement("a");
            a.href = pngUrl;
            a.download = `${baseFilename}.png`;
            a.click();
            URL.revokeObjectURL(pngUrl);
          } else {
            downloadBlob(svgMarkup, `${baseFilename}.svg`, "image/svg+xml;charset=utf-8");
          }
        }, "image/png");
        return;
      }
    } catch (_) {}
    URL.revokeObjectURL(svgUrl);
    downloadBlob(svgMarkup, `${baseFilename}.svg`, "image/svg+xml;charset=utf-8");
  };

  img.onerror = () => {
    URL.revokeObjectURL(svgUrl);
    downloadBlob(svgMarkup, `${baseFilename}.svg`, "image/svg+xml;charset=utf-8");
  };

  img.src = svgUrl;
}

function renderManageView() {
  renderDeviceSessions();
  renderCloudSnapshots();

  const listEl = document.getElementById("audit-log-list");
  if (!listEl) return;
  if (!state.auditLog || state.auditLog.length === 0) {
    listEl.innerHTML = `<div class="section-subtitle">Henüz kayıtlı bir düzenleme işlemi bulunmuyor.</div>`;
    return;
  }

  listEl.innerHTML = state.auditLog
    .map(
      (item) => `
      <div class="root-rank-item">
        <div>
          <div class="sr-name">${escapeHtml(item.summary || item.action)}</div>
          <div class="sr-sub">${escapeHtml(item.editor_name || "Aile Editörü")} • ${escapeHtml(item.created_at || "")}</div>
        </div>
        ${
          item.action !== "UNDO"
            ? `<button type="button" class="chip-btn undo-audit-btn" data-audit-id="${item.id}">↩ Geri Al</button>`
            : `<span class="chip-btn">Geri Alındı</span>`
        }
      </div>
    `
    )
    .join("");

  listEl.querySelectorAll(".undo-audit-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      requireEditorThen(async () => {
        const res = await apiRequest(`undo/${btn.dataset.auditId}`, "POST");
        if (res.ok) renderManageView();
      });
    });
  });
}

async function renderDeviceSessions() {
  const infoEl = document.getElementById("device-session-info");
  const listEl = document.getElementById("active-sessions-list");
  if (!infoEl || !listEl) return;

  const currentDevice = getDeviceSummary();
  const hasSavedKey = Boolean(localStorage.getItem(STORAGE_KEY_MASTER));
  const hasToken = Boolean(state.sessionToken);

  infoEl.innerHTML = `
    <div style="background: var(--bg-primary); border: 1px solid var(--border-subtle); border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 0.5rem; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem;">
      <div>
        <div style="font-weight: 600; color: var(--text-primary); display: flex; align-items: center; gap: 0.4rem;">
          <span>📱 Bu Cihaz:</span> <span>${escapeHtml(currentDevice)}</span>
          <span class="gender-pill" style="font-size: 0.75rem; background: rgba(46, 125, 50, 0.12); color: #2e7d32; border: 1px solid rgba(46, 125, 50, 0.3);">
            ${state.role === "editor" ? "Editör Yetkili" : "İzleyici"}
          </span>
        </div>
        <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.2rem;">
          ${hasToken ? "✓ 1 Yıl Süreli Güvenli Cihaz Oturumu Açık (Şifresiz Otomatik Giriş)" : "Geçici Oturum"}
          ${hasSavedKey ? " • Çevrimdışı Kriptografik Anahtar Kayıtlı" : ""}
        </div>
      </div>
      <div style="font-size: 0.75rem; color: #2e7d32; display: flex; align-items: center; gap: 0.3rem;">
        <span>🔒 Şifreniz tarayıcıda asla düz metin olarak saklanmaz</span>
      </div>
    </div>
  `;

  if (state.role !== "editor") {
    listEl.innerHTML = `<div class="sr-sub" style="padding: 0.25rem 0;">Diğer kayıtlı cihazları görüntülemek ve yönetmek için Editör modu gereklidir.</div>`;
    return;
  }

  try {
    listEl.innerHTML = `<div class="sr-sub" style="padding: 0.5rem 0;">Cihaz listesi yükleniyor...</div>`;
    const res = await fetch("/api/sessions", { headers: getApiHeaders() });
    if (!res.ok) {
      listEl.innerHTML = `<div class="sr-sub" style="padding: 0.5rem 0;">Cihaz listesi yalnızca editörler tarafından görüntülenebilir.</div>`;
      return;
    }
    const data = await res.json();
    const sessions = data.sessions || [];
    if (sessions.length === 0) {
      listEl.innerHTML = `<div class="sr-sub" style="padding: 0.5rem 0;">Kayıtlı başka cihaz oturumu bulunmuyor.</div>`;
      return;
    }

    listEl.innerHTML = sessions
      .map((s) => {
        const isCurrent = s.id === state.sessionToken;
        const roleLabel = s.role === "editor" ? "Editör" : "İzleyici";
        const roleStyle = s.role === "editor" ? "color: #b78103; font-weight: 600;" : "color: var(--text-secondary);";
        const lastActive = s.last_used_at ? s.last_used_at.replace("T", " ").slice(0, 16) : s.created_at;
        return `
        <div class="root-rank-item" style="cursor: default;">
          <div>
            <div class="sr-name" style="display: flex; align-items: center; gap: 0.4rem;">
              <span>${escapeHtml(s.device_name || "Bilinmeyen Cihaz")}</span>
              ${isCurrent ? `<span class="gender-pill" style="font-size: 0.7rem; background: rgba(30, 136, 229, 0.12); color: #1e88e5;">Bu Cihaz</span>` : ""}
              <span style="font-size: 0.75rem; ${roleStyle}">(${roleLabel})</span>
            </div>
            <div class="sr-sub">Son İşlem: ${escapeHtml(lastActive)} • Bitiş: 365 Gün Süreli</div>
          </div>
          ${
            !isCurrent
              ? `<button type="button" class="chip-btn revoke-session-btn" data-session-id="${escapeHtml(s.id)}" style="color: #c62828;">Yetkiyi Kaldır</button>`
              : `<span class="chip-btn" style="opacity: 0.7;">Mevcut</span>`
          }
        </div>
      `;
      })
      .join("");

    listEl.querySelectorAll(".revoke-session-btn").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const sId = btn.dataset.sessionId;
        if (!confirm("Bu cihazın oturumunu sonlandırmak istiyor musunuz? Tekrar giriş yapana kadar soy ağacını açamayacaktır.")) {
          return;
        }
        btn.disabled = true;
        btn.textContent = "Kaldırılıyor...";
        const revRes = await fetch("/api/sessions/revoke", {
          method: "POST",
          headers: getApiHeaders(),
          body: JSON.stringify({ sessionId: sId }),
        });
        if (revRes.ok) {
          renderDeviceSessions();
        } else {
          alert("Oturum iptal edilemedi.");
          btn.disabled = false;
          btn.textContent = "Yetkiyi Kaldır";
        }
      });
    });
  } catch (_) {
    listEl.innerHTML = `<div class="sr-sub" style="padding: 0.5rem 0;">Cihaz oturumları listelenemedi (yerel sunucu).</div>`;
  }
}

async function renderCloudSnapshots() {
  const listEl = document.getElementById("cloud-snapshots-list");
  if (!listEl) return;

  try {
    const res = await fetch("/api/snapshots", { headers: getApiHeaders() });
    if (!res.ok) {
      listEl.innerHTML = `<div class="sr-sub" style="padding: 0.5rem 0;">Bulut yedekleri alınamadı (${res.status}).</div>`;
      return;
    }
    const data = await res.json();
    const snapshots = data.snapshots || [];
    if (snapshots.length === 0) {
      listEl.innerHTML = `<div class="sr-sub" style="padding: 0.5rem 0;">Henüz kaydedilmiş bulut yedeği bulunmuyor.</div>`;
      return;
    }

    listEl.innerHTML = snapshots
      .map((s) => {
        const triggerLabel =
          s.trigger_type === "auto_milestone"
            ? "🤖 Otomatik Dönüm Noktası"
            : s.trigger_type === "pre_restore_safety"
            ? "🛡️ Geri Yükleme Emniyeti"
            : "📸 Manuel Yedek";
        return `
        <div class="root-rank-item" style="padding: 0.75rem 0; border-bottom: 1px solid var(--border-subtle); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem;">
          <div>
            <div class="sr-name" style="display: flex; align-items: center; gap: 0.4rem;">
              <span>#${s.id}</span>
              <span style="font-size: 0.82rem; font-weight: normal; color: var(--text-secondary);">${escapeHtml(s.notes || triggerLabel)}</span>
            </div>
            <div class="sr-sub" style="margin-top: 0.2rem;">
              ${s.total_individuals || 0} Kişi • ${s.total_families || 0} Aile • ${escapeHtml(s.created_by || "Sistem")} • ${escapeHtml(s.created_at || "")}
            </div>
          </div>
          <div style="display: flex; gap: 0.4rem;">
            <button type="button" class="chip-btn download-snapshot-btn" data-snap-id="${s.id}">⬇ İndir (.json)</button>
            <button type="button" class="chip-btn restore-snapshot-btn" data-snap-id="${s.id}" data-snap-desc="${escapeHtml(s.notes || `#${s.id}`)}" style="color: #b91c1c; border-color: rgba(185, 28, 28, 0.3);">↺ Geri Yükle</button>
          </div>
        </div>
      `;
      })
      .join("");

    listEl.querySelectorAll(".download-snapshot-btn").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const sId = btn.dataset.snapId;
        const origText = btn.textContent;
        btn.textContent = "İndiriliyor...";
        try {
          const sRes = await fetch(`/api/snapshots/${sId}`, { headers: getApiHeaders() });
          if (sRes.ok) {
            const sData = await sRes.json();
            const blob = new Blob([JSON.stringify(sData.snapshot.data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `niksic-hocic-snapshot-${sId}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }
        } catch (_) {}
        btn.textContent = origText;
      });
    });

    listEl.querySelectorAll(".restore-snapshot-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const sId = btn.dataset.snapId;
        const desc = btn.dataset.snapDesc;
        requireEditorThen(async () => {
          if (!confirm(`DİKKAT: #${sId} (${desc}) numaralı bulut yedeğini geri yüklemek istediğinize emin misiniz?\n\nBu işlem öncesinde mevcut canlı veritabanınızın otomatik emniyet yedeği alınacaktır.`)) {
            return;
          }
          btn.textContent = "Geri Yükleniyor...";
          const res = await apiRequest("snapshots/restore", "POST", { snapshotId: sId });
          if (res.ok) {
            alert(`Bulut yedeği #${sId} başarıyla geri yüklendi!`);
            renderManageView();
          } else {
            alert(`Geri yükleme başarısız: ${res.error || "Hata"}`);
          }
          btn.textContent = "↺ Geri Yükle";
        });
      });
    });
  } catch (_) {
    listEl.innerHTML = `<div class="sr-sub" style="padding: 0.5rem 0;">Bulut yedekleri listelenemedi.</div>`;
  }
}

/* ================= PAN, PINCH-ZOOM, SEMANTIC ZOOM & MINI-MAP CONTROLS ================= */
function applyStageTransform() {
  const stage = document.getElementById("tree-stage");
  if (!stage) return;
  stage.style.transform = `translate(${state.panX}px, ${state.panY}px) scale(${state.scale})`;

  // Semantic Zoom Tier for crisp readability at any zoom level
  const zoomTier = state.scale < 0.55 ? "far" : state.scale < 0.88 ? "mid" : "close";
  if (stage.getAttribute("data-zoom-tier") !== zoomTier) {
    stage.setAttribute("data-zoom-tier", zoomTier);
  }

  // Sync Zoom Slider & Percentage Readout
  const pct = Math.round(state.scale * 100);
  const slider = document.getElementById("zoom-slider");
  if (slider) {
    slider.value = String(Math.min(Math.max(pct, 12), 180));
  }
  const pctLbl = document.getElementById("zoom-pct-label");
  if (pctLbl) {
    pctLbl.textContent = `${pct}%`;
  }

  updateMinimapViewport();
}

function renderMinimapNodes() {
  const mmSvg = document.getElementById("minimap-svg");
  if (!mmSvg) return;
  const { width, height } = state.treeBounds;
  if (!width || !height || !state.layoutNodes || state.layoutNodes.length === 0) {
    mmSvg.innerHTML = "";
    return;
  }

  const mmW = 200;
  const mmH = 130;
  const scale = Math.min(mmW / width, mmH / height);
  const offX = (mmW - width * scale) / 2;
  const offY = (mmH - height * scale) / 2;

  const rects = state.layoutNodes.map((n) => {
    const rx = offX + n.x * scale;
    const ry = offY + n.y * scale;
    const rw = Math.max(4.5, 190 * scale);
    const rh = Math.max(3, 76 * scale);
    const fill = n.isFocused ? "#d97706" : n.sex === "F" ? "#c02669" : "#2563eb";
    const stroke = n.isFocused ? "#ffffff" : "none";
    return `<rect x="${(rx - rw / 2).toFixed(1)}" y="${(ry - rh / 2).toFixed(1)}" width="${rw.toFixed(1)}" height="${rh.toFixed(1)}" rx="1.5" fill="${fill}" stroke="${stroke}" stroke-width="1" opacity="${n.isCollateral ? "0.72" : "0.95"}" />`;
  });

  mmSvg.innerHTML = rects.join("");
  updateMinimapViewport();
}

function updateMinimapViewport() {
  const mmEl = document.getElementById("tree-minimap");
  const vpRectEl = document.getElementById("minimap-viewport-rect");
  const vp = document.getElementById("tree-viewport");
  if (!mmEl || !vpRectEl || !vp) return;

  const { width, height } = state.treeBounds;
  if (!width || !height) return;

  const vpBox = vp.getBoundingClientRect();
  const mmBox = mmEl.getBoundingClientRect();
  if (!vpBox.width || !mmBox.width) return;

  const fitScale = Math.min(mmBox.width / width, mmBox.height / height);
  const offX = (mmBox.width - width * fitScale) / 2;
  const offY = (mmBox.height - height * fitScale) / 2;

  // Visible region in tree stage coordinates:
  const visLeft = -state.panX / state.scale;
  const visTop = -state.panY / state.scale;
  const visW = vpBox.width / state.scale;
  const visH = vpBox.height / state.scale;

  const rx = Math.max(0, offX + visLeft * fitScale);
  const ry = Math.max(0, offY + visTop * fitScale);
  const rw = Math.min(mmBox.width - rx, Math.max(14, visW * fitScale));
  const rh = Math.min(mmBox.height - ry, Math.max(10, visH * fitScale));

  vpRectEl.style.left = `${rx}px`;
  vpRectEl.style.top = `${ry}px`;
  vpRectEl.style.width = `${rw}px`;
  vpRectEl.style.height = `${rh}px`;
}

function applyFanTransform() {
  const stage = document.getElementById("fan-stage");
  if (stage) {
    stage.style.transform = `translate(${state.fanPanX}px, ${state.fanPanY}px) scale(${state.fanScale})`;
  }
}

function resetFanZoom() {
  state.fanPanX = 0;
  state.fanPanY = 0;
  state.fanScale = 1;
  applyFanTransform();
}

function zoomFanByFactor(factor) {
  state.fanScale = Math.min(Math.max(state.fanScale * factor, 0.45), 3.2);
  applyFanTransform();
}

function smartFocusCamera() {
  const vp = document.getElementById("tree-viewport");
  const rect = vp.getBoundingClientRect();
  const { width, height } = state.treeBounds;
  if (!width || !height || !rect.width) return;

  const isMobile = window.innerWidth <= 768;
  const pad = isMobile ? 16 : 44;
  const fullScaleX = (rect.width - pad) / width;
  const fullScaleY = (rect.height - pad) / height;
  const fullFitScale = Math.min(fullScaleX, fullScaleY);

  // 1. If the entire family tree fits at a clear overview scale on this device, fit 100% of the tree!
  const minFullFitThreshold = isMobile ? 0.24 : 0.34;
  if (fullFitScale >= minFullFitThreshold) {
    state.scale = Math.min(fullFitScale, isMobile ? 0.85 : 0.92);
    state.panX = (rect.width - width * state.scale) / 2;
    state.panY = (rect.height - height * state.scale) / 2;
    applyStageTransform();
    return;
  }

  // 2. For ultra-wide trees (e.g., many collateral aunts/uncles on a narrow mobile screen),
  // compute the bounding box of the Core Family Cluster around the focused person so 4–5 generations
  // auto-scale to fill the device screen intelligently.
  const fx = state.focusedNodeCoords.x;
  const fy = state.focusedNodeCoords.y;
  const maxHorizSpan = isMobile ? 540 : 950;
  const coreNodes = (state.layoutNodes || []).filter((n) => {
    const dy = n.y - fy;
    const dx = Math.abs(n.x - fx);
    if (n.isFocused || n.isSpouse) return true;
    if (n.isDirectAncestor) return true;
    if (!n.isCollateral && dy >= -1600 && dy <= 450 && dx <= maxHorizSpan) return true;
    if (n.isCollateral && dy >= -480 && dy <= 180 && dx <= maxHorizSpan) return true;
    return false;
  });

  if (coreNodes.length > 1) {
    let cMinX = Infinity,
      cMaxX = -Infinity,
      cMinY = Infinity,
      cMaxY = -Infinity;
    for (const cn of coreNodes) {
      const hw = (cn.width || 196) / 2 + 16;
      if (cn.x - hw < cMinX) cMinX = cn.x - hw;
      if (cn.x + hw > cMaxX) cMaxX = cn.x + hw;
      if (cn.y - 52 < cMinY) cMinY = cn.y - 52;
      if (cn.y + 52 > cMaxY) cMaxY = cn.y + 52;
    }
    const clusterW = Math.max(320, cMaxX - cMinX);
    const clusterH = Math.max(260, cMaxY - cMinY);
    const clusterScale = Math.min((rect.width - pad) / clusterW, (rect.height - pad) / clusterH);
    state.scale = isMobile
      ? Math.min(Math.max(clusterScale, 0.60), 0.85)
      : Math.min(Math.max(clusterScale, 0.45), 0.82);

    const clusterCenterX = (cMinX + cMaxX) / 2;
    const clusterCenterY = (cMinY + cMaxY) / 2;
    state.panX = rect.width / 2 - clusterCenterX * state.scale;
    state.panY = (isMobile ? rect.height * 0.46 : rect.height / 2) - clusterCenterY * state.scale;
  } else {
    state.scale = isMobile ? 0.68 : 0.78;
    state.panX = rect.width / 2 - fx * state.scale;
    state.panY = (isMobile ? rect.height * 0.46 : rect.height * 0.55) - fy * state.scale;
  }

  applyStageTransform();
}

function centerOnFocusedNode() {
  const vp = document.getElementById("tree-viewport");
  const rect = vp.getBoundingClientRect();
  const isMobile = window.innerWidth <= 768;
  state.scale = isMobile ? Math.min(Math.max(state.scale, 0.68), 0.95) : Math.min(Math.max(state.scale, 0.78), 1.05);
  state.panX = rect.width / 2 - state.focusedNodeCoords.x * state.scale;
  state.panY = (isMobile ? rect.height * 0.46 : rect.height / 2) - state.focusedNodeCoords.y * state.scale;
  applyStageTransform();
}

function zoomFitTree() {
  const vp = document.getElementById("tree-viewport");
  const rect = vp.getBoundingClientRect();
  const { width, height } = state.treeBounds;
  if (!width || !height) return;

  const isMobile = window.innerWidth <= 768;
  const pad = isMobile ? 20 : 44;
  const scaleX = (rect.width - pad) / width;
  const scaleY = (rect.height - pad) / height;
  const minScale = isMobile ? 0.32 : 0.18;
  state.scale = Math.min(Math.max(Math.min(scaleX, scaleY), minScale), 1.15);
  state.panX = (rect.width - width * state.scale) / 2;
  state.panY = (rect.height - height * state.scale) / 2;
  applyStageTransform();
}

function setZoomScale(targetScale: number, clientX?: number, clientY?: number) {
  const vp = document.getElementById("tree-viewport");
  const rect = vp.getBoundingClientRect();
  const cx = clientX !== undefined ? clientX - rect.left : rect.width / 2;
  const cy = clientY !== undefined ? clientY - rect.top : rect.height / 2;

  const prevScale = state.scale;
  const nextScale = Math.min(Math.max(targetScale, 0.12), 2.5);
  const ratio = nextScale / prevScale;

  state.panX = cx - (cx - state.panX) * ratio;
  state.panY = cy - (cy - state.panY) * ratio;
  state.scale = nextScale;
  applyStageTransform();
}

function zoomByFactor(factor: number, clientX?: number, clientY?: number) {
  setZoomScale(state.scale * factor, clientX, clientY);
}

function initViewportInteractions() {
  // 1. Tree Viewport Multi-Touch Pinch-to-Zoom & Pan
  const vp = document.getElementById("tree-viewport");
  const pointers = new Map();
  let lastPinchDist = 0;
  let lastPinchMid = null;
  let lastTapTime = 0;

  vp.addEventListener("pointerdown", (e) => {
    if (
      e.target.closest(".person-node") ||
      e.target.closest(".canvas-hud-dock") ||
      e.target.closest(".canvas-hud") ||
      e.target.closest(".spouse-connector-badge")
    ) {
      return;
    }

    // Double-tap on empty canvas zooms in smoothly on mobile
    const now = Date.now();
    if (pointers.size === 0 && now - lastTapTime < 280) {
      zoomByFactor(1.35, e.clientX, e.clientY);
      lastTapTime = 0;
      return;
    }
    lastTapTime = now;

    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try {
      vp.setPointerCapture(e.pointerId);
    } catch (_) {}

    if (pointers.size === 1) {
      state.isDragging = true;
      state.dragStartX = e.clientX - state.panX;
      state.dragStartY = e.clientY - state.panY;
      vp.classList.add("dragging");
    } else if (pointers.size === 2) {
      state.isDragging = false;
      const [p1, p2] = Array.from(pointers.values());
      lastPinchDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      lastPinchMid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    }
  });

  vp.addEventListener("pointermove", (e) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.size === 2) {
      const [p1, p2] = Array.from(pointers.values());
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

      if (lastPinchDist > 8) {
        const factor = dist / lastPinchDist;
        zoomByFactor(factor, mid.x, mid.y);
      }
      if (lastPinchMid) {
        state.panX += mid.x - lastPinchMid.x;
        state.panY += mid.y - lastPinchMid.y;
        applyStageTransform();
      }
      lastPinchDist = dist;
      lastPinchMid = mid;
    } else if (pointers.size === 1 && state.isDragging) {
      state.panX = e.clientX - state.dragStartX;
      state.panY = e.clientY - state.dragStartY;
      applyStageTransform();
    }
  });

  const endTreePointer = (e) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) {
      lastPinchDist = 0;
      lastPinchMid = null;
    }
    if (pointers.size === 1) {
      const remaining = Array.from(pointers.values())[0];
      state.isDragging = true;
      state.dragStartX = remaining.x - state.panX;
      state.dragStartY = remaining.y - state.panY;
    } else if (pointers.size === 0) {
      state.isDragging = false;
      vp.classList.remove("dragging");
    }
  };

  vp.addEventListener("pointerup", endTreePointer);
  vp.addEventListener("pointercancel", endTreePointer);

  vp.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
      zoomByFactor(e.deltaY < 0 ? 1.12 : 0.89, e.clientX, e.clientY);
    },
    { passive: false }
  );

  // 1b. Interactive Mini-Map Click & Drag Navigation
  const mmEl = document.getElementById("tree-minimap");
  if (mmEl) {
    let mmDragging = false;
    const panFromMinimapEvent = (e) => {
      const { width, height } = state.treeBounds;
      if (!width || !height) return;
      const mmBox = mmEl.getBoundingClientRect();
      const vpBox = vp.getBoundingClientRect();
      const fitScale = Math.min(mmBox.width / width, mmBox.height / height);
      const offX = (mmBox.width - width * fitScale) / 2;
      const offY = (mmBox.height - height * fitScale) / 2;

      const stageX = (e.clientX - mmBox.left - offX) / fitScale;
      const stageY = (e.clientY - mmBox.top - offY) / fitScale;

      state.panX = vpBox.width / 2 - stageX * state.scale;
      state.panY = vpBox.height / 2 - stageY * state.scale;
      applyStageTransform();
    };

    mmEl.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
      mmDragging = true;
      try {
        mmEl.setPointerCapture(e.pointerId);
      } catch (_) {}
      panFromMinimapEvent(e);
    });

    mmEl.addEventListener("pointermove", (e) => {
      if (!mmDragging) return;
      e.stopPropagation();
      panFromMinimapEvent(e);
    });

    const stopMmDrag = () => {
      mmDragging = false;
    };
    mmEl.addEventListener("pointerup", stopMmDrag);
    mmEl.addEventListener("pointercancel", stopMmDrag);
  }

  // 2. Fan Chart Multi-Touch Pinch-to-Zoom & Pan
  const fanWrap = document.getElementById("fan-canvas-wrap");
  if (fanWrap) {
    const fanPointers = new Map();
    let fanDragging = false;
    let fanStartX = 0;
    let fanStartY = 0;
    let fanPinchDist = 0;

    fanWrap.addEventListener("pointerdown", (e) => {
      if (e.target.closest(".canvas-hud") || e.target.closest(".fan-sector") || e.target.closest(".fan-legend")) return;
      fanPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try {
        fanWrap.setPointerCapture(e.pointerId);
      } catch (_) {}

      if (fanPointers.size === 1) {
        fanDragging = true;
        fanStartX = e.clientX - state.fanPanX;
        fanStartY = e.clientY - state.fanPanY;
        fanWrap.classList.add("dragging");
      } else if (fanPointers.size === 2) {
        fanDragging = false;
        const [p1, p2] = Array.from(fanPointers.values());
        fanPinchDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      }
    });

    fanWrap.addEventListener("pointermove", (e) => {
      if (!fanPointers.has(e.pointerId)) return;
      fanPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (fanPointers.size === 2) {
        const [p1, p2] = Array.from(fanPointers.values());
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        if (fanPinchDist > 8) {
          zoomFanByFactor(dist / fanPinchDist);
        }
        fanPinchDist = dist;
      } else if (fanPointers.size === 1 && fanDragging) {
        state.fanPanX = e.clientX - fanStartX;
        state.fanPanY = e.clientY - fanStartY;
        applyFanTransform();
      }
    });

    const endFanPointer = (e) => {
      fanPointers.delete(e.pointerId);
      if (fanPointers.size < 2) fanPinchDist = 0;
      if (fanPointers.size === 1) {
        const rem = Array.from(fanPointers.values())[0];
        fanDragging = true;
        fanStartX = rem.x - state.fanPanX;
        fanStartY = rem.y - state.fanPanY;
      } else if (fanPointers.size === 0) {
        fanDragging = false;
        fanWrap.classList.remove("dragging");
      }
    };

    fanWrap.addEventListener("pointerup", endFanPointer);
    fanWrap.addEventListener("pointercancel", endFanPointer);

    fanWrap.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        zoomFanByFactor(e.deltaY < 0 ? 1.12 : 0.89);
      },
      { passive: false }
    );
  }

  // 3. Mobile Bottom-Sheet Drawer Swipe-Down-to-Close
  const drawer = document.getElementById("person-drawer");
  const dragHandle = document.getElementById("drawer-drag-handle");
  if (drawer && dragHandle) {
    let startY = 0;
    let currentDeltaY = 0;
    let swiping = false;

    dragHandle.addEventListener("pointerdown", (e) => {
      if (window.innerWidth > 768) return;
      swiping = true;
      startY = e.clientY;
      currentDeltaY = 0;
      drawer.style.transition = "none";
      try {
        dragHandle.setPointerCapture(e.pointerId);
      } catch (_) {}
    });

    dragHandle.addEventListener("pointermove", (e) => {
      if (!swiping) return;
      currentDeltaY = Math.max(0, e.clientY - startY);
      drawer.style.transform = `translateY(${currentDeltaY}px)`;
    });

    const finishSwipe = () => {
      if (!swiping) return;
      swiping = false;
      drawer.style.transition = "";
      drawer.style.transform = "";
      if (currentDeltaY > 95) {
        closePersonDrawer();
      }
    };

    dragHandle.addEventListener("pointerup", finishSwipe);
    dragHandle.addEventListener("pointercancel", finishSwipe);
  }
}

/* ================= ARCHIVE SYSTEM (BELGE & YAZIŞMA ARŞİVİ) ================= */
// Modularized in ./views/archive.ts

/* ================= VIEW SWITCHING & GLOBAL REFRESH ================= */
function refreshActiveViews(autoFitTree = false) {
  if (!state.data) return;
  document.getElementById("header-meta-stats").textContent =
    state.lang === "tr"
      ? `${state.data.meta.totalIndividuals} Kişi • ${state.data.meta.totalFamilies} Aile`
      : `${state.data.meta.totalIndividuals} People • ${state.data.meta.totalFamilies} Families`;

  renderBranchPills();
  populateSurnameFilter();
  renderTree(autoFitTree);
  if (state.activeView === "fan") renderFanChart();
  if (state.activeView === "map") renderMigrationMap();
  if (state.activeView === "directory") renderDirectory();
  if (state.activeView === "timeline") renderTimeline();
  if (state.activeView === "kinship") renderKinshipView();
  if (state.activeView === "stats") renderStats();
  if (state.activeView === "manage") renderManageView();
  if (state.activeView === "archive") renderArchiveView();
}

function switchView(viewName, updateHash = true) {
  state.activeView = viewName;
  if (updateHash && window.location.hash !== `#view=${viewName}`) {
    history.replaceState(null, "", `#view=${viewName}`);
  }

  document.querySelectorAll(".nav-tab").forEach((btn) => {
    const isActive = btn.dataset.view === viewName;
    btn.classList.toggle("active", isActive);
    if (isActive && window.innerWidth <= 768) {
      btn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  });

  ["tree", "fan", "map", "directory", "timeline", "kinship", "stats", "manage", "archive"].forEach((v) => {
    const sec = document.getElementById(`view-${v}`);
    if (sec) {
      sec.classList.toggle("hidden", v !== viewName);
      sec.classList.toggle("active", v === viewName);
    }
  });

  refreshActiveViews(false);
}

function applyTheme(theme) {
  state.theme = theme;
  document.documentElement.setAttribute("data-theme", theme);
  localStorage.setItem(STORAGE_KEY_THEME, theme);
  const icon = document.getElementById("theme-icon");
  if (icon) icon.textContent = theme === "dark" ? "☀️" : "🌙";
}

function applyLanguage(lang) {
  state.lang = lang;
  document.documentElement.lang = lang;
  localStorage.setItem(STORAGE_KEY_LANG, lang);
  const lbl = document.getElementById("current-lang-label");
  if (lbl) lbl.textContent = lang.toUpperCase();

  document.querySelectorAll("[data-i18n]").forEach((el) => {
    const key = el.getAttribute("data-i18n");
    if (key) el.textContent = t(key);
  });

  document.querySelectorAll("[data-i18n-placeholder]").forEach((el) => {
    const key = el.getAttribute("data-i18n-placeholder");
    if (key) el.setAttribute("placeholder", t(key));
  });

  updateEditorBadge();
  refreshActiveViews(false);
}

function renderBranchPills() {
  const container = document.getElementById("branch-pills-container");
  container.innerHTML = FEATURED_BRANCHES.map((b) => {
    const label = state.lang === "tr" ? b.labelTr : b.labelEn;
    const isAct = state.focusedId === b.id ? " active" : "";
    return `<button type="button" class="branch-pill${isAct}" data-person-id="${b.id}">${escapeHtml(label)}</button>`;
  }).join("");

  container.querySelectorAll(".branch-pill").forEach((btn) => {
    btn.addEventListener("click", () => focusPersonInTree(btn.dataset.personId));
  });
}

/* ================= SEARCH & AUTOCOMPLETE ================= */
function initSearchAndAutocomplete() {
  const searchInput = document.getElementById("global-search-input");
  const resultsBox = document.getElementById("global-search-results");

  const renderGlobalSearchResults = () => {
    const qRaw = searchInput.value.trim();
    const q = normalizeText(qRaw);
    const qPhonetic = normalizePhonetic(qRaw);
    if (!qRaw || !state.data) {
      resultsBox.classList.add("hidden");
      return;
    }

    const matches = searchPersons(state.personList, qRaw, { limit: 14 });

    if (matches.length === 0) {
      resultsBox.innerHTML = `
        <div style="padding: 1.1rem; text-align: center; color: var(--text-muted); font-size: 0.85rem;">
          "${escapeHtml(qRaw)}" ${state.lang === "tr" ? "ile eşleşen kişi bulunamadı." : "no matching people found."}
        </div>
      `;
      resultsBox.classList.remove("hidden");
      return;
    }

    resultsBox.innerHTML = matches
      .map((p) => {
        const { father, mother } = getParents(p.id);
        const parentHint =
          father || mother
            ? ` • ${[father ? father.given || father.name : "", mother ? mother.given || mother.name : ""]
                .filter(Boolean)
                .join(" & ")}`
            : "";
        const isDeceased = Boolean(p.deceased);
        const branch = getMaternalBranchInfo(p.id);
        const branchBadge = branch
          ? `<span class="drawer-rel-branch-pill branch-style-${branch.colorIndex}" style="font-size:0.64rem;">${branch.icon} ${escapeHtml(branch.label)}</span>`
          : "";
        const genPill = p.generation !== undefined
          ? `<span class="sr-gen-pill">${p.generation + 1}. ${state.lang === "tr" ? "Kuşak" : "Gen"}</span>`
          : "";

        return `
        <div class="search-result-item sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" data-person-id="${p.id}">
          <span class="sr-avatar sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" aria-hidden="true">${renderAvatarSilhouette(p.sex, isDeceased)}</span>
          <div class="sr-main">
            <span class="sr-name">${highlightMatch(p.name, q)}</span>
            <span class="sr-sub">${escapeHtml(formatLifeYears(p))}${escapeHtml(parentHint)}</span>
          </div>
          <div class="sr-badges">
            ${branchBadge}
            ${genPill}
          </div>
        </div>
      `;
      })
      .join("");
    resultsBox.classList.remove("hidden");

    resultsBox.querySelectorAll(".search-result-item").forEach((item) => {
      item.addEventListener("click", () => {
        const pid = item.dataset.personId;
        resultsBox.classList.add("hidden");
        searchInput.value = "";
        focusPersonInTree(pid);
        openPersonDrawer(pid);
      });
    });
  };

  searchInput.addEventListener("input", renderGlobalSearchResults);
  searchInput.addEventListener("focus", renderGlobalSearchResults);

  document.addEventListener("click", (e) => {
    if (!e.target.closest("#global-search-box")) resultsBox.classList.add("hidden");
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement.tagName)) {
      e.preventDefault();
      searchInput.focus();
    } else if (e.key === "Escape") {
      resultsBox.classList.add("hidden");
      closeModal();
      closePersonDrawer();
    }
  });

  function bindKinshipAutocomplete(inputId, dropdownId, slotKey) {
    const inp = document.getElementById(inputId);
    const drop = document.getElementById(dropdownId);
    inp.addEventListener("input", () => {
      const qRaw = inp.value.trim();
      const q = normalizeText(qRaw);
      if (!qRaw || !state.data) {
        drop.classList.add("hidden");
        return;
      }

      const matches = searchPersons(state.personList, qRaw, { limit: 8 });

      if (matches.length === 0) {
        drop.innerHTML = `
          <div style="padding: 0.8rem; text-align: center; color: var(--text-muted); font-size: 0.8rem;">
            "${escapeHtml(qRaw)}" ${state.lang === "tr" ? "bulunamadı" : "not found"}
          </div>
        `;
        drop.classList.remove("hidden");
        return;
      }
      drop.innerHTML = matches
        .map((p) => {
          const isDeceased = Boolean(p.deceased);
          return `
          <div class="search-result-item sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" data-person-id="${p.id}">
            <span class="sr-avatar sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" aria-hidden="true">${renderAvatarSilhouette(p.sex, isDeceased)}</span>
            <div class="sr-main">
              <span class="sr-name">${highlightMatch(p.name, q)}</span>
              <span class="sr-sub">${escapeHtml(formatLifeYears(p))}</span>
            </div>
            <div class="sr-badges">
              <span class="sr-gen-pill">${p.generation + 1}. Kuşak</span>
            </div>
          </div>
        `;
        })
        .join("");
      drop.classList.remove("hidden");

      drop.querySelectorAll(".search-result-item").forEach((item) => {
        item.addEventListener("click", () => {
          state[slotKey] = item.dataset.personId;
          inp.value = "";
          drop.classList.add("hidden");
          renderKinshipView();
        });
      });
    });
  }

  bindKinshipAutocomplete("kinship-input-a", "kinship-dropdown-a", "kinshipA");
  bindKinshipAutocomplete("kinship-input-b", "kinship-dropdown-b", "kinshipB");
}

/* ================= APP BOOTSTRAP ================= */
function handleUrlHashRouting() {
  const hash = window.location.hash || "";
  const personMatch = hash.match(/person=(I\d+)/i);
  if (personMatch && state.data && state.data.individuals[personMatch[1].toUpperCase()]) {
    state.focusedId = personMatch[1].toUpperCase();
    switchView("tree");
    return;
  }

  const viewMatch = hash.match(/view=([a-z0-9_-]+)/i);
  if (viewMatch) {
    const targetView = viewMatch[1].toLowerCase();
    const validViews = ["tree", "fan", "map", "directory", "timeline", "kinship", "stats", "manage", "archive"];
    if (validViews.includes(targetView)) {
      switchView(targetView);
      return;
    }
  }

  // Fallback direct hash like #archive, #map, #stats
  const directView = hash.replace(/^#/, "").toLowerCase();
  const validViews = ["tree", "fan", "map", "directory", "timeline", "kinship", "stats", "manage", "archive"];
  if (validViews.includes(directView)) {
    switchView(directView);
  }
}

async function enterUnlockedApp(initialData, alreadySynced = false) {
  prepareDataset(initialData);

  const savedRole = localStorage.getItem(STORAGE_KEY_SESSION_ROLE);
  if (savedRole === "editor") {
    state.role = "editor";
  }

  document.getElementById("auth-gate").classList.add("hidden");
  document.getElementById("app-shell").classList.remove("hidden");

  updateEditorBadge();
  handleUrlHashRouting();
  refreshActiveViews(true);

  // Sync latest live records from Cloudflare D1 in background if not already freshly fetched
  if (!alreadySynced) {
    await syncFromCloud();
  }
}

async function initApp() {
  applyTheme(state.theme);
  applyLanguage(state.lang);
  initViewportInteractions();
  initSearchAndAutocomplete();
  setupMigrationMap({ switchView, focusPersonInTree, openPersonDrawer, closePersonDrawer });
  setupFanChart({ focusPersonInTree, openPersonDrawer });
  setupArchiveView({
    apiRequest,
    showSyncToast,
    focusPersonInTree,
    openModal,
    closeModal,
    requireAdminThen,
  });
  initArchiveView();

  const toggleTheme = () => applyTheme(state.theme === "dark" ? "light" : "dark");
  const toggleLang = () => applyLanguage(state.lang === "tr" ? "en" : "tr");

  document.getElementById("theme-toggle-btn").addEventListener("click", toggleTheme);
  document.getElementById("auth-theme-toggle").addEventListener("click", toggleTheme);
  document.getElementById("lang-toggle-btn").addEventListener("click", toggleLang);
  document.getElementById("auth-lang-toggle").addEventListener("click", toggleLang);

  document.getElementById("toggle-pass-vis").addEventListener("click", () => {
    const inp = document.getElementById("passcode-input");
    inp.type = inp.type === "password" ? "text" : "password";
  });

  document.getElementById("editor-mode-btn").addEventListener("click", () => {
    if (state.role === "editor" || state.role === "admin") {
      const roleName = state.role === "admin" ? "Yönetici (Admin)" : "Editör";
      if (confirm(`${roleName} modundan çıkıp salt-okunur İzleyici moduna geçmek ister misiniz?`)) {
        state.role = "viewer";
        localStorage.setItem(STORAGE_KEY_SESSION_ROLE, "viewer");
        sessionStorage.setItem(STORAGE_KEY_SESSION_ROLE, "viewer");
        updateEditorBadge();
        if (state.activeView === "archive") renderArchiveView();
      }
    } else {
      requireEditorThen(() => {
        if (state.activeView === "archive") renderArchiveView();
      });
    }
  });

  document.getElementById("top-add-person-btn").addEventListener("click", () => {
    requireEditorThen(() => openEditPersonModal(null));
  });

  document.getElementById("dir-add-person-btn").addEventListener("click", () => {
    requireEditorThen(() => openEditPersonModal(null));
  });

  document.getElementById("open-merge-modal-btn").addEventListener("click", () => {
    requireEditorThen(() => openMergeModal());
  });

  document.getElementById("open-find-replace-btn").addEventListener("click", () => {
    requireEditorThen(() => openFindReplaceModal());
  });

  const healthBtn = document.getElementById("open-health-modal-btn");
  if (healthBtn) {
    healthBtn.addEventListener("click", openHealthReportModal);
  }

  document.getElementById("focused-add-rel-btn").addEventListener("click", () => {
    requireEditorThen(() => openAddRelativeModal(state.focusedId));
  });

  document.getElementById("close-modal-btn").addEventListener("click", closeModal);

  // Unified modal backdrop click & Escape key dismissal
  const modalBackdrop = document.getElementById("modal-backdrop");
  if (modalBackdrop) {
    modalBackdrop.addEventListener("click", (e) => {
      if (e.target === modalBackdrop) closeModal();
    });
  }
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modalBackdrop && !modalBackdrop.classList.contains("hidden")) {
      closeModal();
    }
  });

  document.getElementById("app-about-btn")?.addEventListener("click", () => {
    openAboutModal();
  });

  document.getElementById("lock-app-btn").addEventListener("click", () => {
    if (state.sessionToken) {
      fetch("/api/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionToken: state.sessionToken }),
      }).catch(() => {});
    }
    localStorage.removeItem(STORAGE_KEY_MASTER);
    sessionStorage.removeItem(STORAGE_KEY_MASTER);
    localStorage.removeItem(STORAGE_KEY_SESSION_TOKEN);
    localStorage.removeItem(STORAGE_KEY_SESSION_ROLE);
    localStorage.removeItem("nh_family_viewer_pass_v1");
    localStorage.removeItem("nh_family_editor_pass_v1");
    state.sessionToken = "";
    state.role = "viewer";
    document.getElementById("app-shell").classList.add("hidden");
    document.getElementById("auth-gate").classList.remove("hidden");
    document.getElementById("passcode-input").value = "";
  });

  window.addEventListener("hashchange", () => {
    handleUrlHashRouting();
  });

  document.querySelectorAll(".nav-tab").forEach((btn) => {
    btn.addEventListener("click", () => switchView(btn.dataset.view));
  });

  document.getElementById("brand-home-btn").addEventListener("click", () => {
    switchView("tree");
    focusPersonInTree("I227");
  });

  // Tree toolbar controls
  document.getElementById("ancestor-depth-select").addEventListener("change", (e: any) => {
    state.ancestorDepth = parseInt(e.target.value, 10);
    localStorage.setItem(STORAGE_KEY_TREE_ANCESTOR_DEPTH, String(state.ancestorDepth));
    renderTree(true);
  });

  document.getElementById("descendant-depth-select").addEventListener("change", (e: any) => {
    state.descendantDepth = parseInt(e.target.value, 10);
    localStorage.setItem(STORAGE_KEY_TREE_DESCENDANT_DEPTH, String(state.descendantDepth));
    renderTree(true);
  });

  const spToggleBtn = document.getElementById("toggle-spouses-btn");
  if (spToggleBtn) {
    spToggleBtn.addEventListener("click", () => {
      state.showSpouses = !state.showSpouses;
      localStorage.setItem(STORAGE_KEY_TREE_SHOW_SPOUSES, String(state.showSpouses));
      renderTree(true);
    });
  }

  const sibToggleBtn = document.getElementById("toggle-siblings-btn");
  if (sibToggleBtn) {
    sibToggleBtn.addEventListener("click", () => {
      state.showSiblings = !state.showSiblings;
      localStorage.setItem(STORAGE_KEY_TREE_SHOW_SIBLINGS, String(state.showSiblings));
      renderTree(true);
    });
  }

  document.getElementById("expand-all-gen-btn").addEventListener("click", () => {
    state.ancestorDepth = 12;
    state.descendantDepth = 15;
    state.showSiblings = true;
    state.showSpouses = true;
    localStorage.setItem(STORAGE_KEY_TREE_ANCESTOR_DEPTH, "12");
    localStorage.setItem(STORAGE_KEY_TREE_DESCENDANT_DEPTH, "15");
    localStorage.setItem(STORAGE_KEY_TREE_SHOW_SIBLINGS, "true");
    localStorage.setItem(STORAGE_KEY_TREE_SHOW_SPOUSES, "true");
    document.getElementById("ancestor-depth-select").value = "12";
    document.getElementById("descendant-depth-select").value = "15";
    renderTree(false);
    zoomFitTree();
  });

  document.getElementById("export-svg-btn").addEventListener("click", () => {
    openPosterExportModal();
  });

  document.getElementById("fan-direction-select").addEventListener("change", renderFanChart);
  document.getElementById("fan-rings-select").addEventListener("change", renderFanChart);
  ["fan-color-select", "fan-layout-select"].forEach((id) => {
    const sel = document.getElementById(id);
    if (sel) sel.addEventListener("change", renderFanChart);
  });

  const fanResetRootBtn = document.getElementById("fan-reset-root-btn");
  if (fanResetRootBtn) {
    fanResetRootBtn.addEventListener("click", () => {
      focusPersonInTree("I227");
    });
  }

  const fanBackBtn = document.getElementById("fan-back-btn");
  if (fanBackBtn) {
    fanBackBtn.addEventListener("click", () => {
      if (state.focusHistory && state.focusHistory.length > 0) {
        const prevId = state.focusHistory.pop();
        focusPersonInTree(prevId, false);
      }
    });
  }

  const fanProfileBtn = document.getElementById("fan-profile-btn");
  if (fanProfileBtn) {
    fanProfileBtn.addEventListener("click", () => {
      openPersonDrawer(state.focusedId);
    });
  }

  document.getElementById("open-focused-profile-btn").addEventListener("click", () => {
    openPersonDrawer(state.focusedId);
  });

  document.getElementById("history-back-btn").addEventListener("click", () => {
    if (state.focusHistory.length > 0) {
      const prevId = state.focusHistory.pop();
      focusPersonInTree(prevId, false);
    }
  });

  document.getElementById("zoom-in-btn").addEventListener("click", () => zoomByFactor(1.2));
  document.getElementById("zoom-out-btn").addEventListener("click", () => zoomByFactor(0.84));
  document.getElementById("zoom-fit-btn").addEventListener("click", zoomFitTree);
  document.getElementById("zoom-center-btn").addEventListener("click", centerOnFocusedNode);

  const toggleMmBtn = document.getElementById("toggle-minimap-btn");
  const mmEl = document.getElementById("tree-minimap");
  if (toggleMmBtn && mmEl) {
    toggleMmBtn.addEventListener("click", () => {
      if (window.innerWidth <= 768) {
        const isShown = mmEl.classList.toggle("mobile-visible");
        toggleMmBtn.classList.toggle("active", isShown);
      } else {
        const isHidden = mmEl.classList.toggle("desktop-hidden");
        toggleMmBtn.classList.toggle("active", !isHidden);
      }
      updateMinimapViewport();
    });
  }

  let resizeTimer = null;
  let lastViewportWidth = window.innerWidth;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (!state.data) return;
      if (state.activeView === "tree") {
        const widthChanged = Math.abs(window.innerWidth - lastViewportWidth) > 40;
        lastViewportWidth = window.innerWidth;
        if (widthChanged) {
          smartFocusCamera();
        } else {
          updateMinimapViewport();
        }
      } else if (state.activeView === "map") {
        fitMap();
      }
    }, 150);
  });

  const zoomSlider = document.getElementById("zoom-slider");
  if (zoomSlider) {
    zoomSlider.addEventListener("input", (e) => {
      const pct = parseFloat(e.target.value) || 75;
      setZoomScale(pct / 100);
    });
  }

  const fanZoomIn = document.getElementById("fan-zoom-in-btn");
  const fanZoomOut = document.getElementById("fan-zoom-out-btn");
  const fanZoomFit = document.getElementById("fan-zoom-fit-btn");
  if (fanZoomIn) fanZoomIn.addEventListener("click", () => zoomFanByFactor(1.22));
  if (fanZoomOut) fanZoomOut.addEventListener("click", () => zoomFanByFactor(0.82));
  if (fanZoomFit) fanZoomFit.addEventListener("click", resetFanZoom);

  // Map HUD & Toolbar Controls
  const mapZoomIn = document.getElementById("map-zoom-in-btn");
  const mapZoomOut = document.getElementById("map-zoom-out-btn");
  const mapZoomFit = document.getElementById("map-zoom-fit-btn");
  if (mapZoomIn) mapZoomIn.addEventListener("click", () => zoomMap(0.25));
  if (mapZoomOut) mapZoomOut.addEventListener("click", () => zoomMap(-0.25));
  if (mapZoomFit) mapZoomFit.addEventListener("click", fitMap);

  const mapYearSlider = document.getElementById("map-year-slider");
  if (mapYearSlider) {
    mapYearSlider.addEventListener("input", (e) => {
      state.mapYear = parseInt(e.target.value, 10);
      updateMapTimelineUI();
      renderMigrationMap(false);
    });
  }

  const mapPlayBtn = document.getElementById("map-play-btn");
  if (mapPlayBtn) {
    mapPlayBtn.addEventListener("click", toggleMapTimelinePlay);
  }

  const mapBranchFilter = document.getElementById("map-branch-filter");
  if (mapBranchFilter) {
    mapBranchFilter.addEventListener("change", (e) => {
      state.mapBranch = e.target.value;
      renderMigrationMap(false);
    });
  }

  const mapEventFilter = document.getElementById("map-event-filter");
  if (mapEventFilter) {
    mapEventFilter.addEventListener("change", (e) => {
      state.mapEvent = e.target.value;
      renderMigrationMap(false);
    });
  }

  const startTourBtn = document.getElementById("start-story-tour-btn");
  if (startTourBtn) {
    startTourBtn.addEventListener("click", startStoryTour);
  }

  const storyPrevBtn = document.getElementById("story-prev-btn");
  const storyNextBtn = document.getElementById("story-next-btn");
  const closeStoryBtn = document.getElementById("close-story-card-btn");
  if (storyPrevBtn) storyPrevBtn.addEventListener("click", prevStoryStep);
  if (storyNextBtn) storyNextBtn.addEventListener("click", nextStoryStep);
  if (closeStoryBtn) closeStoryBtn.addEventListener("click", closeStoryTour);

  const closeInspectorBtn = document.getElementById("close-city-inspector-btn");
  if (closeInspectorBtn) closeInspectorBtn.addEventListener("click", closeCityInspector);

  initMapPanZoomGestures();

  // Register PWA Service Worker & force update check so mobile clients never stay on stale cache
  if ("serviceWorker" in navigator) {
    let refreshingSw = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!refreshingSw) {
        refreshingSw = true;
        window.location.reload();
      }
    });
    navigator.serviceWorker
      .register("./sw.js?v=27")
      .then((reg) => {
        reg.update().catch(() => {});
      })
      .catch(() => {});
  }

  const pwaInstallBtn = document.getElementById("pwa-install-btn");
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

  if (isIos && !isStandalone && pwaInstallBtn) {
    pwaInstallBtn.classList.remove("hidden");
  }

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    state.deferredInstallPrompt = e;
    if (pwaInstallBtn && !isStandalone) {
      pwaInstallBtn.classList.remove("hidden");
    }
  });

  if (pwaInstallBtn) {
    pwaInstallBtn.addEventListener("click", async () => {
      if (state.deferredInstallPrompt) {
        state.deferredInstallPrompt.prompt();
        await state.deferredInstallPrompt.userChoice.catch(() => {});
        state.deferredInstallPrompt = null;
        pwaInstallBtn.classList.add("hidden");
      } else {
        openModal(
          state.lang === "tr" ? "📲 Telefon Ana Ekranına Ekle" : "📲 Add to Home Screen",
          state.lang === "tr"
            ? `<p class="section-subtitle">Bu soy ağacını telefonunuzda tam ekran bir uygulama gibi kullanmak için:</p>
               <ol style="padding-left:1.25rem;line-height:1.7;color:var(--text-secondary);font-size:0.9rem;">
                 <li>Tarayıcınızın alt veya üst menüsündeki <strong>Paylaş (Share ⬆️)</strong> veya <strong>Menü (⋮)</strong> simgesine dokunun.</li>
                 <li>Açılan listeden <strong>"Ana Ekrana Ekle" (Add to Home Screen)</strong> seçeneğine dokunun.</li>
               </ol>`
            : `<p class="section-subtitle">To install the family tree as a full-screen app on your phone:</p>
               <ol style="padding-left:1.25rem;line-height:1.7;color:var(--text-secondary);font-size:0.9rem;">
                 <li>Tap the <strong>Share (⬆️)</strong> or <strong>Menu (⋮)</strong> button in your browser.</li>
                 <li>Select <strong>"Add to Home Screen"</strong>.</li>
               </ol>`
        );
      }
    });
  }

  document.getElementById("close-drawer-btn").addEventListener("click", closePersonDrawer);
  document.getElementById("drawer-backdrop").addEventListener("click", closePersonDrawer);

  ["dir-search-input", "dir-surname-filter", "dir-gender-filter", "dir-status-filter", "dir-sort-select"].forEach(
    (id) => {
      document.getElementById(id).addEventListener("input", renderDirectory);
      document.getElementById(id).addEventListener("change", renderDirectory);
    }
  );

  const tlSearchInput = document.getElementById("timeline-search-input");
  if (tlSearchInput) {
    tlSearchInput.addEventListener("input", renderTimeline);
  }

  const tlModeLifespansBtn = document.getElementById("timeline-mode-lifespans");
  const tlModeEventsBtn = document.getElementById("timeline-mode-events");
  if (tlModeLifespansBtn && tlModeEventsBtn) {
    tlModeLifespansBtn.addEventListener("click", () => {
      state.timelineMode = "lifespans";
      tlModeLifespansBtn.classList.add("active");
      tlModeEventsBtn.classList.remove("active");
      renderTimeline();
    });
    tlModeEventsBtn.addEventListener("click", () => {
      state.timelineMode = "events";
      tlModeEventsBtn.classList.add("active");
      tlModeLifespansBtn.classList.remove("active");
      renderTimeline();
    });
  }

  const tlBranchFilter = document.getElementById("timeline-branch-filter");
  if (tlBranchFilter) {
    tlBranchFilter.addEventListener("change", (e) => {
      state.timelineBranch = e.target.value;
      renderTimeline();
    });
  }

  const tlGenFilter = document.getElementById("timeline-gen-filter");
  if (tlGenFilter) {
    tlGenFilter.addEventListener("change", (e) => {
      state.timelineGen = e.target.value;
      renderTimeline();
    });
  }

  const tlTypeFilter = document.getElementById("timeline-type-filter");
  if (tlTypeFilter) {
    tlTypeFilter.addEventListener("change", (e) => {
      state.timelineType = e.target.value;
      renderTimeline();
    });
  }

  document.getElementById("kinship-swap-btn").addEventListener("click", () => {
    const tmp = state.kinshipA;
    state.kinshipA = state.kinshipB;
    state.kinshipB = tmp;
    renderKinshipView();
  });

  document.getElementById("export-gedcom-btn").addEventListener("click", () => {
    const ged = generateGedcomFile();
    downloadBlob(ged, "Niksic-Hocic.ged", "text/plain;charset=utf-8");
  });

  document.getElementById("export-json-btn").addEventListener("click", () => {
    const j = JSON.stringify(state.data, null, 2);
    downloadBlob(j, "Niksic-Hocic-Backup.json", "application/json;charset=utf-8");
  });

  const openGedcomImportBtn = document.getElementById("open-gedcom-import-btn");
  if (openGedcomImportBtn) {
    openGedcomImportBtn.addEventListener("click", () => {
      openGedcomImportModal();
    });
  }

  document.getElementById("refresh-cloud-btn").addEventListener("click", async () => {
    await syncFromCloud();
  });

  const refreshSessionsBtn = document.getElementById("refresh-sessions-btn");
  if (refreshSessionsBtn) {
    refreshSessionsBtn.addEventListener("click", renderDeviceSessions);
  }

  const syncLiveBadge = document.getElementById("sync-live-badge");
  if (syncLiveBadge) {
    syncLiveBadge.addEventListener("click", () => syncDelta(true));
  }

  const refreshSnapshotsBtn = document.getElementById("refresh-snapshots-btn");
  if (refreshSnapshotsBtn) {
    refreshSnapshotsBtn.addEventListener("click", renderCloudSnapshots);
  }

  const takeSnapshotBtn = document.getElementById("take-cloud-snapshot-btn");
  if (takeSnapshotBtn) {
    takeSnapshotBtn.addEventListener("click", () => {
      requireEditorThen(async () => {
        const note = prompt("Yedekleme açıklaması girin (isteğe bağlı):", "Manuel Bulut Yedeği") || "Manuel Bulut Yedeği";
        const origText = takeSnapshotBtn.textContent;
        takeSnapshotBtn.textContent = "Kaydediliyor...";
        const res = await apiRequest("snapshots", "POST", { notes: note });
        takeSnapshotBtn.textContent = origText;
        if (res.ok) {
          showSyncToast("✓ Yeni bulut yedeği başarıyla oluşturuldu");
          renderCloudSnapshots();
        } else {
          alert("Yedekleme başarısız: " + (res.error || "Hata"));
        }
      });
    });
  }

  // 1. One-time legacy session migration & cleanup (elderly-friendly upgrade)
  const legacyViewer = localStorage.getItem("nh_family_viewer_pass_v1");
  const legacyEditor = localStorage.getItem("nh_family_editor_pass_v1");
  if (!localStorage.getItem(STORAGE_KEY_SESSION_TOKEN) && (legacyViewer || legacyEditor)) {
    try {
      const pass = legacyEditor || legacyViewer;
      const res = await fetch("/api/auth-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode: pass, deviceName: getDeviceSummary() }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d && d.sessionToken) {
          localStorage.setItem(STORAGE_KEY_SESSION_TOKEN, d.sessionToken);
          localStorage.setItem(STORAGE_KEY_SESSION_ROLE, d.role);
          state.sessionToken = d.sessionToken;
          state.role = d.role;
        }
      }
    } catch (_) {}
  }
  // Ensure plaintext passcodes are completely purged
  localStorage.removeItem("nh_family_viewer_pass_v1");
  localStorage.removeItem("nh_family_editor_pass_v1");

  // 2. Try auto-unlocking from saved master encryption key
  try {
    const savedMasterB64 =
      localStorage.getItem(STORAGE_KEY_MASTER) || sessionStorage.getItem(STORAGE_KEY_MASTER);
    if (savedMasterB64) {
      state.sessionToken =
        localStorage.getItem(STORAGE_KEY_SESSION_TOKEN) ||
        sessionStorage.getItem(STORAGE_KEY_SESSION_TOKEN) ||
        state.sessionToken ||
        "";
      state.role =
        (localStorage.getItem(STORAGE_KEY_SESSION_ROLE) as any) ||
        (sessionStorage.getItem(STORAGE_KEY_SESSION_ROLE) as any) ||
        state.role ||
        "viewer";

      // Fast path: if device has a valid session token, load live tree directly from D1 (saves 416KB encrypted JSON download)
      if (state.sessionToken) {
        try {
          const liveRes = await fetch("/api/tree", { headers: getApiHeaders() });
          if (liveRes.ok) {
            const liveData = await liveRes.json();
            if (liveData && liveData.individuals) {
              await enterUnlockedApp(liveData, true);
              return;
            }
          }
        } catch (_) {}
      }

      // Offline / fallback path: fetch and decrypt static local bundle
      const bundle = await loadEncryptedBundle();
      const data = await decryptWithMasterKeyBytes(base64ToBytes(savedMasterB64), bundle);
      await enterUnlockedApp(data);
      return;
    }
  } catch (_) {
    localStorage.removeItem(STORAGE_KEY_MASTER);
    sessionStorage.removeItem(STORAGE_KEY_MASTER);
  }

  // 3. Auth form submit
  document.getElementById("auth-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const errBox = document.getElementById("auth-error");
    const btnText = document.getElementById("auth-btn-text");
    const submitBtn = document.getElementById("auth-submit-btn");

    errBox.classList.add("hidden");
    submitBtn.disabled = true;
    btnText.textContent = t("authDecrypting");

    try {
      const bundle = await loadEncryptedBundle();
      const rawPass = document.getElementById("passcode-input").value;
      const { data, masterKeyB64, normPass } = await unlockWithPasscode(rawPass, bundle);

      // Exchange passcode for an opaque 1-year sliding device session token
      let serverRole = ADMIN_CODES.has(normPass) ? "admin" : EDITOR_CODES.has(normPass) ? "editor" : "viewer";
      try {
        const authRes = await fetch("/api/auth-check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            passcode: normPass,
            deviceName: getDeviceSummary(),
          }),
        });
        if (authRes.ok) {
          const authData = await authRes.json();
          if (authData && authData.sessionToken) {
            state.sessionToken = authData.sessionToken;
            serverRole = authData.role || serverRole;
          }
        }
      } catch (_) {
        // Local preview fallback
      }

      state.role = serverRole;

      // Ensure plaintext passwords are never stored in browser
      localStorage.removeItem("nh_family_viewer_pass_v1");
      localStorage.removeItem("nh_family_editor_pass_v1");

      const remember = document.getElementById("remember-pass").checked;
      if (remember) {
        localStorage.setItem(STORAGE_KEY_MASTER, masterKeyB64);
        if (state.sessionToken) {
          localStorage.setItem(STORAGE_KEY_SESSION_TOKEN, state.sessionToken);
        }
        localStorage.setItem(STORAGE_KEY_SESSION_ROLE, state.role);
      } else {
        sessionStorage.setItem(STORAGE_KEY_MASTER, masterKeyB64);
        if (state.sessionToken) {
          sessionStorage.setItem(STORAGE_KEY_SESSION_TOKEN, state.sessionToken);
        }
        sessionStorage.setItem(STORAGE_KEY_SESSION_ROLE, state.role);
      }

      await enterUnlockedApp(data);
    } catch (_) {
      errBox.textContent = t("authErrorInvalid");
      errBox.classList.remove("hidden");
    } finally {
      submitBtn.disabled = false;
      btnText.textContent = t("authButton");
    }
  });
}

window.addEventListener("DOMContentLoaded", initApp);
